/* ─────────────────────────────────────────────────────────────
   Subscription Portal v2 — logic for sections/manage-subscription-v2.liquid
   Configuration (texts, collection handles, shop categories) is read from
   the <script type="application/json" data-msv2-config> element rendered
   by snippets/msv2-config.liquid.
   Talks to the /apps/customer/* app-proxy endpoints.
   ───────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  var root = document.querySelector('[data-msv2-portal]');
  if (!root) return;

  var CONFIG = {};
  try {
    CONFIG = JSON.parse(document.querySelector('[data-msv2-config]').textContent);
  } catch (e) {
    console.error('Subscription portal: invalid config', e);
    return;
  }
  var SETTINGS = CONFIG.settings || {};
  var SHOP_CATEGORIES = Array.isArray(CONFIG.categories) ? CONFIG.categories : [];

  // Shopify's `t` filter HTML-escapes translations (e.g. ' -> &#39;), and the
  // JS escapes again when rendering, showing literal entities. Decode once so
  // all settings hold plain text.
  (function decodeSettingsEntities() {
    var ta = document.createElement('textarea');
    (function decode(obj) {
      Object.keys(obj).forEach(function (k) {
        var v = obj[k];
        if (typeof v === 'string' && v.indexOf('&') !== -1) {
          ta.innerHTML = v;
          obj[k] = ta.value;
        } else if (v && typeof v === 'object') {
          decode(v);
        }
      });
    })(SETTINGS);
  })();

  var contract = null;

  /* ── helpers ── */
  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  }
  function escapeAttr(str) { return escapeHtml(str).replace(/`/g, '&#096;'); }
  /* Placeholder shown when a product has no photo, so image boxes keep their size */
  var NO_IMAGE_SVG =
    '<svg class="msv2-noimg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<rect x="3" y="3" width="18" height="18" rx="2"/>' +
      '<circle cx="8.5" cy="8.5" r="1.5"/>' +
      '<path d="M21 15l-5-5L5 21"/>' +
    '</svg>';
  function formatMoney(amount, currency) {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'AUD' }).format(Number(amount) || 0);
  }
  function formatDate(iso) {
    var d = new Date(iso);
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }
  /**
   * Format a shop-local calendar day (YYYY-MM-DD). Parsing it with Date()
   * would read it as UTC midnight and shift the day for anyone west of UTC.
   */
  function formatYmd(ymd) {
    var m = String(ymd || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return formatDate(ymd);
    return formatDate(new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  }
  function formatFrequency(p) {
    if (!p || !p.interval || !p.intervalCount) return '';
    var n = Number(p.intervalCount);
    var m = { DAY: ['day', 'days'], WEEK: ['week', 'weeks'], MONTH: ['month', 'months'], YEAR: ['year', 'years'] };
    var pair = m[p.interval] || ['unit', 'units'];
    if (p.interval === 'WEEK' && n === 1) return 'Weekly';
    if (p.interval === 'WEEK' && n === 2) return 'Fortnightly';
    if (p.interval === 'MONTH' && n === 1) return 'Monthly';
    return 'Every ' + n + ' ' + (n === 1 ? pair[0] : pair[1]);
  }
  function pickPrimaryContract(list) {
    if (!Array.isArray(list) || !list.length) return null;
    return list.find(function (c) { return c && (c.status === 'PAUSED' || c.status === 'ACTIVE'); }) || list[0];
  }
  function isSelectableContract(c) { return !!c && (c.status === 'ACTIVE' || c.status === 'PAUSED'); }
  function formatContractLabel(c, idx) {
    var name = 'Subscription ' + (idx + 1);
    var status = c && c.status ? '(' + c.status + ')' : '';
    var nb = c && c.next_billing && c.next_billing.date ? '• ' + formatDate(c.next_billing.date) : '';
    return (name + ' ' + status + ' ' + nb).replace(/\s+/g, ' ').trim();
  }

  /* Success / error banners auto-dismiss after 5s; re-showing a banner
     restarts the timer. */
  var BANNER_HIDE_MS = 5000;
  var errorHideTimer = null;
  var successHideTimer = null;
  function setError(msg) {
    var el = root.querySelector('[data-error-text]');
    if (!el) return;
    el.textContent = msg || SETTINGS.genericErrorText;
    el.classList.add('is-visible');
    if (errorHideTimer) clearTimeout(errorHideTimer);
    errorHideTimer = setTimeout(function () {
      errorHideTimer = null;
      el.textContent = '';
      el.classList.remove('is-visible');
    }, BANNER_HIDE_MS);
  }
  function clearError() {
    var el = root.querySelector('[data-error-text]');
    if (!el) return;
    if (errorHideTimer) { clearTimeout(errorHideTimer); errorHideTimer = null; }
    el.textContent = '';
    el.classList.remove('is-visible');
  }
  function setSuccess(msg) {
    var el = root.querySelector('[data-success-text]');
    if (!el) return;
    el.textContent = msg || '';
    el.classList.toggle('is-visible', !!msg);
    if (successHideTimer) { clearTimeout(successHideTimer); successHideTimer = null; }
    if (msg) {
      successHideTimer = setTimeout(function () {
        successHideTimer = null;
        el.textContent = '';
        el.classList.remove('is-visible');
      }, BANNER_HIDE_MS);
    }
  }

  /* ── shipping preview (same rules as backend lib/subscription_shipping.php) ── */
  function computeShippingPreview(c) {
    var s = c && c.shipping;
    if (!s || !s.enabled) return null;
    if (s.method_type && s.method_type !== 'SubscriptionDeliveryMethodShipping') return null;

    var lines = Array.isArray(c.lines) ? c.lines : [];
    var subtotal = lines.reduce(function (acc, l) {
      return acc + (Number(l.price) || 0) * Math.max(0, Number(l.quantity) || 0);
    }, 0);
    var currency = (c.deliveryPrice && c.deliveryPrice.currencyCode) || (lines[0] && lines[0].currency) || 'AUD';
    var province = String(s.province_code || '').trim().toUpperCase();
    var country = String(s.address_country_code || '').trim().toUpperCase();
    var waCodes = Array.isArray(s.wa_province_codes) ? s.wa_province_codes.map(function (x) { return String(x).toUpperCase(); }) : [];
    var isWA = country === String(s.rule_country_code || '').toUpperCase() && waCodes.indexOf(province) !== -1;
    var threshold = Number(s.free_threshold || 0);

    var amount;
    if (threshold > 0 && subtotal >= threshold) amount = 0;
    else if (isWA) amount = Number(s.wa_standard_rate || 0);
    else if (s.tier === 'express') amount = Number(s.express_rate || 0);
    else amount = Number(s.standard_rate || 0);

    return { amount: amount, currency: currency };
  }

  /* ── one-time items (rewards added to the next billing cycle only) ── */
  function getOneTimeItems(c) {
    var items = c && c.next_billing && c.next_billing.free_items;
    return Array.isArray(items) ? items : [];
  }
  /* Recurring lines + one-time items, as shown in the next order card. */
  function getDisplayLines(c) {
    var lines = (c && Array.isArray(c.lines)) ? c.lines.slice() : [];
    getOneTimeItems(c).forEach(function (it) {
      var copy = {};
      for (var k in it) copy[k] = it[k];
      copy.is_one_time = true;
      lines.push(copy);
    });
    return lines;
  }
  function recurringLineCount(c) {
    var source = c || contract;
    return (source && Array.isArray(source.lines)) ? source.lines.length : 0;
  }
  /* Totals of the one-time items: full price and price after reward discounts.
     They are not part of pricing_preview (that only covers recurring lines),
     so the order summary adds them on top. */
  function oneTimeTotals(c) {
    var subtotal = 0, after = 0;
    getOneTimeItems(c).forEach(function (it) {
      var qty = Math.max(1, Number(it.quantity) || 0);
      var full = (Number(it.price) || 0) * qty;
      var hasDiscounted = it.discounted_price !== null && it.discounted_price !== undefined;
      var discounted = hasDiscounted
        ? Math.round((Number(it.discounted_price) || 0) * qty * 100) / 100
        : full;
      subtotal += full;
      after += Math.max(0, Math.min(full, discounted));
    });
    return { subtotal: subtotal, after: after };
  }

  /* ── render: next order card ── */
  var linesPage = 1;
  /* Tallest rendered page for the current set of lines, applied as a
     min-height so the card keeps the same height on the shorter last page. */
  var linesMinHeight = 0;
  var linesHeightKey = '';

  function linesPerPage() {
    var n = parseInt(SETTINGS.linesPerPage, 10);
    return n > 0 ? n : 4;
  }
  function lockLinesHeight(host, lines, perPage, totalPages) {
    var key = String(contract && contract.id) + ':' + lines.length + ':' + perPage;
    if (key !== linesHeightKey) {
      linesHeightKey = key;
      linesMinHeight = 0;
    }
    if (totalPages <= 1) {
      host.style.minHeight = '';
      return;
    }
    host.style.minHeight = '';
    var h = host.offsetHeight;
    if (h > linesMinHeight) linesMinHeight = h;
    host.style.minHeight = linesMinHeight + 'px';
  }
  /* Text wrapping changes with viewport width, so re-measure after resizes. */
  var linesResizeTimer = null;
  window.addEventListener('resize', function () {
    if (!linesMinHeight) return;
    clearTimeout(linesResizeTimer);
    linesResizeTimer = setTimeout(function () {
      linesHeightKey = '';
      renderNextOrder();
    }, 150);
  });
  function renderLinesPagination(totalPages) {
    var host = root.querySelector('[data-lines-pagination]');
    if (!host) return;
    if (totalPages <= 1) {
      host.innerHTML = '';
      host.style.display = 'none';
      return;
    }
    var html =
      '<button type="button" class="msv2-page-btn msv2-page-btn--nav" data-lines-page="prev"' +
        (linesPage <= 1 ? ' disabled' : '') + ' aria-label="Previous page">' +
        '<svg viewBox="0 0 15 15" fill="none" aria-hidden="true"><path d="M9.5 3.5 5.5 7.5l4 4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '</button>';
    for (var p = 1; p <= totalPages; p++) {
      html += '<button type="button" class="msv2-page-btn' + (p === linesPage ? ' is-active' : '') + '" ' +
        'data-lines-page="' + p + '"' + (p === linesPage ? ' aria-current="page"' : '') + '>' + p + '</button>';
    }
    html +=
      '<button type="button" class="msv2-page-btn msv2-page-btn--nav" data-lines-page="next"' +
        (linesPage >= totalPages ? ' disabled' : '') + ' aria-label="Next page">' +
        '<svg viewBox="0 0 15 15" fill="none" aria-hidden="true"><path d="M5.5 3.5l4 4-4 4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '</button>';
    host.innerHTML = html;
    host.style.display = '';
  }
  function goToLinesPage(target) {
    var lines = getDisplayLines(contract);
    var totalPages = Math.max(1, Math.ceil(lines.length / linesPerPage()));
    var next = linesPage;
    if (target === 'prev') next = linesPage - 1;
    else if (target === 'next') next = linesPage + 1;
    else next = parseInt(target, 10) || 1;
    next = Math.min(totalPages, Math.max(1, next));
    if (next === linesPage) return;
    linesPage = next;
    renderNextOrder();
  }

  /* Minimum order value (admin setting): the backend decides `below` on the same
     basis the renewal is blocked on, so this only formats its numbers. */
  /* Re-evaluate against a new lines subtotal from a mutation response, in
     cents so a box at exactly the minimum passes. */
  function refreshMinimumOrder(min, subtotal) {
    var shortCents = Math.round((Number(min.amount) || 0) * 100) - Math.round(subtotal * 100);
    min.subtotal = subtotal;
    min.below = !!min.enabled && shortCents > 0;
    min.shortfall = min.below ? shortCents / 100 : 0;
  }
  function renderMinimumWarning() {
    var box = root.querySelector('[data-min-warning]');
    if (!box) return;
    var min = contract && contract.minimum_order;
    var failure = contract && contract.renewal_failure;
    // A renewal already blocked for the minimum says the same thing in its own banner.
    var show = !!(min && min.enabled && min.below && SETTINGS.belowMinimumText) &&
      !(failure && failure.type === 'minimum');
    box.hidden = !show;
    if (!show) return;
    var textEl = box.querySelector('[data-min-warning-text]');
    if (textEl) {
      textEl.textContent = SETTINGS.belowMinimumText
        .replace('[minimum]', formatMoney(min.amount, min.currency))
        .replace('[shortfall]', formatMoney(min.shortfall, min.currency))
        .replace('[subtotal]', formatMoney(min.subtotal, min.currency));
    }
  }

  /* Open failed renewal (payment / out of stock / below minimum). The backend
     only sends the failure type and cycle date; the wording lives in the locale. */
  function renderRenewalFailure() {
    var box = root.querySelector('[data-renewal-failure]');
    if (!box) return;
    var failure = contract && contract.renewal_failure;
    var copy = failure && SETTINGS.renewalFailure && SETTINGS.renewalFailure[failure.type];
    box.hidden = !copy;
    if (!copy) return;

    var min = contract.minimum_order || {};
    var fixed = failure.type === 'minimum' && !min.below;
    var dateText = failure.cycle_date ? formatYmd(failure.cycle_date) : '';
    var text = String((fixed ? copy.fixedText : copy.text) || '')
      .replace('[date]', dateText)
      .replace('[minimum]', formatMoney(min.amount, min.currency))
      .replace('[shortfall]', formatMoney(min.shortfall, min.currency));

    var titleEl = box.querySelector('[data-renewal-failure-title]');
    if (titleEl) titleEl.textContent = copy.title || '';
    var textEl = box.querySelector('[data-renewal-failure-text]');
    if (textEl) textEl.textContent = text;
    var cta = box.querySelector('[data-renewal-failure-cta]');
    if (cta) {
      cta.textContent = copy.cta || '';
      cta.hidden = fixed || !copy.cta;
      cta.setAttribute('data-renewal-failure-cta', failure.type);
    }
  }
  function handleRenewalFailureCta(type) {
    if (type === 'payment') {
      var payBtn = root.querySelector('[data-action="edit-payment"]');
      if (!payBtn) return;
      payBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
      payBtn.click();
      return;
    }
    openShopOverlay();
  }

  function renderNextOrder() {
    var dateEl = root.querySelector('[data-processing-date]');
    if (dateEl) {
      dateEl.textContent = (contract && contract.next_billing && contract.next_billing.date)
        ? SETTINGS.processingPrefix + ' ' + formatDate(contract.next_billing.date)
        : '';
    }
    var deliveryEl = root.querySelector('[data-delivery-date]');
    if (deliveryEl) {
      var deliveryYmd = contract && contract.next_delivery && contract.next_delivery.date;
      deliveryEl.textContent = deliveryYmd
        ? SETTINGS.deliveringPrefix + ' ' + formatYmd(deliveryYmd)
        : '';
      deliveryEl.hidden = !deliveryYmd;
    }
    updateSendNowButton();
    updateFrequencyButton();
    renderRenewalFailure();
    renderMinimumWarning();

    var host = root.querySelector('[data-lines]');
    if (!host) return;
    var lines = getDisplayLines(contract);
    if (!lines.length) {
      host.innerHTML = '<p class="msv2-card-sub">' + escapeHtml(SETTINGS.noItemsText) + '</p>';
      host.style.minHeight = '';
      renderLinesPagination(1);
      return;
    }
    var perPage = linesPerPage();
    var totalPages = Math.max(1, Math.ceil(lines.length / perPage));
    if (linesPage > totalPages) linesPage = totalPages;
    if (linesPage < 1) linesPage = 1;
    var pageLines = lines.slice((linesPage - 1) * perPage, linesPage * perPage);
    var freq = formatFrequency(contract.billingPolicy);
    host.innerHTML = pageLines.map(function (line) {
      var img = (line.variant_image && line.variant_image.url) || '';
      var alt = (line.variant_image && line.variant_image.altText) || line.product_title || '';
      var qty = Number(line.quantity) || 0;
      var lineTotal = (Number(line.price) || 0) * Math.max(1, qty);

      // Lines are stored at full price; reward / cycle discounts apply at billing.
      // discounted_price covers one-time extras and recurring SKUs already in the
      // box that a free-product code entitles (no duplicate line).
      var priceHtml = '<p class="msv2-line-price">' + escapeHtml(formatMoney(lineTotal, line.currency)) + '</p>';
      if (line.discounted_price !== null && line.discounted_price !== undefined) {
        // Round money at line level so unit÷qty precision does not show $12.96 for $12.95.
        var discountedTotal = Math.round((Number(line.discounted_price) || 0) * Math.max(1, qty) * 100) / 100;
        if (discountedTotal < lineTotal - 0.005) {
          priceHtml = '<p class="msv2-line-price">' +
            '<s>' + escapeHtml(formatMoney(lineTotal, line.currency)) + '</s> ' +
            (discountedTotal <= 0.005
              ? '<span class="msv2-line-free">' + escapeHtml(SETTINGS.oneTimeFreeLabel || 'FREE') + '</span>'
              : '<span class="msv2-line-discounted">' + escapeHtml(formatMoney(discountedTotal, line.currency)) + '</span>') +
          '</p>';
        }
      }

      var metaHtml = line.is_one_time
        ? '<p class="msv2-line-freq"><span class="msv2-line-onetime">' + escapeHtml(SETTINGS.oneTimeLabel || 'Next order only') + '</span></p>'
        : (freq ? '<p class="msv2-line-freq">' + escapeHtml(freq) + '</p>' : '');

      var outOfStock = isLineOutOfStock(line);
      var oosLabel = lineAvailabilityLabel(line);
      var oosHtml = outOfStock
        ? '<p class="msv2-line-oos">' + escapeHtml(oosLabel) + '</p>'
        : '';

      // One-time reward lines lock the upcoming cycle and can't be edited or
      // removed from the portal, so they get no action buttons. Hide Remove when
      // this is the only recurring line — cancelling is the way to end the box.
      var canRemove = recurringLineCount() > 1;
      var actionsHtml = line.is_one_time
        ? ''
        : '<div class="msv2-line-actions">' +
            '<button type="button" class="msv2-link msv2-link--xs" data-action="line-edit">' + escapeHtml(SETTINGS.editLabel) + '</button>' +
            (canRemove
              ? '<button type="button" class="msv2-link msv2-link--xs" data-action="line-remove">' + escapeHtml(SETTINGS.removeLabel || 'Remove') + '</button>'
              : '') +
          '</div>';

      return (
        '<div class="msv2-line' + (line.is_one_time ? ' msv2-line--onetime' : '') + (outOfStock ? ' msv2-line--oos' : '') + '" data-line-id="' + escapeAttr(String(line.line_id || '')) + '">' +
          '<div class="msv2-line-imgwrap">' +
            '<a href="' + escapeAttr(line.product_url || '#') + '" target="_blank" class="msv2-line-img' + (outOfStock ? ' is-oos' : '') + '">' +
              (img ? '<img src="' + escapeAttr(img) + '" alt="' + escapeAttr(alt) + '" loading="lazy">' : NO_IMAGE_SVG) +
            '</a>' +
            (qty > 1 ? '<span class="msv2-line-qty">' + qty + '</span>' : '') +
          '</div>' +
          '<div class="msv2-line-info">' +
            '<p class="msv2-line-title">' + escapeHtml(line.product_title || '') + '</p>' +
            metaHtml +
            (line.variant_title && line.variant_title !== 'Default Title'
              ? '<p class="msv2-line-variant">' + escapeHtml(line.variant_title) + '</p>' : '') +
            oosHtml +
            priceHtml +
            actionsHtml +
          '</div>' +
        '</div>'
      );
    }).join('');
    lockLinesHeight(host, lines, perPage, totalPages);
    renderLinesPagination(totalPages);
  }

  /* ── render: order summary ── */
  function renderSummary() {
    if (!contract) return;
    var lines = Array.isArray(contract.lines) ? contract.lines : [];
    var currency = (contract.deliveryPrice && contract.deliveryPrice.currencyCode) || (lines[0] && lines[0].currency) || 'AUD';
    var pp = contract.pricing_preview || {};

    var estimatedOrderTotal = Number(pp.estimated_order_total_pre_tax_after_pending_discounts) || 0;
    var deliveryInEstimate = Number(contract.deliveryPrice && contract.deliveryPrice.amount) || 0;
    var totalNow = Math.max(0, estimatedOrderTotal - deliveryInEstimate);
    var linesSubtotal = Number(pp.lines_subtotal) || 0;

    // pricing_preview only covers recurring lines: one-time reward items (and
    // the reward discounts entitled to them) live on the next billing cycle
    // edit, so add their full price to the subtotal and their savings to the
    // discount row.
    var ot = oneTimeTotals(contract);
    var subtotalShown = linesSubtotal + ot.subtotal;
    var discount = (linesSubtotal - totalNow) + (ot.subtotal - ot.after);
    var hasDiscount = discount > 0.005;

    var subEl = root.querySelector('[data-subtotal]');
    if (subEl) subEl.textContent = formatMoney(subtotalShown, currency);

    var dRow = root.querySelector('[data-discount-row]');
    var dVal = root.querySelector('[data-discount-value]');
    if (dRow) dRow.style.display = hasDiscount ? '' : 'none';
    if (dVal && hasDiscount) dVal.textContent = '-' + formatMoney(discount, currency);

    var ship = computeShippingPreview(contract);
    var shipVal = root.querySelector('[data-shipping-value]');
    var shipAmount = 0;
    if (ship) {
      shipAmount = ship.amount;
      if (shipVal) shipVal.textContent = ship.amount === 0 ? SETTINGS.freeShippingLabel : formatMoney(ship.amount, ship.currency);
    } else if (shipVal) {
      shipVal.textContent = SETTINGS.calculatedShippingLabel;
    }

    var totalEl = root.querySelector('[data-total]');
    if (totalEl) totalEl.textContent = formatMoney(totalNow + ot.after + shipAmount, currency);

    var listEl = root.querySelector('[data-discount-list]');
    if (listEl) {
      listEl.classList.remove('is-loading');
      var codes = Array.isArray(contract.applied_discount_codes) ? contract.applied_discount_codes : [];
      // Codes living only on the next billing cycle edit (reward codes) can't
      // be removed: the cycle edit locks the contract until the order is
      // processed. The cycle's edited contract also repeats the base contract
      // discounts, so a code is cycle-only when it's absent from the base list.
      var cycleCodes = (contract.next_billing && Array.isArray(contract.next_billing.applied_discount_codes))
        ? contract.next_billing.applied_discount_codes : [];
      var baseTitles = (Array.isArray(contract.discounts) ? contract.discounts : [])
        .map(function (d) { return String((d && d.title) || '').toUpperCase(); });
      var lockedCodes = cycleCodes.filter(function (c) {
        return baseTitles.indexOf(String(c).toUpperCase()) === -1;
      });
      listEl.innerHTML = codes.map(function (c) {
        var html = '<span class="msv2-coupon">' + escapeHtml(c);
        if (isRemovableCoupon(c) && lockedCodes.indexOf(c) === -1) {
          html += '<button type="button" class="msv2-coupon-remove" data-coupon-remove="' + escapeAttr(c) + '" aria-label="' + escapeAttr(SETTINGS.promoRemoveLabel || 'Remove') + ' ' + escapeAttr(c) + '">' +
            '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M5 5l10 10M15 5 5 15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
          '</button>';
        }
        return html + '</span>';
      }).join('');
    }
  }

  /* Loyalty ladder discounts ("10% off next box (2)" / legacy "Subscription loyalty · ...")
     and the cancel-flow retention offer are managed by the backend / cancel flow
     and must not be removable from the promo list. */
  function isRemovableCoupon(code) {
    var c = String(code || '').trim();
    if (!c) return false;
    if (/^\d+%\s+off\s+next\s+box\s*\(\d+\)$/i.test(c)) return false;
    if (c.indexOf('Subscription loyalty') === 0) return false;
    if (/^20%\s+off\s+next\s+order\b/i.test(c)) return false;
    return true;
  }

  /* ── partial skeleton states (used while a mutation + refresh is in flight) ── */
  function setSummaryLoading() {
    ['[data-subtotal]', '[data-discount-value]', '[data-shipping-value]', '[data-total]'].forEach(function (sel) {
      var el = root.querySelector(sel);
      if (el) el.innerHTML = '<span class="msv2-sk msv2-sk--w80"></span>';
    });
    var dRow = root.querySelector('[data-discount-row]');
    if (dRow) dRow.style.display = '';
    var listEl = root.querySelector('[data-discount-list]');
    if (listEl) listEl.classList.add('is-loading');
    root.setAttribute('data-loading', 'true');
  }
  function setLinesLoading() {
    var host = root.querySelector('[data-lines]');
    if (!host) return;
    var lineCount = getDisplayLines(contract).length;
    var count = Math.max(1, Math.min(linesPerPage(), lineCount + 1));
    var row =
      '<div class="msv2-line">' +
        '<div class="msv2-line-img msv2-sk-box"></div>' +
        '<div class="msv2-line-info">' +
          '<span class="msv2-sk msv2-sk--w160"></span>' +
          '<span class="msv2-sk msv2-sk--w120 msv2-sk--sm"></span>' +
          '<span class="msv2-sk msv2-sk--w80 msv2-sk--sm"></span>' +
        '</div>' +
      '</div>';
    host.innerHTML = new Array(count + 1).join(row);
    root.setAttribute('data-loading', 'true');
  }
  /* Restore real content from the in-memory contract (mutation failed, no refetch). */
  function restoreFromContract() {
    renderNextOrder();
    renderSummary();
    root.setAttribute('data-loading', 'false');
    refreshOpenShopCards();
  }

  /* ── promo code ── */
  function setPromoMsg(msg, ok) {
    var el = root.querySelector('[data-promo-msg]');
    if (!el) return;
    el.textContent = msg || '';
    el.classList.toggle('is-error', !ok && !!msg);
    el.classList.toggle('is-success', !!ok && !!msg);
  }
  function wirePromoForm() {
    var form = root.querySelector('[data-promo-form]');
    if (!form) return;
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var input = form.querySelector('[data-promo-input]');
      var btn = form.querySelector('[data-promo-apply]');
      var code = (input && input.value || '').trim();
      if (!code || !contract || !contract.id || SETTINGS.designMode) return;
      setPromoMsg('');
      btn.disabled = true;
      btn.classList.add('is-loading');
      setSummaryLoading();
      fetch('/apps/customer/apply_discount_code.php', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ subscriptionContractId: contract.id, discountCode: code })
      })
        .then(function (r) { return r.json().catch(function () { return {}; }).then(function (d) { return { ok: r.ok, data: d }; }); })
        .then(function (res) {
          if (!res.ok || res.data.error || res.data.success === false) {
            setPromoMsg(res.data.message || SETTINGS.genericErrorText, false);
            restoreFromContract();
            return;
          }
          var msg = SETTINGS.promoSuccessText;
          var replaced = Array.isArray(res.data.replaced_codes) ? res.data.replaced_codes : [];
          if (replaced.length && SETTINGS.promoReplacedText) {
            msg += ' ' + SETTINGS.promoReplacedText.replace('[code]', replaced.join(', '));
          }
          setPromoMsg(msg, true);
          input.value = '';
          reloadContracts();
        })
        .catch(function () {
          setPromoMsg(SETTINGS.genericErrorText, false);
          restoreFromContract();
        })
        .finally(function () {
          btn.disabled = false;
          btn.classList.remove('is-loading');
        });
    });
  }
  function wireCouponRemoval() {
    var listEl = root.querySelector('[data-discount-list]');
    if (!listEl) return;
    listEl.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-coupon-remove]');
      if (!btn || btn.disabled) return;
      var code = btn.getAttribute('data-coupon-remove');
      if (!code || !contract || !contract.id || SETTINGS.designMode) return;
      openCouponRemoveOverlay(code);
    });
  }

  /* ══════════ COUPON REMOVE CONFIRM MODAL ══════════ */
  var couponRemoveOverlay = document.getElementById('msv2-coupon-remove-overlay');
  var couponRemoveState = { code: '', removing: false };

  function setCouponRemoveMsg(msg) {
    var el = couponRemoveOverlay && couponRemoveOverlay.querySelector('[data-coupon-remove-msg]');
    if (!el) return;
    el.hidden = !msg;
    el.textContent = msg || '';
  }

  function setCouponRemoveLoading(on) {
    couponRemoveState.removing = !!on;
    if (!couponRemoveOverlay) return;
    couponRemoveOverlay.classList.toggle('is-coupon-removing', couponRemoveState.removing);
    var confirmBtn = couponRemoveOverlay.querySelector('[data-coupon-remove-confirm]');
    var cancelBtns = couponRemoveOverlay.querySelectorAll('[data-coupon-remove-close]');
    if (confirmBtn) {
      confirmBtn.disabled = couponRemoveState.removing;
      confirmBtn.classList.toggle('is-loading', couponRemoveState.removing);
      confirmBtn.textContent = couponRemoveState.removing
        ? (SETTINGS.promoRemovingText || 'Removing…')
        : (SETTINGS.promoRemoveConfirmBtn || 'Yes, remove code');
    }
    cancelBtns.forEach(function (b) { b.disabled = couponRemoveState.removing; });
  }

  function openCouponRemoveOverlay(code) {
    if (!couponRemoveOverlay || !code) return;
    couponRemoveState.code = code;
    setCouponRemoveMsg('');
    setCouponRemoveLoading(false);
    var codeEl = couponRemoveOverlay.querySelector('[data-coupon-remove-code]');
    if (codeEl) {
      codeEl.textContent = code;
      codeEl.hidden = !code;
    }
    couponRemoveOverlay.classList.add('is-open');
    couponRemoveOverlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('msv2-no-scroll');
  }

  function closeCouponRemoveOverlay() {
    if (!couponRemoveOverlay || couponRemoveState.removing) return;
    couponRemoveOverlay.classList.remove('is-open');
    couponRemoveOverlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('msv2-no-scroll');
    couponRemoveState.code = '';
    setCouponRemoveMsg('');
  }

  function confirmCouponRemove() {
    if (!couponRemoveOverlay || couponRemoveState.removing) return;
    var code = (couponRemoveState.code || '').trim();
    if (!code || !contract || !contract.id) {
      setCouponRemoveMsg(SETTINGS.genericErrorText || 'Something went wrong.');
      return;
    }

    setCouponRemoveLoading(true);
    setSummaryLoading();
    fetch('/apps/customer/remove_discount_code.php', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ subscriptionContractId: contract.id, discountCode: code })
    })
      .then(function (r) { return r.json().catch(function () { return {}; }).then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        if (!res.ok || res.data.error || res.data.success === false) {
          setCouponRemoveLoading(false);
          setCouponRemoveMsg(res.data.message || SETTINGS.genericErrorText || 'Something went wrong.');
          restoreFromContract();
          return;
        }
        couponRemoveState.removing = false;
        couponRemoveOverlay.classList.remove('is-coupon-removing', 'is-open');
        couponRemoveOverlay.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('msv2-no-scroll');
        couponRemoveState.code = '';
        setPromoMsg(SETTINGS.promoRemovedText, true);
        reloadContracts();
      })
      .catch(function () {
        setCouponRemoveLoading(false);
        setCouponRemoveMsg(SETTINGS.genericErrorText || 'Something went wrong.');
        restoreFromContract();
      });
  }

  function wireCouponRemoveOverlay() {
    if (!couponRemoveOverlay) return;
    couponRemoveOverlay.addEventListener('click', function (e) {
      if (e.target.closest('[data-coupon-remove-close]')) {
        closeCouponRemoveOverlay();
        return;
      }
      var confirmBtn = e.target.closest('[data-coupon-remove-confirm]');
      if (confirmBtn && !confirmBtn.disabled) confirmCouponRemove();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && couponRemoveOverlay.classList.contains('is-open')) closeCouponRemoveOverlay();
    });
  }

  /* Removable discount codes entitled to a subscription line (reward / promo). */
  function removableCodesForLine(lineId) {
    if (!contract || !lineId) return [];
    var out = [];
    var seen = {};
    function scan(list) {
      (Array.isArray(list) ? list : []).forEach(function (d) {
        if (!d) return;
        var title = String(d.title || '').trim();
        if (!title || !isRemovableCoupon(title)) return;
        var limit = d.recurringCycleLimit != null ? d.recurringCycleLimit : d.recurring_cycle_limit;
        var used = d.usageCount != null ? d.usageCount : d.usage_count;
        if (limit != null && used != null && Number(used) >= Number(limit)) return;
        var el = d.entitled_lines || {};
        var ids = el.line_ids || el.lineIds || [];
        var hit = false;
        if (el.all) {
          // Whole-box codes stay if other lines remain — only warn when this
          // line is clearly discounted by a product-specific code below.
          hit = false;
        } else {
          for (var i = 0; i < ids.length; i++) {
            if (String(ids[i]) === String(lineId)) {
              hit = true;
              break;
            }
          }
        }
        if (!hit) return;
        var key = title.toUpperCase();
        if (seen[key]) return;
        seen[key] = true;
        out.push(title);
      });
    }
    scan(contract.discounts);
    scan(contract.next_billing && contract.next_billing.discounts);
    return out;
  }

  /* ── shipping & payment ── */
  /* Single-line summary of a contract's own delivery address. Street plus
     suburb/state/postcode, so two saved addresses on the same street stay
     distinguishable. */
  function formatAddressLine(addr) {
    if (!addr) return '';
    var street = [addr.address1 || addr.line1, addr.address2]
      .filter(function (part) { return part; })
      .join(' ');
    var region = [addr.city, [addr.provinceCode || addr.province, addr.zip].filter(Boolean).join(' ')]
      .filter(function (part) { return part; })
      .join(', ');
    return [street, region].filter(function (part) { return part; }).join(', ');
  }

  function renderShippingPayment() {
    if (!contract) return;
    var addrEl = root.querySelector('[data-address-line]');
    if (addrEl) {
      /* The contract carries its own copy of the delivery address, which can
         differ from the account's default address. */
      var addrText = formatAddressLine(contract.delivery_address || contract.shipping_address);
      addrEl.textContent = addrText || SETTINGS.addressNoneText || '';
      addrEl.classList.toggle('is-warning', !addrText);
    }
    var payEl = root.querySelector('[data-payment-line]');
    var payBtn = root.querySelector('[data-action="edit-payment"]');
    if (payEl) {
      var pm = contract.payment_method || null;
      if (pm && (pm.brand || pm.last4)) {
        payEl.textContent = (pm.brand || 'Card') + (pm.last4 ? ' \u2022\u2022\u2022\u2022' + pm.last4 : '');
        payEl.classList.remove('is-warning');
        if (payBtn) payBtn.textContent = SETTINGS.editLabel;
      } else if (pm && pm.type === 'none') {
        // Contract has no payment method attached: warn and offer to attach.
        payEl.textContent = SETTINGS.paymentNoneText;
        payEl.classList.add('is-warning');
        if (payBtn) payBtn.textContent = SETTINGS.paymentAttachLabel;
      } else {
        // Replace the skeleton with the generic label when the backend has no
        // card details (e.g. payment scope not granted yet).
        payEl.textContent = SETTINGS.paymentPlaceholderText || 'Payment method on file';
        payEl.classList.remove('is-warning');
        if (payBtn) payBtn.textContent = SETTINGS.editLabel;
      }
    }
  }

  /* ── payment method update ── */
  // ok: true (success, green), false (error, red), 'steps' (neutral hint panel).
  function setPaymentMsg(html, ok) {
    var el = root.querySelector('[data-payment-msg]');
    if (!el) return;
    if (!html) {
      el.hidden = true;
      el.innerHTML = '';
      el.classList.remove('is-error', 'is-success', 'is-steps');
      return;
    }
    el.hidden = false;
    el.innerHTML = html;
    el.classList.toggle('is-error', ok === false);
    el.classList.toggle('is-success', ok === true);
    el.classList.toggle('is-steps', ok === 'steps');
  }

  function buildAddCardStepsHtml() {
    var accountUrl = SETTINGS.accountProfileUrl || '/account';
    return (
      '<span class="msv2-payment-steps-title">' + escapeHtml(SETTINGS.paymentAddTitle) + '</span>' +
      '<span class="msv2-payment-step"><span class="msv2-payment-step-num">1</span><span>' + escapeHtml(SETTINGS.paymentAddStep1) + '</span></span>' +
      '<span class="msv2-payment-step"><span class="msv2-payment-step-num">2</span><span>' + escapeHtml(SETTINGS.paymentAddStep2) + '</span></span>' +
      '<a class="msv2-btn msv2-btn--primary msv2-btn--sm msv2-payment-add-link" href="' + escapeAttr(accountUrl) + '" target="_blank" rel="noopener">' +
        escapeHtml(SETTINGS.paymentUpdateAddMethodLink) +
        '<svg viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M4.5 2.5h5v5M9.5 2.5 2.5 9.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '</a>'
    );
  }

  function watchPaymentPopup(popup) {
    var timer = setInterval(function () {
      if (!popup || popup.closed) {
        clearInterval(timer);
        // Cross-origin: we can't know whether the card was actually changed,
        // so refresh the contract either way to pick up new brand/last4.
        reloadContracts();
      }
    }, 800);
  }

  function wirePaymentEdit() {
    var btn = root.querySelector('[data-action="edit-payment"]');
    if (!btn) return;
    btn.addEventListener('click', function () {
      if (SETTINGS.designMode) {
        var demo = (SETTINGS.paymentUpdateEmailSent || '').replace('[email]', 'j***@example.com');
        setPaymentMsg(escapeHtml(demo), true);
        return;
      }
      if (!contract || !contract.id) return;

      var pm = contract.payment_method || null;
      var isShopPay = !!(pm && pm.type === 'shop_pay');

      // Shop Pay gets a hosted update URL; the popup must be opened
      // synchronously in the click handler or popup blockers kill it.
      var popup = null;
      if (isShopPay) {
        var w = 480;
        var h = 720;
        var left = Math.max(0, Math.round((window.screen.width - w) / 2));
        var top = Math.max(0, Math.round((window.screen.height - h) / 2));
        popup = window.open('about:blank', 'msv2-payment-update', 'width=' + w + ',height=' + h + ',left=' + left + ',top=' + top);
        if (popup) {
          try {
            popup.document.write('<p style="font-family: sans-serif; padding: 24px; color: #374151;">' + escapeHtml(SETTINGS.paymentUpdateSending) + '</p>');
          } catch (err) { /* ignore */ }
        }
      }

      btn.disabled = true;
      btn.classList.add('is-loading');
      setPaymentMsg(escapeHtml(SETTINGS.paymentUpdateSending), true);

      fetch('/apps/customer/payment_method_update.php', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ subscriptionContractId: contract.id })
      })
        .then(function (r) { return r.json().catch(function () { return {}; }).then(function (d) { return { ok: r.ok, data: d }; }); })
        .then(function (res) {
          var data = res.data || {};
          if (data.code === 'ADD_PAYMENT_METHOD') {
            // Nothing vaulted on the account: show the two-step hint (add a
            // card in the Shopify account first, then attach it here).
            if (popup) popup.close();
            setPaymentMsg(buildAddCardStepsHtml(), 'steps');
            return;
          }
          if (!res.ok || data.error || data.success === false) {
            if (popup) popup.close();
            setPaymentMsg(escapeHtml(data.message || SETTINGS.genericErrorText), false);
            return;
          }
          if (data.mode === 'attached') {
            // A vaulted method existed and the backend linked it to the contract.
            if (popup) popup.close();
            var label = data.brand ? (data.brand + (data.last4 ? ' \u2022\u2022\u2022\u2022' + data.last4 : '')) : '';
            var attachedMsg = label
              ? (SETTINGS.paymentUpdateAttached || '').replace('[method]', label)
              : SETTINGS.paymentUpdateAttachedGeneric;
            setPaymentMsg(escapeHtml(attachedMsg), true);
            reloadContracts();
            return;
          }
          if (data.mode === 'url' && data.url) {
            if (popup) {
              popup.location = data.url;
              setPaymentMsg('');
              watchPaymentPopup(popup);
            } else {
              // Popup blocked: give the customer a direct link instead.
              setPaymentMsg(
                '<a href="' + escapeAttr(data.url) + '" target="_blank" rel="noopener">' + escapeHtml(SETTINGS.paymentUpdateOpenLink) + '</a>',
                true
              );
            }
            return;
          }
          // Email mode (regular cards, or Shop Pay URL fallback on the backend).
          if (popup) popup.close();
          var msg = data.email
            ? (SETTINGS.paymentUpdateEmailSent || '').replace('[email]', data.email)
            : SETTINGS.paymentUpdateEmailSentGeneric;
          setPaymentMsg(escapeHtml(msg), true);
        })
        .catch(function () {
          if (popup) popup.close();
          setPaymentMsg(escapeHtml(SETTINGS.genericErrorText), false);
        })
        .finally(function () {
          btn.disabled = false;
          btn.classList.remove('is-loading');
        });
    });
  }

  /* ── contract picker ── */
  function renderContractPicker(selectable, selectedId, onChange) {
    var host = root.querySelector('[data-contract-picker]');
    if (!host) return;
    if (!Array.isArray(selectable) || selectable.length <= 1) {
      host.style.display = 'none';
      host.innerHTML = '';
      return;
    }
    var opts = selectable.map(function (c, idx) {
      return '<option value="' + escapeAttr(String(c.id || '')) + '">' + escapeHtml(formatContractLabel(c, idx)) + '</option>';
    }).join('');
    host.innerHTML =
      '<label class="msv2-contract-label">' + escapeHtml(SETTINGS.chooseSubscriptionLabel) + '</label>' +
      '<select class="msv2-contract-select" data-contract-select>' + opts + '</select>';
    host.style.display = 'flex';
    var sel = host.querySelector('[data-contract-select]');
    if (sel) {
      sel.value = String(selectedId || '');
      sel.addEventListener('change', function () { onChange(sel.value); });
    }
  }

  /* ── render everything from a contract ── */
  function renderPortal(selected) {
    if (!contract || !selected || String(contract.id) !== String(selected.id)) {
      linesPage = 1;
      reschedCyclesLoadedFor = null;
      reschedCycles = [];
      reschedCycleByDay = {};
      reschedSelected = {};
    }
    contract = selected;
    if (!contract) {
      setError(SETTINGS.genericErrorText);
      root.setAttribute('data-loading', 'false');
      return;
    }
    clearError();
    root.removeAttribute('data-empty');
    root.removeAttribute('data-paused');
    if (!SETTINGS.designMode) {
      sessionStorage.setItem('contract_id', contract.id);
      sessionStorage.setItem('contract_status', contract.status);
    }

    /* Non-ACTIVE contracts: hide next-order content and show the
       empty "No active subscription" state instead of the grid. */
    if (String(contract.status || '').toUpperCase() !== 'ACTIVE') {
      root.setAttribute('data-inactive', 'true');
      if (String(contract.status || '').toUpperCase() === 'PAUSED') {
        root.setAttribute('data-paused', 'true');
      }
      renderPendingCancellation();
      renderPauseControls();
      root.setAttribute('data-loading', 'false');
      return;
    }

    root.removeAttribute('data-inactive');
    renderNextOrder();
    renderSummary();
    renderShippingPayment();
    renderPendingCancellation();
    renderPauseControls();
    root.setAttribute('data-loading', 'false');
    refreshOpenShopCards();
    loadLoyalty();
    // Non-blocking: stock comes from storefront /products/{handle}.js, not Admin.
    enrichContractLinesAvailability(contract);
  }

  /* Deferred cancellation: when the selected contract has a pending
     cancellation request, the account tab shows the amber notice with a
     reactivate button instead of the "Cancel my subscription" button.
     Cancel is also hidden for non-ACTIVE contracts (paused/cancelled/etc.). */
  function renderPendingCancellation() {
    var host = root.querySelector('[data-pending-cancel]');
    var cancelOpenBtn = root.querySelector('[data-cancel-open]');
    var pending = contract && contract.pending_cancellation;
    var isActive = !!(contract && contract.status === 'ACTIVE');
    if (host) {
      host.hidden = !pending;
      if (pending) {
        var isPaused = contract && contract.status === 'PAUSED';
        var defaultEl = host.querySelector('[data-pending-text-default]');
        var pausedEl = host.querySelector('[data-pending-text-paused]');
        if (defaultEl) defaultEl.hidden = isPaused;
        if (pausedEl) pausedEl.hidden = !isPaused;
        if (!isPaused) {
          var dateEl = host.querySelector('[data-pending-final-date]');
          if (dateEl) {
            var dateText = pending.final_charge_date ? formatDate(pending.final_charge_date) : '';
            dateEl.textContent = dateText || SETTINGS.cancelDateFallbackText || 'its next scheduled date';
          }
        }
      }
    }
    if (cancelOpenBtn) cancelOpenBtn.hidden = !isActive || !!pending;
  }

  /* ── theme editor demo data (design mode only, no customer session) ── */
  function buildDemoContract() {
    var nextBilling = new Date();
    nextBilling.setDate(nextBilling.getDate() + 5);
    return {
      id: 'demo',
      status: 'ACTIVE',
      next_billing: { date: nextBilling.toISOString() },
      billingPolicy: { interval: 'WEEK', intervalCount: 2 },
      // Enough demo lines to preview the products-per-page pagination.
      lines: [
        'Baby Meals Box', 'Roasted Chicken & Thyme', 'Green Pea & Mint Blend',
        'Chicken & Veggie Risotto', 'Beef Parsnip Purée', 'Salmon & Sweet Potato'
      ].map(function (title, idx) {
        return {
          line_id: 'demo-' + (idx + 1),
          product_title: title,
          variant_title: '100% Whole Foods',
          quantity: idx === 0 ? 10 : 1,
          price: 7.5,
          currency: 'AUD',
          product_url: '#'
        };
      }),
      pricing_preview: {
        lines_subtotal: 150,
        estimated_order_total_pre_tax_after_pending_discounts: 120
      },
      deliveryPrice: { amount: 0, currencyCode: 'AUD' },
      shipping: {
        enabled: true,
        method_type: 'SubscriptionDeliveryMethodShipping',
        free_threshold: 100,
        standard_rate: 10
      },
      applied_discount_codes: [],
      delivery_address: { address1: '86 Campbell Street', city: 'Surry Hills', provinceCode: 'NSW', zip: '2010' },
      payment_method: { brand: 'Mastercard', last4: '5183' },
      // Theme editor: show Send now without a real billing window.
      send_now: { allowed: true }
    };
  }

  /* ── contracts fetch ── */
  var allContracts = [];
  /* Bumps on each get_subscriptions refresh so stale responses are ignored. */
  var contractsFetchGen = 0;
  /* No contracts at all: swap the portal content for the empty state
     (root data-empty="true", see the CSS). The Account tab keeps working. */
  function showEmptyState() {
    clearError();
    root.removeAttribute('data-inactive');
    root.removeAttribute('data-paused');
    root.setAttribute('data-empty', 'true');
    root.setAttribute('data-loading', 'false');
  }
  function loadContracts() {
    if (SETTINGS.designMode) {
      renderPortal(buildDemoContract());
      return Promise.resolve();
    }
    return fetch('/apps/customer/get_subscriptions.php', { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        clearError();
        if (!data || data.ok !== true) {
          setError((data && (data.reason || data.message)) || '');
          root.setAttribute('data-loading', 'false');
          return;
        }
        allContracts = Array.isArray(data.contracts) ? data.contracts : [];
        if (!allContracts.length) {
          showEmptyState();
          return;
        }
        var selectable = allContracts.filter(isSelectableContract);
        var remembered = sessionStorage.getItem('contract_id_preferred') || (function () {
          try { return localStorage.getItem('contract_id_preferred') || ''; } catch (e) { return ''; }
        })();
        var initial = (remembered && selectable.find(function (c) { return String(c.id) === String(remembered); }))
          || pickPrimaryContract(selectable)
          || pickPrimaryContract(allContracts);

        renderContractPicker(selectable, initial && initial.id, function (nextId) {
          sessionStorage.setItem('contract_id_preferred', String(nextId || ''));
          try { localStorage.setItem('contract_id_preferred', String(nextId || '')); } catch (e) {}
          root.setAttribute('data-loading', 'true');
          var next = selectable.find(function (c) { return String(c.id) === String(nextId); })
            || allContracts.find(function (c) { return String(c.id) === String(nextId); });
          renderPortal(next);
        });

        if (initial && initial.id) {
          sessionStorage.setItem('contract_id_preferred', String(initial.id));
          try { localStorage.setItem('contract_id_preferred', String(initial.id)); } catch (e) {}
        }
        renderPortal(initial);
      })
      .catch(function (err) {
        console.error('Subscriptions fetch failed:', err);
        setError(SETTINGS.genericErrorText);
        root.setAttribute('data-loading', 'false');
      });
  }
  function reloadContracts() {
    if (SETTINGS.designMode) return Promise.resolve();
    var id = contract && contract.id;
    var gen = ++contractsFetchGen;
    return fetch('/apps/customer/get_subscriptions.php', { credentials: 'same-origin' })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (gen !== contractsFetchGen) return; // newer fetch already in flight
        if (!data || data.ok !== true) {
          restoreFromContract();
          return;
        }
        allContracts = Array.isArray(data.contracts) ? data.contracts : [];
        if (!allContracts.length) {
          showEmptyState();
          return;
        }
        var next = allContracts.find(function (c) { return String(c.id) === String(id); }) || pickPrimaryContract(allContracts);
        renderPortal(next);
      })
      .catch(function () {
        if (gen !== contractsFetchGen) return;
        restoreFromContract();
      });
  }

  /* ── shared product fetch (native Shopify endpoint, cached per handle) ── */
  var collectionCache = {};
  function shopCurrency() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
  }
  function normalizeShopifyProduct(p) {
    var variants = Array.isArray(p.variants) ? p.variants : [];
    return {
      id: p.id,
      title: p.title,
      handle: p.handle,
      tags: p.tags,
      image_url: (p.images && p.images[0] && p.images[0].src) || '',
      product_url: '/products/' + p.handle,
      price: variants[0] ? variants[0].price : null,
      currency: shopCurrency(),
      variants: variants.map(function (v) {
        return {
          id: v.id,
          title: v.title,
          price: v.price,
          compare_at_price: v.compare_at_price,
          available: v.available
        };
      })
    };
  }
  function fetchCollectionProducts(handle) {
    if (!handle) return Promise.resolve([]);
    if (collectionCache[handle]) return collectionCache[handle];
    var promise = fetch('/collections/' + encodeURIComponent(handle) + '/products.json?limit=250', {
      headers: { Accept: 'application/json' }
    })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        return Array.isArray(data.products) ? data.products.map(normalizeShopifyProduct) : [];
      })
      .catch(function () { return []; });
    collectionCache[handle] = promise;
    return promise;
  }

  /* ── product data helpers ── */
  function getProductTags(product) {
    if (Array.isArray(product.tags)) {
      return product.tags.map(function (t) { return String(t.name || t.title || t).toLowerCase().trim(); });
    }
    if (typeof product.tags === 'string' && product.tags) {
      return product.tags.split(',').map(function (t) { return t.toLowerCase().trim(); });
    }
    if (Array.isArray(product.tag_list)) {
      return product.tag_list.map(function (t) { return String(t.name || t.title || t).toLowerCase().trim(); });
    }
    return [];
  }
  function readAvailabilityFlag(obj) {
    if (!obj || typeof obj !== 'object') return null;
    var keys = ['available_for_sale', 'availableForSale', 'available', 'is_available', 'in_stock'];
    for (var i = 0; i < keys.length; i++) {
      if (typeof obj[keys[i]] === 'boolean') return obj[keys[i]];
    }
    return null;
  }
  function isVariantOutOfStock(variant) {
    var flag = readAvailabilityFlag(variant);
    if (flag !== null) return !flag;
    if (variant && typeof variant.inventory_quantity === 'number') return variant.inventory_quantity <= 0;
    return false;
  }
  function isProductOutOfStock(product) {
    var flag = readAvailabilityFlag(product);
    if (flag !== null) return !flag;
    var variants = Array.isArray(product && product.variants) ? product.variants : [];
    if (variants.length) return variants.every(isVariantOutOfStock);
    return false;
  }
  function isLineOutOfStock(line) {
    if (!line) return false;
    if (typeof line.unavailable === 'boolean') return line.unavailable;
    if (typeof line.out_of_stock === 'boolean') return line.out_of_stock;
    if (typeof line.available_for_sale === 'boolean') return !line.available_for_sale;
    var status = String(line.product_status || '').toUpperCase();
    if (status === 'DRAFT' || status === 'ARCHIVED') return true;
    return isVariantOutOfStock(line);
  }
  function lineAvailabilityLabel(line) {
    var status = String((line && line.product_status) || '').toUpperCase();
    if (status === 'DRAFT' || status === 'ARCHIVED') {
      return SETTINGS.unavailableLabel || 'Unavailable';
    }
    if (line && line.out_of_stock) {
      return SETTINGS.outOfStockLabel || SETTINGS.shopOosLabel || 'Out of stock';
    }
    if (line && line.unavailable) {
      return SETTINGS.outOfStockLabel || SETTINGS.shopOosLabel || 'Out of stock';
    }
    return SETTINGS.outOfStockLabel || SETTINGS.shopOosLabel || 'Out of stock';
  }
  /**
   * Optional storefront refine for ACTIVE products when Admin left availability
   * unknown. DRAFT/unpublished products 404 on /products/{handle}.js — those
   * must come from get_subscriptions (Admin product batch).
   * @see https://shopify.dev/docs/api/ajax/reference/product
   */
  function enrichContractLinesAvailability(c) {
    if (!c || SETTINGS.designMode) return Promise.resolve();
    var targets = [];
    (Array.isArray(c.lines) ? c.lines : []).forEach(function (line) { targets.push(line); });
    var free = c.next_billing && c.next_billing.free_items;
    if (Array.isArray(free)) {
      free.forEach(function (line) { targets.push(line); });
    }

    var byHandle = {};
    targets.forEach(function (line) {
      // Admin already resolved this line (including DRAFT → unavailable).
      if (typeof line.available_for_sale === 'boolean' || typeof line.unavailable === 'boolean') return;
      var handle = productHandleFromUrl(line.product_url);
      var vid = normalizeVariantId(line.variant_id);
      if (!handle || !vid) return;
      if (!byHandle[handle]) byHandle[handle] = [];
      byHandle[handle].push({ line: line, variantId: vid });
    });

    var handles = Object.keys(byHandle);
    if (!handles.length) return Promise.resolve();

    return Promise.all(handles.map(function (handle) {
      return fetchProductByHandle(handle).then(function (product) {
        if (!product || !Array.isArray(product.variants)) return;
        var map = {};
        product.variants.forEach(function (v) {
          map[normalizeVariantId(v.id)] = v.available === true;
        });
        byHandle[handle].forEach(function (entry) {
          if (!(entry.variantId in map)) return;
          entry.line.available_for_sale = map[entry.variantId];
          entry.line.out_of_stock = !map[entry.variantId];
          entry.line.unavailable = !map[entry.variantId];
        });
      });
    })).then(function () {
      if (contract && String(contract.id) === String(c.id)) {
        renderNextOrder();
      }
    });
  }

  /* ── meals carousel (sidebar) ── */
  function renderMealCard(product) {
    var img = product.image_url || '';
    var url = product.product_url || '#';
    var variants = Array.isArray(product.variants) ? product.variants : [];
    var first = variants.find(function (v) {
      return !isVariantOutOfStock(v);
    }) || variants[0] || null;
    var variantId = first ? first.id : '';
    var currency = (product.currency || '').trim();
    var price = first && Number.isFinite(Number(first.price)) ? Number(first.price) : Number(product.price);
    var compareAt = first && Number.isFinite(Number(first.compare_at_price)) ? Number(first.compare_at_price) : NaN;
    var priceText = Number.isFinite(price)
      ? formatMoney(price, currency || 'AUD')
      : (product.price_formatted || '');
    var compareHtml = (Number.isFinite(compareAt) && compareAt > price)
      ? '<p class="msv2-product-compare">' + escapeHtml(formatMoney(compareAt, currency || 'AUD')) + '</p>'
      : '';
    return (
      '<div class="msv2-product-card" data-product-id="' + escapeAttr(String(product.id || '')) + '">' +
        '<a class="msv2-product-img" href="' + escapeAttr(url) + '" target="_blank">' +
          (img ? '<img src="' + escapeAttr(img) + '" alt="' + escapeAttr(product.title || '') + '" loading="lazy">' : NO_IMAGE_SVG) +
        '</a>' +
        '<div class="msv2-product-body">' +
          '<a class="msv2-product-title" href="' + escapeAttr(url) + '" target="_blank">' +
            '<span class="msv2-marquee"><span class="msv2-marquee-text">' + escapeHtml(product.title || '') + '</span></span>' +
          '</a>' +
          compareHtml +
          '<p class="msv2-product-price">' + escapeHtml(priceText) + '</p>' +
          '<button type="button" class="msv2-btn msv2-btn--primary msv2-btn--block msv2-btn--sm" ' +
            'data-action="meal-add" data-variant-id="' + escapeAttr(String(variantId || '')) + '"' +
            (variantId ? '' : ' disabled') + '>' + escapeHtml(SETTINGS.addLabel) + '</button>' +
        '</div>' +
      '</div>'
    );
  }
  /* Titles are clamped to one line; when the text overflows, duplicate it
     so the CSS can loop it as a marquee while the card is hovered
     (speed proportional to length). */
  function initTitleMarquees(host) {
    host.querySelectorAll('.msv2-product-title').forEach(function (title) {
      if (title.classList.contains('is-scrolling')) return;
      var track = title.querySelector('.msv2-marquee');
      var text = track && track.querySelector('.msv2-marquee-text');
      if (!track || !text) return;
      if (text.getBoundingClientRect().width <= title.clientWidth + 1) return;
      title.classList.add('is-scrolling');
      track.appendChild(text.cloneNode(true));
      var loopWidth = text.getBoundingClientRect().width;
      title.style.setProperty('--msv2-marquee-dur', Math.max(4, loopWidth / 25) + 's');
    });
  }

  function loadMeals() {
    var host = root.querySelector('[data-meals-host]');
    if (!host || !SETTINGS.mealsCollection) return;
    fetchCollectionProducts(SETTINGS.mealsCollection)
      .then(function (products) {
        if (!products.length) {
          host.innerHTML = '<p class="msv2-card-sub">' + escapeHtml(SETTINGS.noProductsText) + '</p>';
          return;
        }
        host.innerHTML = products.map(renderMealCard).join('');
        initTitleMarquees(host);
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(function () { initTitleMarquees(host); });
        }
      });
  }
  function addMealToBox(btn) {
    if (SETTINGS.designMode) return;
    var variantId = btn.getAttribute('data-variant-id');
    if (!variantId || !contract || !contract.id) return;
    btn.disabled = true;
    btn.classList.add('is-loading');
    setLinesLoading();
    setSummaryLoading();
    fetch('/apps/customer/subscription_add_item.php', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        subscriptionContractId: contract.id,
        variantId: 'gid://shopify/ProductVariant/' + String(variantId).replace(/\D/g, ''),
        quantity: 1
      })
    })
      .then(function (r) { return r.json().catch(function () { return {}; }).then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        if (!res.ok || res.data.error) {
          setError(res.data.message || SETTINGS.genericErrorText);
          restoreFromContract();
          return;
        }
        if (res.data.contract && res.data.contract.pricing_preview && contract) {
          contract.lines = Array.isArray(res.data.contract.lines) ? res.data.contract.lines : contract.lines;
          contract.pricing_preview = res.data.contract.pricing_preview;
          restoreFromContract();
        } else {
          reloadContracts();
        }
        setSuccess(SETTINGS.itemAddedText);
      })
      .catch(function () {
        setError(SETTINGS.genericErrorText);
        restoreFromContract();
      })
      .finally(function () {
        btn.disabled = false;
        btn.classList.remove('is-loading');
      });
  }

  /* ── carousels ── */
  function wireCarousels() {
    root.querySelectorAll('[data-scroll-prev], [data-scroll-next]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var key = btn.getAttribute('data-scroll-prev') || btn.getAttribute('data-scroll-next');
        var region = root.querySelector('[data-scroll-region="' + key + '"]');
        if (!region) return;
        var dir = btn.hasAttribute('data-scroll-prev') ? -1 : 1;
        var card = region.firstElementChild;
        var step = card ? card.getBoundingClientRect().width + 15 : 200;
        region.scrollBy({ left: dir * step, behavior: 'smooth' });
      });
    });

    // Mouse drag-to-scroll on horizontal reward / meal carousels.
    // Touch keeps native overflow scrolling; we only capture mouse pointers.
    root.querySelectorAll('[data-scroll-region]').forEach(wireDragScroll);
  }

  function wireDragScroll(region) {
    if (!region || region.getAttribute('data-drag-scroll') === '1') return;
    region.setAttribute('data-drag-scroll', '1');
    region.classList.add('msv2-scroll--draggable');

    var active = false;
    var dragged = false;
    var startX = 0;
    var startScroll = 0;
    var pointerId = null;

    function onMove(e) {
      if (!active || e.pointerId !== pointerId) return;
      var dx = e.clientX - startX;
      if (!dragged && Math.abs(dx) < 6) return;
      if (!dragged) {
        dragged = true;
        region.classList.add('is-dragging');
      }
      region.scrollLeft = startScroll - dx;
      e.preventDefault();
    }

    function onUp(e) {
      if (!active || (pointerId != null && e.pointerId !== pointerId)) return;
      active = false;
      pointerId = null;
      region.classList.remove('is-dragging');
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
      // Suppress the click that follows a real drag (not a tap on a product link).
      if (dragged) {
        window.setTimeout(function () { dragged = false; }, 0);
      }
    }

    region.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      // Don't hijack Add / Redeem controls — only start drag from the card chrome.
      if (e.target.closest('button, [data-action="meal-add"], [data-action="reward-redeem"], [data-action="reward-add-to-box"], input, textarea, select, label')) {
        return;
      }
      // Stop native image ghost-drag on photos; do NOT preventDefault on <a>
      // or product links won't open.
      if (e.target.closest('img, picture')) {
        e.preventDefault();
      }
      active = true;
      dragged = false;
      startX = e.clientX;
      startScroll = region.scrollLeft;
      pointerId = e.pointerId;
      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onUp);
      document.addEventListener('pointercancel', onUp);
    });

    region.addEventListener('dragstart', function (e) {
      e.preventDefault();
    });

    region.addEventListener('click', function (e) {
      if (!dragged) return;
      e.preventDefault();
      e.stopPropagation();
    }, true);
  }

  /* ── address overlay ── */
  function showAddressView(overlay, name) {
    overlay.querySelectorAll('[data-addr-view]').forEach(function (view) {
      view.hidden = view.getAttribute('data-addr-view') !== name;
    });
  }

  /* Re-render the overlay (address list + edit forms) from a fresh copy of
     the current page, so a saved address shows up without a full reload.
     The card's address line comes from the contract, not from this markup. */
  function refreshAddressOverlay(overlay, closeOverlay) {
    return fetch(window.location.href, { credentials: 'same-origin' })
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.getElementById('msv2-address-overlay');
        if (fresh) {
          overlay.innerHTML = fresh.innerHTML;
          wireAddressContent(overlay, closeOverlay);
        }
      });
  }

  /* status: 'pending' | 'error' | '' */
  function setAddressMsg(overlay, text, status) {
    var el = overlay.querySelector('[data-addr-msg]');
    if (!el) return;
    el.textContent = text || '';
    el.hidden = !text;
    el.classList.toggle('is-pending', status === 'pending');
    el.classList.toggle('is-error', status === 'error');
  }

  /* Contracts keep their own copy of the delivery address, so a new default
     address only reaches them through this endpoint. */
  function syncDefaultAddressToSubscriptions() {
    if (SETTINGS.designMode) return Promise.resolve({ success: true, updated: [] });
    return fetch('/apps/customer/subscription_sync_default_address.php', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: '{}'
    })
      .then(function (res) { return res.json(); })
      .catch(function () { return null; });
  }

  /* Runs after Shopify has saved the address, so the endpoint reads the new
     default straight from the customer record. */
  function applyDefaultAddressToSubscriptions(overlay, closeOverlay) {
    setAddressMsg(overlay, SETTINGS.addressSyncingText, 'pending');
    return syncDefaultAddressToSubscriptions().then(function (data) {
      setAddressMsg(overlay, '');
      closeOverlay();
      if (!data || data.error) {
        setError((data && data.message) || SETTINGS.addressSyncErrorText);
      } else if (Array.isArray(data.failed) && data.failed.length) {
        setError(data.failed[0].message || SETTINGS.addressSyncErrorText);
      } else if (Array.isArray(data.updated) && data.updated.length) {
        setSuccess(SETTINGS.addressSyncedText);
      }
      return Promise.all([
        refreshAddressOverlay(overlay, closeOverlay),
        reloadContracts()
      ]);
    });
  }

  /* Fill the province select from the data-provinces list Shopify puts on each
     country option, so the modal needs no extra script from Shopify. Countries
     without provinces keep the select disabled, which leaves it out of the
     submitted form instead of clearing a province Shopify still has. */
  function syncProvinceField(countrySelect) {
    var form = countrySelect.form;
    var field = form && form.querySelector('[data-province-field]');
    var select = field && field.querySelector('select');
    if (!select) return;

    var option = countrySelect.options[countrySelect.selectedIndex];
    var provinces = [];
    try {
      provinces = JSON.parse((option && option.getAttribute('data-provinces')) || '[]');
    } catch (e) { /* keep the field hidden */ }

    if (!provinces.length) {
      select.innerHTML = '';
      select.disabled = true;
      field.hidden = true;
      return;
    }

    var wanted = select.getAttribute('data-default') || select.value || '';
    select.innerHTML = provinces.map(function (p) {
      return '<option value="' + escapeAttr(p[0]) + '">' + escapeHtml(p[1]) + '</option>';
    }).join('');
    if (wanted) select.value = wanted;
    select.disabled = false;
    field.hidden = false;
  }

  /* Wires everything inside the overlay. Called again after the overlay's
     content is replaced by refreshAddressOverlay. */
  function wireAddressContent(overlay, closeOverlay) {
    overlay.querySelectorAll('select[name="address[country]"]').forEach(function (sel) {
      if (sel.getAttribute('data-default')) sel.value = sel.getAttribute('data-default');
      syncProvinceField(sel);
      sel.addEventListener('change', function () {
        var province = sel.form && sel.form.querySelector('[data-province-field] select');
        if (province) province.removeAttribute('data-default');
        syncProvinceField(sel);
      });
    });
    overlay.querySelectorAll('[data-close-address]').forEach(function (el) {
      el.addEventListener('click', closeOverlay);
    });
    overlay.querySelectorAll('[data-addr-open]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        showAddressView(overlay, btn.getAttribute('data-addr-open'));
      });
    });
    overlay.querySelectorAll('[data-addr-back]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        showAddressView(overlay, 'list');
      });
    });

    /* "Use for my subscriptions" on a list row: tick the default checkbox on
       that address's own edit form and submit it, so the save goes through the
       same Shopify form (and the same success path) as a manual edit. */
    overlay.querySelectorAll('[data-addr-default]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var view = overlay.querySelector('[data-addr-view="edit-' + btn.getAttribute('data-addr-default') + '"]');
        var form = view && view.querySelector('form');
        if (!form) return;
        var box = form.querySelector('input[type="checkbox"][name="address[default]"]');
        if (box) box.checked = true;
        btn.disabled = true;
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      });
    });

    /* Submit the Shopify customer_address forms in the background so the
       customer stays on the portal instead of being redirected to the
       hosted customer-accounts profile page. */
    overlay.querySelectorAll('form').forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var btn = form.querySelector('[type="submit"]');
        if (btn) btn.disabled = true;
        /* Read the checkbox rather than the FormData entry: Shopify pairs it
           with a hidden "0" input under the same name. */
        var defaultBox = form.querySelector('input[type="checkbox"][name="address[default]"]');
        var becomesDefault = !!(defaultBox && defaultBox.checked);
        fetch(form.action, {
          method: 'POST',
          body: new FormData(form),
          credentials: 'same-origin',
          redirect: 'manual'
        }).then(function (res) {
          /* Shopify answers a successful save with a redirect (surfaced as
             an opaqueredirect under redirect:'manual'); a validation error
             re-renders the page at HTTP 200 instead. */
          if (res.type === 'opaqueredirect' || (res.status >= 300 && res.status < 400)) {
            if (becomesDefault) return applyDefaultAddressToSubscriptions(overlay, closeOverlay);
            closeOverlay();
            return refreshAddressOverlay(overlay, closeOverlay);
          }
          form.submit(); /* full-page submit so Shopify shows the errors */
        }).catch(function () {
          form.submit();
        }).then(function () {
          if (btn) btn.disabled = false;
        });
      });
    });
  }

  function wireAddressOverlay() {
    var overlay = document.getElementById('msv2-address-overlay');
    if (!overlay) return;

    function closeOverlay() {
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('msv2-no-scroll');
    }

    // Both the shipping card "Edit" link and the account tab's
    // "Manage addresses" row open the same overlay.
    root.querySelectorAll('[data-action="edit-address"]').forEach(function (open) {
      open.addEventListener('click', function () {
        showAddressView(overlay, 'list');
        overlay.classList.add('is-open');
        overlay.setAttribute('aria-hidden', 'false');
        document.body.classList.add('msv2-no-scroll');
      });
    });

    wireAddressContent(overlay, closeOverlay);
  }

  /* ══════════ UPDATE MY BOX (SHOP) MODAL ══════════ */
  var shopOverlay = document.getElementById('msv2-shop-overlay');
  var shopProducts = [];
  var shopLoaded = false;
  var shopLoading = false;
  var shopQuery = '';
  var shopActiveDietaries = [];
  var recoProducts = [];
  /* Theme-editor only: tracks variants "in the box" so qty steppers can be previewed. */
  var designBoxByVariant = {};
  /* True while an add/qty mutation is in flight — blocks overlapping clicks. */
  var shopMutating = false;

  function normalizeVariantId(id) {
    if (id == null || id === '') return '';
    var s = String(id);
    var digits = s.replace(/\D/g, '');
    return digits || s;
  }
  /* Find a recurring contract line for a storefront variant id (GID or numeric). */
  function findContractLineByVariant(variantId) {
    var nid = normalizeVariantId(variantId);
    if (!nid) return null;
    if (SETTINGS.designMode) {
      var demoQty = designBoxByVariant[nid];
      if (!demoQty) return null;
      return { line_id: 'demo-line-' + nid, quantity: demoQty, variant_id: nid };
    }
    var lines = Array.isArray(contract && contract.lines) ? contract.lines : [];
    for (var i = 0; i < lines.length; i++) {
      if (normalizeVariantId(lines[i].variant_id) === nid) return lines[i];
    }
    return null;
  }
  function refreshOpenShopCards() {
    if (!shopOverlay || !shopOverlay.classList.contains('is-open')) return;
    if (shopLoaded) renderShopResults();
    var recoHost = shopOverlay.querySelector('[data-reco-host]');
    if (recoHost && recoProducts.length) {
      recoHost.innerHTML = recoProducts.map(renderShopCardHtml).join('');
    }
  }
  /* Keep richer fields (product_url, images) from the previous lines when a
     mutation returns a thinner normalizeContract() payload. */
  function mergeContractLines(prev, next) {
    var prevById = {};
    (Array.isArray(prev) ? prev : []).forEach(function (l) {
      if (l && l.line_id) prevById[String(l.line_id)] = l;
    });
    return (Array.isArray(next) ? next : []).map(function (l) {
      if (!l || !l.line_id) return l;
      var old = prevById[String(l.line_id)];
      return old ? Object.assign({}, old, l) : l;
    });
  }
  function setShopMutating(on) {
    shopMutating = !!on;
    if (!shopOverlay) return;
    shopOverlay.classList.toggle('is-mutating', shopMutating);
  }
  /* Apply mutation lines for immediate qty UI, then refetch real pricing from
     the backend. Never invent subtotal/discount/total client-side. */
  function applyContractMutation(res) {
    var updated = res && res.data && res.data.contract;
    if (!updated || !contract) {
      setSummaryLoading();
      return reloadContracts();
    }
    if (Array.isArray(updated.lines)) {
      contract.lines = mergeContractLines(contract.lines, updated.lines);
    }
    if (updated.minimum_order) {
      contract.minimum_order = updated.minimum_order;
    } else if (updated.pricing_preview && contract.minimum_order) {
      refreshMinimumOrder(contract.minimum_order, Number(updated.pricing_preview.lines_subtotal) || 0);
    }
    renderNextOrder();
    refreshOpenShopCards();
    if (updated.pricing_preview) {
      contract.pricing_preview = updated.pricing_preview;
      renderSummary();
      root.setAttribute('data-loading', 'false');
      return Promise.resolve();
    }
    setSummaryLoading();
    return reloadContracts();
  }

  function removeLineFromContract(lineId) {
    if (!lineId) return;
    if (recurringLineCount() <= 1) {
      setError(SETTINGS.removeLastErrorText || 'Cannot remove the last item. Please cancel the subscription instead.');
      return;
    }
    var lines = Array.isArray(contract && contract.lines) ? contract.lines : [];
    var line = null;
    for (var i = 0; i < lines.length; i++) {
      if (String(lines[i].line_id) === String(lineId)) {
        line = lines[i];
        break;
      }
    }
    if (!line) return;
    openLineRemoveOverlay(line);
  }

  var SHOP_QTY_MINUS_SVG =
    '<svg viewBox="0 0 30 30" fill="none" aria-hidden="true"><circle cx="15" cy="15" r="14.4" fill="currentColor"/><line x1="9" y1="15" x2="21" y2="15" stroke="#fcb18c" stroke-width="1.5" stroke-linecap="round"/></svg>';
  var SHOP_QTY_PLUS_SVG =
    '<svg viewBox="0 0 30 30" fill="none" aria-hidden="true"><circle cx="15" cy="15" r="14.4" fill="currentColor"/><path d="M9 15h12M15 9v12" stroke="#fcb18c" stroke-width="1.5" stroke-linecap="round"/></svg>';
  function renderShopActionHtml(variantId, outOfStock, currency) {
    var line = findContractLineByVariant(variantId);
    if (line && Number(line.quantity) > 0) {
      var qty = Math.max(0, Number(line.quantity) || 0);
      return (
        '<div class="msv2-shop-qty" data-shop-qty ' +
          'data-line-id="' + escapeAttr(String(line.line_id || '')) + '" ' +
          'data-variant-id="' + escapeAttr(String(variantId || '')) + '">' +
          '<button type="button" class="msv2-shop-qty-btn" data-shop-qty-minus aria-label="Decrease quantity">' +
            SHOP_QTY_MINUS_SVG +
          '</button>' +
          '<span class="msv2-shop-qty-count" data-shop-qty-count>' + escapeHtml(String(qty)) + '</span>' +
          '<button type="button" class="msv2-shop-qty-btn" data-shop-qty-plus aria-label="Increase quantity">' +
            SHOP_QTY_PLUS_SVG +
          '</button>' +
        '</div>'
      );
    }
    return (
      '<button type="button" class="msv2-shop-add" data-shop-add ' +
        'data-variant-id="' + escapeAttr(String(variantId || '')) + '" ' +
        'data-currency="' + escapeAttr(currency || 'AUD') + '"' +
        (variantId && !outOfStock ? '' : ' disabled') + '>' +
        escapeHtml(SETTINGS.shopAddLabel) +
      '</button>'
    );
  }

  /* ── filtering ── */
  function productMatchesDietaries(product) {
    if (!shopActiveDietaries.length) return true;
    var tags = getProductTags(product);
    return shopActiveDietaries.every(function (filterTags) {
      return filterTags.some(function (ft) {
        return tags.some(function (t) { return t.indexOf(ft) !== -1; });
      });
    });
  }
  function productMatchesQuery(product) {
    if (!shopQuery) return true;
    var q = shopQuery;
    var title = (product.title || '').toLowerCase();
    var handle = (product.handle || '').toLowerCase();
    var vTitles = (Array.isArray(product.variants) ? product.variants : [])
      .map(function (v) { return (v.title || '').toLowerCase(); }).join(' ');
    return title.indexOf(q) !== -1 || handle.indexOf(q) !== -1 || vTitles.indexOf(q) !== -1;
  }
  function filteredShopProducts() {
    return shopProducts.filter(function (p) {
      return productMatchesDietaries(p) && productMatchesQuery(p);
    });
  }

  /* ── design mode demo data ── */
  function buildDemoShopProducts(catHandle) {
    var demo = [
      { title: 'Beef Parsnip & Bone Marrow Purée', price: 25, dietitian: false, variants: 2 },
      { title: 'Roasted Chicken & Thyme', price: 25, dietitian: false, variants: 2 },
      { title: 'Green Pea & Mint Blend', price: 20, dietitian: false, variants: 0 },
      { title: 'Chicken & Veggie Risotto', price: 28, dietitian: true, variants: 2 }
    ];
    return demo.map(function (d, idx) {
      var variants = [];
      for (var i = 0; i < d.variants; i++) {
        variants.push({ id: 'demo-' + catHandle + '-' + idx + '-' + i, title: i === 0 ? '4 pack' : '8 pack', price: d.price * (i + 1), inventory_quantity: 10 });
      }
      return {
        id: 'demo-' + catHandle + '-' + idx,
        title: d.title,
        price: d.price,
        currency: 'AUD',
        image_url: '',
        product_url: '#',
        tags: d.dietitian ? [SETTINGS.dietitianTag] : [],
        variants: variants,
        _cat: catHandle
      };
    });
  }

  /* ── product card ── */
  function renderShopCardHtml(product) {
    var variants = Array.isArray(product.variants) ? product.variants : [];
    var hasVariants = variants.length > 0;
    var firstAvailable = variants.filter(function (v) { return !isVariantOutOfStock(v); })[0] || variants[0] || null;
    var variantId = firstAvailable ? firstAvailable.id : '';
    var currency = (product.currency || '').trim() || 'AUD';
    var price = firstAvailable && Number.isFinite(Number(firstAvailable.price)) ? Number(firstAvailable.price) : Number(product.price);
    var priceText = Number.isFinite(price) ? formatMoney(price, currency) : (product.price_formatted || '');
    var outOfStock = firstAvailable ? isVariantOutOfStock(firstAvailable) : isProductOutOfStock(product);
    var tags = getProductTags(product);
    var isDietitian = !!SETTINGS.dietitianTag && tags.indexOf(SETTINGS.dietitianTag) !== -1;

    var img = product.image_url
      ? '<img src="' + escapeAttr(product.image_url) + '" alt="' + escapeAttr(product.title || '') + '" loading="lazy">'
      : NO_IMAGE_SVG;
    var badge = isDietitian
      ? '<span class="msv2-shop-badge"><svg viewBox="0 0 12 12" fill="none" aria-hidden="true"><circle cx="6" cy="6" r="5.5" stroke="currentColor"/><path d="M3.8 6l1.6 1.6L8.5 4.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg><span>' + escapeHtml(SETTINGS.dietitianLabel) + '</span></span>'
      : '';
    var oosOverlay = outOfStock ? '<span class="msv2-shop-oos">' + escapeHtml(SETTINGS.shopOosLabel) + '</span>' : '';

    var selectHtml = '';
    if (hasVariants && (variants.length > 1 || (variants[0] && variants[0].title && variants[0].title !== 'Default Title'))) {
      var options = variants.map(function (v, idx) {
        var vTitle = v.title || 'Variant ' + (idx + 1);
        if (vTitle === 'Default Title') vTitle = product.title || vTitle;
        var oos = isVariantOutOfStock(v);
        return '<option value="' + escapeAttr(String(v.id)) + '" data-price="' + escapeAttr(String(v.price == null ? '' : v.price)) + '"' +
          (oos ? ' disabled' : '') + (String(v.id) === String(variantId) ? ' selected' : '') + '>' +
          escapeHtml(vTitle) + (oos ? ' (' + escapeHtml(SETTINGS.shopOosLabel) + ')' : '') + '</option>';
      }).join('');
      selectHtml = '<select class="msv2-shop-select" data-shop-variant aria-label="Variant">' + options + '</select>';
    }

    return (
      '<div class="msv2-shop-card" data-shop-product="' + escapeAttr(String(product.id || '')) + '">' +
        '<a class="msv2-shop-card-img' + (outOfStock ? ' is-oos' : '') + '" href="' + escapeAttr(product.product_url || '#') + '" target="_blank" tabindex="-1">' +
          img + badge + oosOverlay +
        '</a>' +
        '<div class="msv2-shop-card-body">' +
          '<p class="msv2-shop-card-title">' + escapeHtml(product.title || '') + '</p>' +
          '<p class="msv2-shop-card-price" data-shop-price>' + escapeHtml(priceText) + '</p>' +
          selectHtml +
          '<div class="msv2-shop-action" data-shop-action data-currency="' + escapeAttr(currency) + '">' +
            renderShopActionHtml(variantId, outOfStock, currency) +
          '</div>' +
        '</div>' +
      '</div>'
    );
  }

  /* ── rendering ── */
  function renderShopCount() {
    var el = shopOverlay && shopOverlay.querySelector('[data-shop-count]');
    if (!el) return;
    el.textContent = shopLoaded ? filteredShopProducts().length + ' ' + SETTINGS.shopItemsLabel : '';
  }
  function renderShopResults() {
    var host = shopOverlay && shopOverlay.querySelector('[data-shop-results]');
    if (!host) return;
    var filtered = filteredShopProducts();
    renderShopCount();
    if (!filtered.length) {
      host.innerHTML = '<p class="msv2-shop-empty">' + escapeHtml(SETTINGS.shopEmptyText) + '</p>';
      return;
    }
    var html = '';
    SHOP_CATEGORIES.forEach(function (cat) {
      var items = filtered.filter(function (p) { return p._cat === cat.handle; });
      if (!items.length) return;
      html +=
        '<section class="msv2-shop-section" data-shop-section="' + escapeAttr(String(cat.handle || '')) + '">' +
          '<h3 class="msv2-shop-section-title">' + escapeHtml(cat.name || '') + '</h3>' +
          (cat.subtitle ? '<p class="msv2-shop-section-sub">' + escapeHtml(cat.subtitle) + '</p>' : '') +
          '<div class="msv2-shop-grid">' + items.map(renderShopCardHtml).join('') + '</div>' +
        '</section>';
    });
    host.innerHTML = html || '<p class="msv2-shop-empty">' + escapeHtml(SETTINGS.shopEmptyText) + '</p>';
  }

  function buildAllDemoShopProducts() {
    return SHOP_CATEGORIES.reduce(function (acc, cat) {
      return acc.concat(buildDemoShopProducts(cat.handle));
    }, []);
  }
  function loadShopProducts() {
    if (shopLoaded || shopLoading || !shopOverlay) return;
    var host = shopOverlay.querySelector('[data-shop-results]');
    if (!host) return;
    shopLoading = true;

    host.innerHTML = '<p class="msv2-shop-loading">' + escapeHtml(SETTINGS.shopLoadingText) + '</p>';
    Promise.all(SHOP_CATEGORIES.map(function (cat) {
      return fetchCollectionProducts(cat.handle).then(function (products) {
        return products.map(function (p) {
          var copy = Object.assign({}, p);
          copy._cat = cat.handle;
          return copy;
        });
      });
    }))
      .then(function (results) {
        shopProducts = results.reduce(function (acc, list) { return acc.concat(list); }, []);
        // In the theme editor, fall back to demo data when the app proxy
        // returns nothing, so the modal can still be styled.
        if (!shopProducts.length && SETTINGS.designMode) {
          shopProducts = buildAllDemoShopProducts();
        }
        shopLoaded = true;
        renderShopResults();
      })
      .catch(function () {
        if (SETTINGS.designMode) {
          shopProducts = buildAllDemoShopProducts();
          shopLoaded = true;
          renderShopResults();
          return;
        }
        host.innerHTML = '<p class="msv2-shop-empty">' + escapeHtml(SETTINGS.shopErrorText) + '</p>';
      })
      .finally(function () { shopLoading = false; });
  }

  /* ── recommendation strip ── */
  function loadRecoStrip() {
    var reco = shopOverlay && shopOverlay.querySelector('[data-shop-reco]');
    if (!reco) return;
    var host = reco.querySelector('[data-reco-host]');
    if (!host) return;

    if (!SETTINGS.mealsCollection) {
      if (SETTINGS.designMode) {
        recoProducts = buildDemoShopProducts('reco');
        host.innerHTML = recoProducts.map(renderShopCardHtml).join('');
        reco.style.display = '';
      }
      return;
    }
    fetchCollectionProducts(SETTINGS.mealsCollection).then(function (products) {
      if (!products.length) {
        if (SETTINGS.designMode) {
          recoProducts = buildDemoShopProducts('reco');
          host.innerHTML = recoProducts.map(renderShopCardHtml).join('');
          reco.style.display = '';
        }
        return;
      }
      recoProducts = products;
      host.innerHTML = recoProducts.map(renderShopCardHtml).join('');
      reco.style.display = '';
    });
  }

  /* ── variant change ── */
  function handleShopVariantChange(select) {
    var card = select.closest('.msv2-shop-card');
    if (!card) return;
    var option = select.options[select.selectedIndex];
    var action = card.querySelector('[data-shop-action]');
    var priceEl = card.querySelector('[data-shop-price]');
    var imgWrap = card.querySelector('.msv2-shop-card-img');
    var oos = !!(option && option.disabled);
    var currency = (action && action.getAttribute('data-currency')) || 'AUD';
    if (action) {
      action.innerHTML = renderShopActionHtml(select.value || '', oos, currency);
    }
    if (priceEl && option) {
      var raw = Number(option.getAttribute('data-price'));
      if (Number.isFinite(raw)) {
        priceEl.textContent = formatMoney(raw, currency);
      }
    }
    if (imgWrap) {
      imgWrap.classList.toggle('is-oos', oos);
      var overlay = imgWrap.querySelector('.msv2-shop-oos');
      if (oos && !overlay) {
        imgWrap.insertAdjacentHTML('beforeend', '<span class="msv2-shop-oos">' + escapeHtml(SETTINGS.shopOosLabel) + '</span>');
      } else if (!oos && overlay) {
        overlay.remove();
      }
    }
  }

  /* ── add to box ── */
  function addShopItem(btn) {
    var variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;
    if (SETTINGS.designMode) {
      var nid = normalizeVariantId(variantId);
      designBoxByVariant[nid] = (designBoxByVariant[nid] || 0) + 1;
      refreshOpenShopCards();
      return;
    }
    if (!contract || !contract.id || shopMutating) return;
    setShopMutating(true);
    btn.disabled = true;
    btn.classList.add('is-loading');
    fetch('/apps/customer/subscription_add_item.php', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        subscriptionContractId: contract.id,
        variantId: 'gid://shopify/ProductVariant/' + String(variantId).replace(/\D/g, ''),
        quantity: 1
      })
    })
      .then(function (r) { return r.json().catch(function () { return {}; }).then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        if (!res.ok || res.data.error) {
          setError(res.data.message || SETTINGS.genericErrorText);
          restoreFromContract();
          return;
        }
        setSuccess(SETTINGS.itemAddedText);
        return applyContractMutation(res);
      })
      .catch(function () {
        setError(SETTINGS.genericErrorText);
        restoreFromContract();
      })
      .finally(function () {
        setShopMutating(false);
      });
  }

  /* ── qty stepper for variants already on the contract ── */
  function updateShopQuantity(qtyEl, delta) {
    if (!qtyEl || shopMutating) return;
    var lineId = qtyEl.getAttribute('data-line-id');
    var variantId = qtyEl.getAttribute('data-variant-id');
    var countEl = qtyEl.querySelector('[data-shop-qty-count]');
    var currentQty = Number(countEl && countEl.textContent) || 0;
    var newQty = currentQty + Number(delta);

    if (SETTINGS.designMode) {
      var nid = normalizeVariantId(variantId);
      if (newQty <= 0) {
        var designCount = Object.keys(designBoxByVariant).length;
        if (designCount <= 1 && designBoxByVariant[nid]) {
          setError(SETTINGS.removeLastErrorText || 'Cannot remove the last item. Please cancel the subscription instead.');
          return;
        }
        delete designBoxByVariant[nid];
      } else {
        designBoxByVariant[nid] = newQty;
      }
      refreshOpenShopCards();
      return;
    }
    if (!contract || !contract.id || !lineId) return;

    if (newQty <= 0 && recurringLineCount() <= 1) {
      setError(SETTINGS.removeLastErrorText || 'Cannot remove the last item. Please cancel the subscription instead.');
      return;
    }

    setShopMutating(true);

    var req;
    if (newQty <= 0) {
      req = fetch('/apps/customer/subscription_remove_item.php', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subscriptionContractId: contract.id,
          lineId: lineId
        })
      });
    } else {
      req = fetch('/apps/customer/subscription_update_quantity.php', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subscriptionContractId: contract.id,
          lineId: lineId,
          quantity: newQty
        })
      });
    }

    req
      .then(function (r) { return r.json().catch(function () { return {}; }).then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        if (!res.ok || res.data.error) {
          setError(res.data.message || SETTINGS.genericErrorText);
          restoreFromContract();
          return;
        }
        return applyContractMutation(res);
      })
      .catch(function () {
        setError(SETTINGS.genericErrorText);
        restoreFromContract();
      })
      .finally(function () {
        setShopMutating(false);
      });
  }

  /* ── open / close ── */
  function openShopOverlay() {
    if (!shopOverlay) return;
    shopOverlay.classList.add('is-open');
    shopOverlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('msv2-no-scroll');
    if (SHOP_CATEGORIES.length && !shopOverlay.querySelector('.msv2-shop-pill.is-active')) {
      setActiveShopTab(SHOP_CATEGORIES[0].handle);
    }
    loadShopProducts();
    loadRecoStrip();
    setTimeout(function () {
      var input = shopOverlay.querySelector('[data-shop-search]');
      if (input && window.matchMedia('(min-width: 640px)').matches) input.focus();
    }, 0);
  }
  function closeShopOverlay() {
    if (!shopOverlay) return;
    shopOverlay.classList.remove('is-open');
    shopOverlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('msv2-no-scroll');
  }

  /* ── category tabs (scroll-to + scroll spy) ── */
  // A pill can stand for several categories (the "Purées" collections share
  // one); data-shop-tab-group then lists every handle it covers, first first.
  function shopTabHandles(pill) {
    var group = String(pill.getAttribute('data-shop-tab-group') || '')
      .split(',')
      .map(function (h) { return h.trim(); })
      .filter(Boolean);
    return group.length ? group : [pill.getAttribute('data-shop-tab') || ''];
  }
  function shopSectionEl(handle) {
    if (!handle) return null;
    return shopOverlay.querySelector('[data-shop-section="' + handle.replace(/"/g, '\\"') + '"]');
  }
  function setActiveShopTab(handle) {
    if (!shopOverlay) return;
    shopOverlay.querySelectorAll('[data-shop-tab]').forEach(function (pill) {
      pill.classList.toggle('is-active', shopTabHandles(pill).indexOf(handle) !== -1);
    });
  }
  function wireShopScrollSpy() {
    var scroller = shopOverlay && shopOverlay.querySelector('[data-shop-scroll]');
    if (!scroller) return;
    var ticking = false;
    scroller.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        var sections = scroller.querySelectorAll('[data-shop-section]');
        if (!sections.length) return;
        var top = scroller.getBoundingClientRect().top;
        var current = sections[0];
        sections.forEach(function (sec) {
          if (sec.getBoundingClientRect().top - top <= 90) current = sec;
        });
        setActiveShopTab(current.getAttribute('data-shop-section'));
      });
    });
  }

  /* ── category tabs drag-to-scroll (desktop) ── */
  function wireShopTabsDrag() {
    var tabs = shopOverlay && shopOverlay.querySelector('[data-shop-tabs]');
    if (!tabs) return;
    var isDown = false;
    var dragged = false;
    var startX = 0;
    var startScroll = 0;

    tabs.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse') return; // touch already scrolls natively
      isDown = true;
      dragged = false;
      startX = e.clientX;
      startScroll = tabs.scrollLeft;
    });
    window.addEventListener('pointermove', function (e) {
      if (!isDown) return;
      var dx = e.clientX - startX;
      if (!dragged && Math.abs(dx) > 4) {
        dragged = true;
        tabs.classList.add('is-dragging');
      }
      if (dragged) tabs.scrollLeft = startScroll - dx;
    });
    window.addEventListener('pointerup', function () {
      if (!isDown) return;
      isDown = false;
      tabs.classList.remove('is-dragging');
    });
    // A release at the end of a drag also fires a click on the pill under
    // the cursor; swallow it so dragging never switches tabs.
    tabs.addEventListener('click', function (e) {
      if (dragged) {
        dragged = false;
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);
  }

  /* ── event wiring ── */
  function wireShopOverlay() {
    if (!shopOverlay) return;

    shopOverlay.addEventListener('click', function (e) {
      if (e.target.closest('[data-shop-close]')) { closeShopOverlay(); return; }

      var dismiss = e.target.closest('[data-reco-dismiss]');
      if (dismiss) {
        var reco = shopOverlay.querySelector('[data-shop-reco]');
        if (reco) reco.style.display = 'none';
        return;
      }

      var recoArrow = e.target.closest('[data-reco-prev], [data-reco-next]');
      if (recoArrow) {
        var recoRow = shopOverlay.querySelector('[data-reco-host]');
        if (recoRow) {
          var dir = recoArrow.hasAttribute('data-reco-prev') ? -1 : 1;
          var recoCard = recoRow.firstElementChild;
          var step = recoCard ? recoCard.getBoundingClientRect().width + 11.25 : 200;
          recoRow.scrollBy({ left: dir * step, behavior: 'smooth' });
        }
        return;
      }

      var tab = e.target.closest('[data-shop-tab]');
      if (tab) {
        var handles = shopTabHandles(tab);
        setActiveShopTab(handles[0]);
        // Grouped pills land on their first category that actually has
        // products, so an empty leading collection isn't a dead tab.
        var section = null;
        for (var hi = 0; hi < handles.length && !section; hi++) section = shopSectionEl(handles[hi]);
        if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }

      var toggle = e.target.closest('[data-shop-filter-toggle]');
      if (toggle) {
        var filters = shopOverlay.querySelector('[data-shop-filters]');
        var open = filters && filters.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        return;
      }

      var clear = e.target.closest('[data-shop-clear]');
      if (clear) {
        shopOverlay.querySelectorAll('[data-shop-dietary]').forEach(function (cb) { cb.checked = false; });
        shopActiveDietaries = [];
        clear.style.display = 'none';
        renderShopResults();
        return;
      }

      var addBtn = e.target.closest('[data-shop-add]');
      if (addBtn) { addShopItem(addBtn); return; }

      var qtyMinus = e.target.closest('[data-shop-qty-minus]');
      if (qtyMinus) {
        var minusWrap = qtyMinus.closest('[data-shop-qty]');
        if (minusWrap) updateShopQuantity(minusWrap, -1);
        return;
      }
      var qtyPlus = e.target.closest('[data-shop-qty-plus]');
      if (qtyPlus) {
        var plusWrap = qtyPlus.closest('[data-shop-qty]');
        if (plusWrap) updateShopQuantity(plusWrap, 1);
        return;
      }
    });

    shopOverlay.addEventListener('change', function (e) {
      if (e.target.matches('[data-shop-variant]')) {
        handleShopVariantChange(e.target);
        return;
      }
      if (e.target.matches('[data-shop-dietary]')) {
        shopActiveDietaries = Array.prototype.map.call(
          shopOverlay.querySelectorAll('[data-shop-dietary]:checked'),
          function (cb) {
            return String(cb.getAttribute('data-tags') || '')
              .split(',')
              .map(function (t) { return t.toLowerCase().trim(); })
              .filter(Boolean);
          }
        ).filter(function (tags) { return tags.length; });
        var clearBtn = shopOverlay.querySelector('[data-shop-clear]');
        if (clearBtn) clearBtn.style.display = shopActiveDietaries.length ? '' : 'none';
        renderShopResults();
      }
    });

    var searchTimer = null;
    var searchInput = shopOverlay.querySelector('[data-shop-search]');
    if (searchInput) {
      searchInput.addEventListener('input', function () {
        clearTimeout(searchTimer);
        searchTimer = setTimeout(function () {
          shopQuery = String(searchInput.value || '').trim().toLowerCase();
          renderShopResults();
        }, 120);
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && shopOverlay.classList.contains('is-open')) closeShopOverlay();
    });

    wireShopScrollSpy();
    wireShopTabsDrag();
  }

  /* ══════════ DELIVERY SCHEDULE MODAL (skip / unskip cycles) ══════════ */
  var reschedOverlay = document.getElementById('msv2-resched-overlay');
  var unskipOverlay = document.getElementById('msv2-unskip-overlay');
  var reschedViewYear = 0;
  var reschedViewMonth = 0;      // 0-based
  var reschedSaving = false;
  // Upcoming cycles, keyed by the delivery day the customer sees (the billing
  // day plus the contract's delivery offset). The calendar never shows the
  // billing day — the backend resolves the offset and sends delivery_date.
  var reschedCycles = [];
  var reschedCycleByDay = {};
  var reschedSelected = {};      // cycle_index -> true, skips not yet submitted
  var reschedCyclesLoading = false;
  var reschedCyclesLoadedFor = null;
  var unskipCycle = null;
  var unskipSaving = false;
  // The backend returns a long horizon of cycles, but customers only manage the
  // nearest few — anything further out stays untouched server-side.
  var RESCHED_VISIBLE_CYCLES = 3;

  function reschedToday() {
    var d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }
  function reschedDateKey(d) {
    if (!d || isNaN(d.getTime())) return '';
    var y = d.getFullYear();
    var m = String(d.getMonth() + 1).padStart(2, '0');
    var day = String(d.getDate()).padStart(2, '0');
    return y + '-' + m + '-' + day;
  }
  /** Build a local Date from a shop-calendar YYYY-MM-DD (no TZ conversion). */
  function reschedYmdToDate(ymd) {
    var m = String(ymd || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return null;
    var d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    d.setHours(0, 0, 0, 0);
    return isNaN(d.getTime()) ? null : d;
  }
  function reschedFormatLong(d) {
    if (!d || isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    });
  }
  function reschedFillTemplate(tmpl, vars) {
    return String(tmpl || '').replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : match;
    });
  }
  function reschedSelectedIndexes() {
    return Object.keys(reschedSelected)
      .filter(function (key) { return reschedSelected[key]; })
      .map(Number)
      .sort(function (a, b) { return a - b; });
  }
  function reschedCycleByIndex(index) {
    return reschedCycles.find(function (c) { return c.index === index; }) || null;
  }
  function reschedDayLabel(cycle) {
    var date = reschedFormatLong(cycle.day);
    if (cycle.skipped) {
      return reschedFillTemplate(SETTINGS.reschedDaySkipped || '{{ date }} — skipped delivery', { date: date });
    }
    if (!cycle.editable) {
      return reschedFillTemplate(SETTINGS.reschedDayLocked || '{{ date }} — delivery being prepared', { date: date });
    }
    return reschedFillTemplate(SETTINGS.reschedDayScheduled || '{{ date }} — scheduled delivery', { date: date });
  }
  /** Demo cycles so the theme editor can preview every day state. */
  function reschedDemoCycles() {
    var base = reschedToday();
    return [0, 1, 2, 3].map(function (i) {
      var d = new Date(base.getTime());
      d.setDate(d.getDate() + 2 + i * 28);
      return {
        cycle_index: i + 1,
        delivery_date: reschedDateKey(d),
        skipped: i === 1,
        editable: i !== 0,
        lock_reason: i === 0 ? 'cycle_ended' : null
      };
    });
  }

  function applyReschedCycles(cycles) {
    reschedCycles = [];
    reschedCycleByDay = {};
    (Array.isArray(cycles) ? cycles : []).forEach(function (c) {
      var index = Number(c && c.cycle_index) || 0;
      var day = reschedYmdToDate(c && (c.delivery_date || c.date));
      if (!index || !day) return;
      reschedCycles.push({
        index: index,
        day: day,
        key: reschedDateKey(day),
        skipped: !!c.skipped,
        editable: c.editable !== false,
        lockReason: c.lock_reason || null
      });
    });
    reschedCycles.sort(function (a, b) { return a.day - b.day; });
    reschedCycles = reschedCycles.slice(0, RESCHED_VISIBLE_CYCLES);
    reschedCycles.forEach(function (cycle) {
      reschedCycleByDay[cycle.key] = cycle;
    });
    // Drop pending selections whose cycle is gone, already skipped or locked.
    var stillSelectable = {};
    reschedCycles.forEach(function (c) {
      if (reschedSelected[c.index] && !c.skipped && c.editable) stillSelectable[c.index] = true;
    });
    reschedSelected = stillSelectable;
  }

  /** Earliest known delivery day, used to open the calendar on a useful month. */
  function reschedFirstCycleDay() {
    var earliest = null;
    reschedCycles.forEach(function (c) {
      if (!earliest || c.day < earliest) earliest = c.day;
    });
    if (earliest) return earliest;
    var nd = contract && contract.next_delivery;
    return reschedYmdToDate(nd && nd.date);
  }

  /** Latest shown delivery day, so the calendar can't page past what's editable. */
  function reschedLastCycleDay() {
    var latest = null;
    reschedCycles.forEach(function (c) {
      if (!latest || c.day > latest) latest = c.day;
    });
    return latest;
  }

  function loadReschedCycles() {
    if (SETTINGS.designMode) {
      applyReschedCycles(reschedDemoCycles());
      renderReschedCalendar();
      updateReschedUI();
      return Promise.resolve();
    }
    if (!contract || !contract.id || reschedCyclesLoading) return Promise.resolve();

    reschedCyclesLoading = true;
    var forContract = String(contract.id);
    return fetch('/apps/customer/subscription_billing_cycles.php', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subscriptionContractId: contract.id })
    })
      .then(function (r) { return r.json(); })
      .then(function (json) {
        if (!json || !json.ok) throw new Error((json && json.message) || 'Failed to load cycles');
        reschedCyclesLoadedFor = forContract;
        applyReschedCycles(json.cycles);
      })
      .catch(function () {
        reschedCyclesLoadedFor = null;
        applyReschedCycles([]);
        setReschedMsg(SETTINGS.rescheduleLoadErrorText || 'We couldn\'t load your delivery schedule.');
      })
      .then(function () {
        reschedCyclesLoading = false;
        renderReschedCalendar();
        updateReschedUI();
      });
  }

  function renderReschedCalendar() {
    if (!reschedOverlay) return;
    var host = reschedOverlay.querySelector('[data-resched-calendar]');
    var monthEl = reschedOverlay.querySelector('[data-resched-month]');
    if (!host) return;

    var first = new Date(reschedViewYear, reschedViewMonth, 1);
    if (monthEl) {
      monthEl.textContent = first.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    }

    var today = reschedToday();
    var daysInMonth = new Date(reschedViewYear, reschedViewMonth + 1, 0).getDate();
    var offset = first.getDay(); // 0 = Sunday, matching the Sun-first header

    var html = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(function (w) {
      return '<span class="msv2-resched-dow">' + w + '</span>';
    }).join('');
    for (var b = 0; b < offset; b++) html += '<span class="msv2-resched-day-empty"></span>';
    for (var day = 1; day <= daysInMonth; day++) {
      var date = new Date(reschedViewYear, reschedViewMonth, day);
      var cycle = reschedCycleByDay[reschedDateKey(date)];
      // Only delivery days are actionable; the rest are plain calendar filler.
      if (!cycle) {
        var plainCls = 'msv2-resched-day msv2-resched-day--plain' +
          (date.getTime() < today.getTime() ? ' is-past' : '');
        html += '<span class="' + plainCls + '">' + day + '</span>';
        continue;
      }
      var selected = !!reschedSelected[cycle.index];
      var cls = 'msv2-resched-day ' + (cycle.skipped ? 'is-skipped' : 'is-scheduled');
      if (!cycle.editable) cls += ' is-locked';
      if (selected) cls += ' is-selected';
      html += '<button type="button" class="' + cls + '"' +
        ' data-resched-cycle="' + cycle.index + '"' +
        ' aria-pressed="' + (selected ? 'true' : 'false') + '"' +
        ' aria-label="' + escapeAttr(reschedDayLabel(cycle)) + '"' +
        (cycle.editable ? '' : ' disabled') + '>' + day + '</button>';
    }
    host.innerHTML = html;

    // The "already being prepared" legend only makes sense when one is shown.
    var lockedNote = reschedOverlay.querySelector('[data-resched-locked-note]');
    if (lockedNote) {
      lockedNote.hidden = !reschedCycles.some(function (c) { return !c.editable; });
    }

    // Never navigate into fully-past months, or past the last delivery shown.
    var prevBtn = reschedOverlay.querySelector('[data-resched-prev]');
    if (prevBtn) {
      prevBtn.disabled = reschedViewYear === today.getFullYear() && reschedViewMonth === today.getMonth();
    }
    var nextBtn = reschedOverlay.querySelector('[data-resched-next]');
    if (nextBtn) {
      var last = reschedLastCycleDay();
      nextBtn.disabled = !!last &&
        (reschedViewYear > last.getFullYear() ||
          (reschedViewYear === last.getFullYear() && reschedViewMonth >= last.getMonth()));
    }
  }

  function setReschedMsg(msg) {
    var el = reschedOverlay && reschedOverlay.querySelector('[data-resched-msg]');
    if (!el) return;
    el.hidden = !msg;
    el.textContent = msg || '';
  }

  function reschedSkipSuccessText(count) {
    if (count === 1) return SETTINGS.rescheduleSuccessOne || '1 delivery has been skipped.';
    return reschedFillTemplate(
      SETTINGS.rescheduleSuccessOther || '{{ count }} deliveries have been skipped.',
      { count: count }
    );
  }

  function updateReschedUI() {
    if (!reschedOverlay) return;
    var count = reschedSelectedIndexes().length;

    var confirmBtn = reschedOverlay.querySelector('[data-resched-confirm]');
    if (confirmBtn) {
      confirmBtn.disabled = reschedSaving || count === 0;
      confirmBtn.classList.toggle('is-loading', reschedSaving);
      if (reschedSaving) {
        confirmBtn.textContent = SETTINGS.rescheduleSavingText || 'Updating your schedule…';
      } else if (count === 1) {
        confirmBtn.textContent = SETTINGS.rescheduleConfirmCountOne || 'Skip 1 delivery';
      } else if (count > 1) {
        confirmBtn.textContent = reschedFillTemplate(
          SETTINGS.rescheduleConfirmCountOther || 'Skip {{ count }} deliveries',
          { count: count }
        );
      } else {
        confirmBtn.textContent = SETTINGS.rescheduleConfirmBtn || 'Skip selected deliveries';
      }
    }

    var secondaryBtn = reschedOverlay.querySelector('[data-resched-secondary]');
    if (secondaryBtn) {
      secondaryBtn.disabled = reschedSaving;
      secondaryBtn.textContent = SETTINGS.rescheduleCancel || 'Cancel';
    }
  }

  function openReschedOverlay() {
    if (!reschedOverlay) return;
    reschedSelected = {};
    reschedSaving = false;
    setReschedMsg('');
    // Cached cycles keep the calendar painted while the refresh is in flight,
    // but only when they belong to the contract being shown.
    if (reschedCyclesLoadedFor !== String(contract && contract.id)) applyReschedCycles([]);
    var view = reschedFirstCycleDay() || reschedToday();
    reschedViewYear = view.getFullYear();
    reschedViewMonth = view.getMonth();
    renderReschedCalendar();
    updateReschedUI();
    reschedOverlay.classList.add('is-open');
    reschedOverlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('msv2-no-scroll');
    loadReschedCycles();
  }
  function closeReschedOverlay() {
    if (!reschedOverlay || reschedSaving) return;
    reschedOverlay.classList.remove('is-open');
    reschedOverlay.setAttribute('aria-hidden', 'true');
    if (!unskipOverlay || !unskipOverlay.classList.contains('is-open')) {
      document.body.classList.remove('msv2-no-scroll');
    }
    reschedSelected = {};
    setReschedMsg('');
  }

  /** Skip or unskip cycles by index; resolves with the refreshed cycle list. */
  function postReschedCycleAction(action, indexes) {
    return fetch('/apps/customer/subscription_skip_cycles.php', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        subscriptionContractId: contract.id,
        action: action,
        cycleIndexes: indexes
      })
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (d) {
        if (!r.ok || !d || !d.ok) {
          // Network failures must not leak a browser message into the modal,
          // so the server's wording travels on its own property.
          var err = new Error('cycle action failed');
          err.serverMessage = d && d.message;
          throw err;
        }
        return d;
      });
    });
  }

  function submitReschedSkips() {
    var indexes = reschedSelectedIndexes();
    if (!indexes.length || reschedSaving) return;

    if (SETTINGS.designMode) {
      closeReschedOverlay();
      setSuccess(reschedSkipSuccessText(indexes.length));
      return;
    }
    if (!contract || !contract.id) return;

    reschedSaving = true;
    setReschedMsg('');
    updateReschedUI();
    postReschedCycleAction('skip', indexes)
      .then(function (data) {
        reschedSaving = false;
        applyReschedCycles(data.cycles);
        renderReschedCalendar();
        updateReschedUI();
        closeReschedOverlay();
        setSuccess(reschedSkipSuccessText(indexes.length));
        return reloadContracts();
      })
      .catch(function (err) {
        reschedSaving = false;
        updateReschedUI();
        setReschedMsg(
          (err && err.serverMessage) ||
          SETTINGS.rescheduleErrorText ||
          'We couldn\'t update your schedule. Please try again.'
        );
      });
  }

  /* ── Unskip confirmation ── */
  function setUnskipMsg(msg) {
    var el = unskipOverlay && unskipOverlay.querySelector('[data-unskip-msg]');
    if (!el) return;
    el.hidden = !msg;
    el.textContent = msg || '';
  }

  function updateUnskipUI() {
    if (!unskipOverlay) return;
    var confirmBtn = unskipOverlay.querySelector('[data-unskip-confirm]');
    if (confirmBtn) {
      confirmBtn.disabled = unskipSaving;
      confirmBtn.classList.toggle('is-loading', unskipSaving);
      confirmBtn.textContent = unskipSaving
        ? (SETTINGS.unskipSavingText || 'Unskipping…')
        : (SETTINGS.unskipConfirmBtn || 'Yes, unskip it');
    }
    unskipOverlay.querySelectorAll('[data-unskip-close]').forEach(function (b) {
      b.disabled = unskipSaving;
    });
  }

  function openUnskipOverlay(cycle) {
    if (!unskipOverlay || !cycle) return;
    unskipCycle = cycle;
    unskipSaving = false;
    setUnskipMsg('');
    var textEl = unskipOverlay.querySelector('[data-unskip-text]');
    if (textEl) {
      textEl.textContent = reschedFillTemplate(
        SETTINGS.unskipText || 'Your delivery on {{ date }} will be added back to your schedule.',
        { date: reschedFormatLong(cycle.day) }
      );
    }
    updateUnskipUI();
    unskipOverlay.classList.add('is-open');
    unskipOverlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('msv2-no-scroll');
  }

  function closeUnskipOverlay() {
    if (!unskipOverlay || unskipSaving) return;
    unskipOverlay.classList.remove('is-open');
    unskipOverlay.setAttribute('aria-hidden', 'true');
    unskipCycle = null;
    setUnskipMsg('');
    if (!reschedOverlay || !reschedOverlay.classList.contains('is-open')) {
      document.body.classList.remove('msv2-no-scroll');
    }
  }

  function submitUnskip() {
    var cycle = unskipCycle;
    if (!cycle || unskipSaving) return;

    if (SETTINGS.designMode) {
      cycle.skipped = false;
      closeUnskipOverlay();
      renderReschedCalendar();
      setSuccess(SETTINGS.unskipSuccessText || 'That delivery is back on your schedule.');
      return;
    }
    if (!contract || !contract.id) return;

    unskipSaving = true;
    setUnskipMsg('');
    updateUnskipUI();
    postReschedCycleAction('unskip', [cycle.index])
      .then(function (data) {
        unskipSaving = false;
        applyReschedCycles(data.cycles);
        renderReschedCalendar();
        updateReschedUI();
        updateUnskipUI();
        closeUnskipOverlay();
        setSuccess(SETTINGS.unskipSuccessText || 'That delivery is back on your schedule.');
        return reloadContracts();
      })
      .catch(function (err) {
        unskipSaving = false;
        updateUnskipUI();
        setUnskipMsg(
          (err && err.serverMessage) ||
          SETTINGS.unskipErrorText ||
          'We couldn\'t unskip that delivery. Please try again.'
        );
      });
  }

  function wireReschedOverlay() {
    if (reschedOverlay) {
      reschedOverlay.addEventListener('click', function (e) {
        if (e.target.closest('[data-resched-close]') || e.target.closest('[data-resched-secondary]')) {
          closeReschedOverlay();
          return;
        }

        var nav = e.target.closest('[data-resched-prev], [data-resched-next]');
        if (nav && !nav.disabled) {
          reschedViewMonth += nav.hasAttribute('data-resched-prev') ? -1 : 1;
          if (reschedViewMonth < 0) { reschedViewMonth = 11; reschedViewYear--; }
          if (reschedViewMonth > 11) { reschedViewMonth = 0; reschedViewYear++; }
          renderReschedCalendar();
          return;
        }

        var dayBtn = e.target.closest('[data-resched-cycle]');
        if (dayBtn && !dayBtn.disabled && !reschedSaving) {
          var cycle = reschedCycleByIndex(Number(dayBtn.getAttribute('data-resched-cycle')));
          if (!cycle) return;
          if (cycle.skipped) { openUnskipOverlay(cycle); return; }
          if (reschedSelected[cycle.index]) delete reschedSelected[cycle.index];
          else reschedSelected[cycle.index] = true;
          setReschedMsg('');
          renderReschedCalendar();
          updateReschedUI();
          return;
        }

        var confirmBtn = e.target.closest('[data-resched-confirm]');
        if (confirmBtn && !confirmBtn.disabled) submitReschedSkips();
      });
    }

    if (unskipOverlay) {
      unskipOverlay.addEventListener('click', function (e) {
        if (e.target.closest('[data-unskip-close]')) { closeUnskipOverlay(); return; }
        var yesBtn = e.target.closest('[data-unskip-confirm]');
        if (yesBtn && !yesBtn.disabled) submitUnskip();
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (unskipOverlay && unskipOverlay.classList.contains('is-open')) { closeUnskipOverlay(); return; }
      if (reschedOverlay && reschedOverlay.classList.contains('is-open')) closeReschedOverlay();
    });
  }

  /* ══════════ SEND-NOW MODAL (charge next cycle now) ══════════ */
  var sendNowOverlay = document.getElementById('msv2-sendnow-overlay');
  var sendNowState = { sending: false, loadingDates: false, datesRequest: 0, selectedDate: '' };

  function estimateSendNowTotal() {
    if (!contract) return { amount: 0, currency: 'AUD', label: '' };
    var lines = Array.isArray(contract.lines) ? contract.lines : [];
    var currency = (contract.deliveryPrice && contract.deliveryPrice.currencyCode)
      || (lines[0] && lines[0].currency) || 'AUD';
    var pp = contract.pricing_preview || {};
    var estimatedOrderTotal = Number(pp.estimated_order_total_pre_tax_after_pending_discounts) || 0;
    var deliveryInEstimate = Number(contract.deliveryPrice && contract.deliveryPrice.amount) || 0;
    var totalNow = Math.max(0, estimatedOrderTotal - deliveryInEstimate);
    var ot = oneTimeTotals(contract);
    var ship = computeShippingPreview(contract);
    var shipAmount = ship ? Number(ship.amount) || 0 : 0;
    var amount = Math.round((totalNow + ot.after + shipAmount) * 100) / 100;
    return { amount: amount, currency: currency, label: formatMoney(amount, currency) };
  }

  /**
   * Why the customer cannot bring their box forward. The backend caps how many
   * cycles ahead they may reach; everything else is a generic unavailability.
   */
  function sendNowUnavailableText(sn) {
    if (sn && sn.reason === 'too_far_ahead') {
      return SETTINGS.sendNowUnavailableAheadText
        || 'You\'ve already brought forward 2 orders. You can bring another one forward after your next scheduled delivery.';
    }
    return SETTINGS.sendNowUnavailableText || 'Bringing your box forward isn\'t available right now.';
  }

  function updateSendNowButton() {
    var btn = root.querySelector('[data-send-now-btn]');
    if (!btn) return;
    var sn = (contract && contract.send_now) || {};
    // Theme editor has no session/contract — keep the control previewable.
    var allowed = SETTINGS.designMode
      || (!!sn.allowed && contract && String(contract.status || '').toUpperCase() === 'ACTIVE');
    btn.disabled = !allowed;
    btn.setAttribute('aria-disabled', allowed ? 'false' : 'true');
    var tip = allowed ? '' : sendNowUnavailableText(sn);
    if (tip) btn.setAttribute('title', tip);
    else btn.removeAttribute('title');
  }

  function setSendNowMsg(msg) {
    var el = sendNowOverlay && sendNowOverlay.querySelector('[data-sendnow-msg]');
    if (!el) return;
    el.hidden = !msg;
    el.textContent = msg || '';
  }

  function setSendNowLoading(on) {
    sendNowState.sending = !!on;
    if (!sendNowOverlay) return;
    var confirmBtn = sendNowOverlay.querySelector('[data-sendnow-confirm]');
    var cancelBtns = sendNowOverlay.querySelectorAll('[data-sendnow-close]');
    if (confirmBtn) {
      confirmBtn.disabled = sendNowState.sending || sendNowState.loadingDates;
      confirmBtn.classList.toggle('is-loading', sendNowState.sending);
      confirmBtn.textContent = sendNowState.sending
        ? (SETTINGS.sendNowSendingText || 'Sending…')
        : (SETTINGS.sendNowConfirmBtn || 'Yes, send my order now');
    }
    cancelBtns.forEach(function (b) { b.disabled = sendNowState.sending; });
  }

  function sendNowFormatDate(ymd) {
    var m = String(ymd || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return '';
    var d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
  }

  function renderSendNowDates(options) {
    if (!sendNowOverlay) return;
    var wrap = sendNowOverlay.querySelector('[data-sendnow-dates]');
    var list = sendNowOverlay.querySelector('[data-sendnow-dates-list]');
    if (!wrap || !list) return;

    var dates = (options || []).filter(function (d) { return /^\d{4}-\d{2}-\d{2}$/.test(d); });
    sendNowState.selectedDate = dates[0] || '';
    wrap.hidden = dates.length === 0;
    list.innerHTML = dates.map(function (ymd, i) {
      return '<button type="button" role="radio" class="msv2-sendnow-date' + (i === 0 ? ' is-selected' : '') +
        '" aria-checked="' + (i === 0 ? 'true' : 'false') + '" data-sendnow-date="' + ymd + '">' +
        sendNowFormatDate(ymd) + '</button>';
    }).join('');
  }

  function selectSendNowDate(ymd) {
    if (!sendNowOverlay) return;
    sendNowState.selectedDate = ymd;
    sendNowOverlay.querySelectorAll('[data-sendnow-date]').forEach(function (btn) {
      var on = btn.getAttribute('data-sendnow-date') === ymd;
      btn.classList.toggle('is-selected', on);
      btn.setAttribute('aria-checked', on ? 'true' : 'false');
    });
  }

  var SEND_NOW_DATE_OPTION_COUNT = 3;

  /**
   * Dates from a checkout-mode get_deliveries.php response for this contract:
   * the contract's own shipping tier, on or after the earliest date the send-now
   * endpoint accepts. States without an express tier (e.g. WA) return every
   * date as standard, so express contracts there fall back to that list.
   */
  function sendNowDatesFromCheckout(d) {
    if (!d || d.error) return [];
    var express = Array.isArray(d.express) ? d.express : [];
    var standard = Array.isArray(d.standard) ? d.standard : [];
    var tier = contract.shipping && contract.shipping.tier;
    var dates = tier === 'express' ? (express.length ? express : standard) : standard;
    var earliest = (contract.send_now && contract.send_now.earliest_delivery) || '';
    return dates
      .filter(function (ymd) { return !earliest || ymd >= earliest; })
      .slice(0, SEND_NOW_DATE_OPTION_COUNT);
  }

  /**
   * Carrier availability for the contract's delivery address, by the same rule
   * checkout uses. A failure here only hides the picker — the order still goes
   * out on the date the app would have used anyway.
   */
  function loadSendNowDates() {
    if (!sendNowOverlay || !contract || !contract.id) return;
    var wrap = sendNowOverlay.querySelector('[data-sendnow-dates]');
    var loadingEl = sendNowOverlay.querySelector('[data-sendnow-dates-loading]');
    var addr = contract.delivery_address || {};
    var postcode = String(addr.zip || '').trim();
    var suburb = String(addr.city || '').trim();
    var request = ++sendNowState.datesRequest;

    if (!postcode || !suburb) {
      sendNowState.loadingDates = false;
      if (loadingEl) loadingEl.hidden = true;
      renderSendNowDates([]);
      setSendNowLoading(false);
      return;
    }

    sendNowState.loadingDates = true;
    sendNowState.selectedDate = '';
    if (wrap) wrap.hidden = true;
    if (loadingEl) loadingEl.hidden = false;
    setSendNowLoading(false);

    fetch('https://iltechno.com/alj/get_deliveries.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ type: 'checkout', postalCode: postcode, suburb: suburb })
    })
      .then(function (r) { return r.json().catch(function () { return {}; }); })
      .catch(function () { return {}; })
      .then(function (d) {
        if (request !== sendNowState.datesRequest) return;
        sendNowState.loadingDates = false;
        if (loadingEl) loadingEl.hidden = true;
        renderSendNowDates(sendNowDatesFromCheckout(d));
        setSendNowLoading(false);
      });
  }

  function openSendNowOverlay() {
    if (!sendNowOverlay || !contract) return;
    var sn = contract.send_now || {};
    if (!SETTINGS.designMode && !sn.allowed) {
      setError(sendNowUnavailableText(sn));
      return;
    }
    setSendNowMsg('');
    setSendNowLoading(false);
    var textEl = sendNowOverlay.querySelector('[data-sendnow-text]');
    if (textEl) {
      textEl.textContent = SETTINGS.sendNowConfirmBody || '';
    }
    var est = estimateSendNowTotal();
    var amountEl = sendNowOverlay.querySelector('[data-sendnow-amount]');
    if (amountEl) {
      var tmpl = SETTINGS.sendNowConfirmAmount || 'Your card will be billed {{ amount }}.';
      amountEl.textContent = tmpl.replace(/\{\{\s*amount\s*\}\}/g, est.label);
      amountEl.hidden = !(est.amount > 0);
    }
    sendNowOverlay.classList.add('is-open');
    sendNowOverlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('msv2-no-scroll');
    // Theme editor has no session, so never hit the app proxy from it.
    if (SETTINGS.designMode) renderSendNowDates([]);
    else loadSendNowDates();
  }

  function closeSendNowOverlay() {
    if (!sendNowOverlay || sendNowState.sending) return;
    sendNowState.datesRequest++;
    sendNowState.loadingDates = false;
    sendNowOverlay.classList.remove('is-open');
    sendNowOverlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('msv2-no-scroll');
    setSendNowMsg('');
    // One acknowledgement covers one attempt only.
    sendNowWarnAcked = false;
  }

  function confirmSendNow() {
    if (!sendNowOverlay || sendNowState.sending || !contract || !contract.id) return;
    // Theme editor: open/confirm only — never hit the charge API.
    if (SETTINGS.designMode) {
      closeSendNowOverlay();
      setSuccess(SETTINGS.sendNowSuccessText || 'Your order is being processed.');
      return;
    }
    if (!(contract.send_now && contract.send_now.allowed)) {
      setSendNowMsg(sendNowUnavailableText(contract.send_now));
      return;
    }

    var payload = { subscriptionContractId: contract.id };
    if (sendNowState.selectedDate) payload.deliveryDate = sendNowState.selectedDate;

    setSendNowLoading(true);
    fetch('/apps/customer/subscription_send_now.php', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (d) {
          return { ok: r.ok, data: d };
        });
      })
      .then(function (res) {
        var d = res.data || {};
        var charged = d.outcome === 'charged';
        var pending = !!d.pending;
        var ok = charged || pending;
        if (!ok) {
          setSendNowLoading(false);
          setSendNowMsg(
            d.message
              || d.normalized_reason
              || (d.attempt && d.attempt.errorMessage)
              || SETTINGS.sendNowErrorText
              || 'We couldn\'t send your order right now.'
          );
          if (d.send_now && contract) {
            contract.send_now = d.send_now;
            updateSendNowButton();
          }
          return;
        }
        sendNowState.sending = false;
        sendNowOverlay.classList.remove('is-open');
        sendNowOverlay.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('msv2-no-scroll');
        sendNowWarnAcked = false;
        // There is now a box in flight. Remember it straight away so a second
        // attempt warns even before Shopify exposes the order to Liquid, and
        // pull the order list in so the customer can see what they just bought.
        markEarlyCharge();
        var msg = d.message
          || (pending
            ? (SETTINGS.sendNowPendingText || 'Your payment is still processing. You will get a confirmation and tracking details by email shortly.')
            : (SETTINGS.sendNowSuccessText || 'Your order is being processed. Tracking details will be emailed to you soon.'));
        setSuccess(msg);
        return Promise.all([reloadContracts(), refreshOrderHistory(true)]);
      })
      .catch(function () {
        setSendNowLoading(false);
        setSendNowMsg(SETTINGS.sendNowErrorText || 'We couldn\'t send your order right now.');
      });
  }

  /* ══════════ UNDELIVERED-BOX WARNING (shown before send-now) ══════════ */
  /* Liquid can only tell us about orders that existed at page load, and Shopify
     needs a moment after a charge before the new order reaches customer.orders.
     So the warning reads from two sources: the (refreshable) Liquid flag, and a
     local note that this browser charged early very recently. Without the second
     one a customer could charge twice in a row and never see the warning. */
  var sendNowWarnOverlay = document.getElementById('msv2-sendnow-warn-overlay');
  var EARLY_CHARGE_KEY = 'msv2_early_charge_at';
  var EARLY_CHARGE_GRACE_MS = 30 * 60 * 1000;
  /* Reset when the send-now modal closes, so every attempt is confirmed once. */
  var sendNowWarnAcked = false;

  function markEarlyCharge() {
    try { sessionStorage.setItem(EARLY_CHARGE_KEY, String(Date.now())); } catch (e) { /* storage blocked */ }
  }

  /* True while a charge from this browser is too fresh for Liquid to see it.
     Expires so the Liquid flag — which knows about actual fulfilment — takes
     over as the only source of truth. */
  function chargedTooRecently() {
    var at = 0;
    try { at = Number(sessionStorage.getItem(EARLY_CHARGE_KEY)) || 0; } catch (e) { return false; }
    if (!at) return false;
    if (Date.now() - at < EARLY_CHARGE_GRACE_MS) return true;
    try { sessionStorage.removeItem(EARLY_CHARGE_KEY); } catch (e) { /* ignore */ }
    return false;
  }

  function lastBoxUndelivered() {
    return !!SETTINGS.sendNowPendingDelivery || chargedTooRecently();
  }

  function openSendNowWarnOverlay() {
    if (!sendNowWarnOverlay) return;
    var orderEl = sendNowWarnOverlay.querySelector('[data-sendnow-warn-order]');
    if (orderEl) {
      var name = SETTINGS.sendNowPendingOrder || '';
      var date = SETTINGS.sendNowPendingDate || '';
      var detail = '';
      if (SETTINGS.sendNowPendingDelivery && name) {
        var tmpl = date
          ? (SETTINGS.sendNowUndeliveredOrderDated || 'Order {{ order }} is still on its way — delivery on {{ date }}.')
          : (SETTINGS.sendNowUndeliveredOrder || 'Order {{ order }} is still on its way.');
        detail = tmpl
          .replace(/\{\{\s*order\s*\}\}/g, name)
          .replace(/\{\{\s*date\s*\}\}/g, date);
      } else if (chargedTooRecently()) {
        // Charged moments ago, so Liquid has no order to name yet.
        detail = SETTINGS.sendNowUndeliveredRecent
          || 'You brought an order forward a few minutes ago and it is still being prepared.';
      }
      orderEl.textContent = detail;
      orderEl.hidden = !detail;
    }
    sendNowWarnOverlay.classList.add('is-open');
    sendNowWarnOverlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('msv2-no-scroll');
    var cancelBtn = sendNowWarnOverlay.querySelector('[data-sendnow-warn-cancel]');
    if (cancelBtn) cancelBtn.focus();
  }

  function closeSendNowWarnOverlay() {
    if (!sendNowWarnOverlay) return;
    sendNowWarnOverlay.classList.remove('is-open');
    sendNowWarnOverlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('msv2-no-scroll');
  }

  /**
   * Entry point for the "Get my box sooner" button. When the box the customer
   * already paid for has not been delivered yet, warn them first so they can
   * back out; only an unavailable send-now skips straight through, so
   * openSendNowOverlay() can explain why.
   */
  function requestSendNow() {
    var sn = (contract && contract.send_now) || {};
    var willProceed = SETTINGS.designMode || !!sn.allowed;
    if (willProceed && !sendNowWarnAcked && sendNowWarnOverlay && lastBoxUndelivered()) {
      openSendNowWarnOverlay();
      return;
    }
    openSendNowOverlay();
  }

  function wireSendNowWarnOverlay() {
    if (!sendNowWarnOverlay) return;

    sendNowWarnOverlay.addEventListener('click', function (e) {
      if (e.target.closest('[data-sendnow-warn-close]')) { closeSendNowWarnOverlay(); return; }
      if (e.target.closest('[data-sendnow-warn-confirm]')) {
        sendNowWarnAcked = true;
        closeSendNowWarnOverlay();
        openSendNowOverlay();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && sendNowWarnOverlay.classList.contains('is-open')) closeSendNowWarnOverlay();
    });
  }

  function wireSendNowOverlay() {
    if (!sendNowOverlay) return;

    sendNowOverlay.addEventListener('click', function (e) {
      if (e.target.closest('[data-sendnow-close]')) { closeSendNowOverlay(); return; }
      var dateBtn = e.target.closest('[data-sendnow-date]');
      if (dateBtn) {
        if (!sendNowState.sending) selectSendNowDate(dateBtn.getAttribute('data-sendnow-date'));
        return;
      }
      var confirmBtn = e.target.closest('[data-sendnow-confirm]');
      if (confirmBtn && !confirmBtn.disabled) confirmSendNow();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && sendNowOverlay.classList.contains('is-open')) closeSendNowOverlay();
    });
  }

  /* ══════════ LINE REMOVE CONFIRM MODAL ══════════ */
  var lineRemoveOverlay = document.getElementById('msv2-line-remove-overlay');
  var lineRemoveState = { lineId: '', removing: false, codes: [] };

  function setLineRemoveMsg(msg) {
    var el = lineRemoveOverlay && lineRemoveOverlay.querySelector('[data-line-remove-msg]');
    if (!el) return;
    el.hidden = !msg;
    el.textContent = msg || '';
  }

  function setLineRemoveCodeWarn(codes) {
    var el = lineRemoveOverlay && lineRemoveOverlay.querySelector('[data-line-remove-code-warn]');
    if (!el) return;
    if (!codes || !codes.length) {
      el.hidden = true;
      el.textContent = '';
      return;
    }
    var tmpl = SETTINGS.removeCodeWarningText
      || 'This item has a discount code applied ([code]). If you remove it, the discount will be lost and you won\'t be able to use that code again.';
    el.textContent = tmpl.replace('[code]', codes.join(', '));
    el.hidden = false;
  }

  function setLineRemoveLoading(on) {
    lineRemoveState.removing = !!on;
    if (!lineRemoveOverlay) return;
    lineRemoveOverlay.classList.toggle('is-removing', lineRemoveState.removing);
    var confirmBtn = lineRemoveOverlay.querySelector('[data-line-remove-confirm]');
    var cancelBtns = lineRemoveOverlay.querySelectorAll('[data-line-remove-close]');
    if (confirmBtn) {
      confirmBtn.disabled = lineRemoveState.removing;
      confirmBtn.classList.toggle('is-loading', lineRemoveState.removing);
      confirmBtn.textContent = lineRemoveState.removing
        ? (SETTINGS.removeRemovingText || 'Removing…')
        : (SETTINGS.removeConfirmBtnText || 'Yes, remove');
    }
    cancelBtns.forEach(function (btn) { btn.disabled = lineRemoveState.removing; });
  }

  function fillLineRemovePreview(line) {
    var nameEl = lineRemoveOverlay.querySelector('[data-line-remove-name]');
    var variantEl = lineRemoveOverlay.querySelector('[data-line-remove-variant]');
    var imgEl = lineRemoveOverlay.querySelector('[data-line-remove-img]');
    if (nameEl) nameEl.textContent = line.product_title || '';
    if (variantEl) {
      var variantTitle = line.variant_title && line.variant_title !== 'Default Title' ? line.variant_title : '';
      variantEl.textContent = variantTitle;
      variantEl.hidden = !variantTitle;
    }
    if (imgEl) {
      var img = (line.variant_image && line.variant_image.url) || '';
      var alt = (line.variant_image && line.variant_image.altText) || line.product_title || '';
      imgEl.innerHTML = img
        ? '<img src="' + escapeAttr(img) + '" alt="' + escapeAttr(alt) + '" loading="lazy">'
        : NO_IMAGE_SVG;
    }
  }

  function openLineRemoveOverlay(line) {
    if (!lineRemoveOverlay || !line) return;
    lineRemoveState.lineId = String(line.line_id || '');
    lineRemoveState.codes = removableCodesForLine(lineRemoveState.lineId);
    // Fallback: line shows a discounted price but entitled_lines didn't match
    // (e.g. stale IDs) — still warn using any removable applied codes.
    if (!lineRemoveState.codes.length
        && line.discounted_price !== null && line.discounted_price !== undefined
        && Number(line.discounted_price) < (Number(line.price) || 0) - 0.0001) {
      var applied = Array.isArray(contract && contract.applied_discount_codes)
        ? contract.applied_discount_codes.filter(isRemovableCoupon)
        : [];
      lineRemoveState.codes = applied;
    }
    setLineRemoveMsg('');
    setLineRemoveCodeWarn(lineRemoveState.codes);
    setLineRemoveLoading(false);
    fillLineRemovePreview(line);
    lineRemoveOverlay.classList.add('is-open');
    lineRemoveOverlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('msv2-no-scroll');
  }

  function closeLineRemoveOverlay() {
    if (!lineRemoveOverlay || lineRemoveState.removing) return;
    lineRemoveOverlay.classList.remove('is-open');
    lineRemoveOverlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('msv2-no-scroll');
    lineRemoveState.lineId = '';
    lineRemoveState.codes = [];
    setLineRemoveMsg('');
    setLineRemoveCodeWarn([]);
  }

  function confirmLineRemove() {
    if (!lineRemoveOverlay || lineRemoveState.removing) return;
    var lineId = lineRemoveState.lineId;
    if (!lineId) return;

    if (recurringLineCount() <= 1) {
      setLineRemoveMsg(SETTINGS.removeLastErrorText || 'Cannot remove the last item. Please cancel the subscription instead.');
      return;
    }

    if (SETTINGS.designMode) {
      if (!contract || !Array.isArray(contract.lines)) return;
      setLineRemoveLoading(true);
      window.setTimeout(function () {
        contract.lines = contract.lines.filter(function (l) {
          return String(l.line_id) !== String(lineId);
        });
        lineRemoveState.removing = false;
        lineRemoveOverlay.classList.remove('is-removing', 'is-open');
        lineRemoveOverlay.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('msv2-no-scroll');
        lineRemoveState.lineId = '';
        renderNextOrder();
        renderSummary();
        setSuccess(SETTINGS.removeSuccessText || 'Item removed from your subscription.');
      }, 400);
      return;
    }

    if (!contract || !contract.id) {
      setLineRemoveMsg(SETTINGS.genericErrorText);
      return;
    }

    setLineRemoveLoading(true);
    setLineRemoveMsg('');

    fetch('/apps/customer/subscription_remove_item.php', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        subscriptionContractId: contract.id,
        lineId: lineId
      })
    })
      .then(function (r) { return r.json().catch(function () { return {}; }).then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        if (!res.ok || res.data.error || res.data.success === false) {
          setLineRemoveLoading(false);
          setLineRemoveMsg(res.data.message || SETTINGS.genericErrorText);
          return;
        }
        lineRemoveState.removing = false;
        lineRemoveOverlay.classList.remove('is-removing', 'is-open');
        lineRemoveOverlay.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('msv2-no-scroll');
        lineRemoveState.lineId = '';
        setSuccess(SETTINGS.removeSuccessText || 'Item removed from your subscription.');
        return applyContractMutation(res);
      })
      .catch(function () {
        setLineRemoveLoading(false);
        setLineRemoveMsg(SETTINGS.genericErrorText);
      });
  }

  function wireLineRemoveOverlay() {
    if (!lineRemoveOverlay) return;

    lineRemoveOverlay.addEventListener('click', function (e) {
      if (e.target.closest('[data-line-remove-close]')) {
        closeLineRemoveOverlay();
        return;
      }
      var confirmBtn = e.target.closest('[data-line-remove-confirm]');
      if (confirmBtn && !confirmBtn.disabled) confirmLineRemove();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && lineRemoveOverlay.classList.contains('is-open')) closeLineRemoveOverlay();
    });
  }

  /* ══════════ ORDER HISTORY TAB ══════════ */
  /* Order rows (snippets/msv2-order-history.liquid) each carry a
     <template data-order-detail> with their full detail markup; clicking
     a row clones it into the order details modal shell
     (snippets/msv2-order-modal.liquid). */
  var orderOverlay = document.getElementById('msv2-order-overlay');

  /* The order list and the undelivered-box flag are rendered by Liquid from
     customer.orders, so both are only a snapshot of page load: an order placed
     in this session is invisible to them. Re-render both from a fresh copy of
     the current page — the same approach refreshAddressOverlay already uses —
     whenever the customer opens the tab and after an early charge, so nobody
     has to know to press reload. window.location.href keeps ?page=N, so
     pagination stays where it was. */
  var orderHistoryFetchedAt = Date.now();
  var orderHistoryFetching = false;
  /* Rapid tab toggling shouldn't refetch the page every time. */
  var ORDER_HISTORY_MIN_INTERVAL_MS = 10000;

  function refreshOrderHistory(force) {
    var host = root.querySelector('[data-msv2-order-history]');
    if (SETTINGS.designMode || !host || orderHistoryFetching) return Promise.resolve();
    // Even when the list itself is fresh enough, the carrier status is not
    // part of the Liquid markup and has to be asked for.
    if (!force && Date.now() - orderHistoryFetchedAt < ORDER_HISTORY_MIN_INTERVAL_MS) return hydrateOrderStatuses();

    orderHistoryFetching = true;
    host.setAttribute('data-refreshing', 'true');
    return fetch(window.location.href, { credentials: 'same-origin' })
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-msv2-order-history]');
        // Rows are handled by the delegated click listener on root, and their
        // detail <template> is read at click time, so swapping this markup
        // needs no re-wiring.
        if (fresh) host.innerHTML = fresh.innerHTML;

        // Same page, so the same config script — with a recomputed flag.
        var freshConfig = doc.querySelector('[data-msv2-config]');
        if (freshConfig) {
          try {
            var parsed = JSON.parse(freshConfig.textContent).settings || {};
            SETTINGS.sendNowPendingDelivery = !!parsed.sendNowPendingDelivery;
            SETTINGS.sendNowPendingOrder = parsed.sendNowPendingOrder || '';
            SETTINGS.sendNowPendingDate = parsed.sendNowPendingDate || '';
          } catch (e) { /* keep the flag we already have */ }
        }
        orderHistoryFetchedAt = Date.now();
      })
      .catch(function () { /* keep what is on screen; retried on the next open */ })
      .then(function () {
        orderHistoryFetching = false;
        host.removeAttribute('data-refreshing');
        // The swapped-in rows are back to Liquid's "Shipped"; layer the
        // carrier status on again.
        return hydrateOrderStatuses(true);
      });
  }

  /* ── Carrier delivery status ──
     Liquid renders the status pill from order.fulfillment_status, which is
     "fulfilled" the moment a tracking number is added — so on its own it can
     never say more than "Shipped". The ALJ backend mirrors each order's
     carrier-fed Fulfillment.displayStatus (in transit, out for delivery,
     delivered, ...); ask it for the orders on the page and upgrade the pill,
     the date line ("Delivered <actual date>") and the tracking block inside
     the detail <template>. Orders it has no answer for keep the Liquid state,
     so a backend hiccup degrades to "Shipped", never to a wrong "Delivered". */
  var ORDER_STATUS_BATCH = 25;
  var orderStatusFetching = false;
  var orderStatusHydratedAt = 0;
  /* Rows keep their upgraded pills until the list is re-rendered, so a
     re-ask within this window (rapid tab toggling) is pure repetition. */
  var ORDER_STATUS_MIN_INTERVAL_MS = 60000;

  /* Same shape as Liquid's `date: '%b %-d, %Y'` on the rest of the line. */
  function formatOrderDate(iso) {
    var d = new Date(iso);
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  }

  /* Apply one order's status to a subtree: the row, or the detail template's
     DocumentFragment (querySelectorAll does not descend into <template>, so
     the two are done separately and never overlap). */
  function applyOrderStatusTo(scope, info) {
    var code = String(info.status || '');
    var labels = SETTINGS.orderStatusLabels || {};
    var label = labels[code];
    if (!label) return; // unknown code: leave the Liquid pill alone

    scope.querySelectorAll('[data-order-status]').forEach(function (pill) {
      var prev = pill.getAttribute('data-order-status');
      if (prev) pill.classList.remove('msv2-oh-status--' + prev);
      pill.classList.add('msv2-oh-status--' + code);
      pill.setAttribute('data-order-status', code);
      var text = pill.querySelector('[data-order-status-label]');
      if (text) text.textContent = label;
    });

    // "Delivery <scheduled day>" becomes "Delivered <day the carrier confirmed>".
    var deliveredDate = code === 'delivered' && info.delivered_at ? formatOrderDate(info.delivered_at) : '';
    if (deliveredDate && SETTINGS.orderDeliveredOnText) {
      var lineText = ' · ' + SETTINGS.orderDeliveredOnText.replace('__DATE__', deliveredDate);
      scope.querySelectorAll('[data-order-dates]').forEach(function (line) {
        var seg = line.querySelector('[data-order-dates-delivery]');
        if (!seg) {
          seg = document.createElement('span');
          seg.setAttribute('data-order-dates-delivery', '');
          var ordered = line.querySelector('[data-order-dates-ordered]');
          if (ordered && ordered.nextSibling) line.insertBefore(seg, ordered.nextSibling);
          else line.appendChild(seg);
        }
        seg.textContent = lineText;
      });
    }

    // Tracking block (only exists in the detail template).
    var tracking = Array.isArray(info.tracking)
      ? info.tracking.filter(function (t) { return t && (t.number || t.url); })
      : [];
    var etaDate = !deliveredDate && code !== 'cancelled' && info.estimated_delivery_at
      ? formatOrderDate(info.estimated_delivery_at)
      : '';
    scope.querySelectorAll('[data-order-tracking]').forEach(function (box) {
      if (!tracking.length && !etaDate) {
        box.hidden = true;
        box.innerHTML = '';
        return;
      }
      var html = '';
      if (tracking.length) {
        html += '<span class="msv2-od-tracking-label">' + escapeHtml(SETTINGS.orderTrackingLabel || 'Tracking') + '</span>';
        tracking.forEach(function (t) {
          html += '<span class="msv2-od-tracking-item">';
          if (t.company) html += '<span>' + escapeHtml(t.company) + '</span>';
          if (t.number) html += '<span class="msv2-od-tracking-number">' + escapeHtml(t.number) + '</span>';
          if (t.url && /^https?:\/\//i.test(t.url)) {
            html += '<a class="msv2-od-tracking-link" href="' + escapeAttr(t.url) + '" target="_blank" rel="noopener noreferrer">' +
              escapeHtml(SETTINGS.orderTrackingLinkText || 'Track parcel') + '</a>';
          }
          html += '</span>';
        });
      }
      if (etaDate && SETTINGS.orderEstimatedDeliveryText) {
        html += '<span class="msv2-od-tracking-item">' +
          escapeHtml(SETTINGS.orderEstimatedDeliveryText.replace('__DATE__', etaDate)) + '</span>';
      }
      box.innerHTML = html;
      box.hidden = false;
    });
  }

  function hydrateOrderStatuses(force) {
    var host = root.querySelector('[data-msv2-order-history]');
    if (SETTINGS.designMode || !host || orderStatusFetching) return Promise.resolve();
    if (!force && Date.now() - orderStatusHydratedAt < ORDER_STATUS_MIN_INTERVAL_MS) return Promise.resolve();
    var items = Array.prototype.slice.call(host.querySelectorAll('.msv2-oh-item[data-order-id]'))
      // Cancelled is final in Liquid already; nothing the carrier says changes it.
      .filter(function (el) { return el.getAttribute('data-order-cancelled') !== 'true'; })
      .slice(0, ORDER_STATUS_BATCH);
    if (!items.length) return Promise.resolve();

    orderStatusFetching = true;
    return fetch('/apps/customer/order_fulfillment_status.php', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orderIds: items.map(function (el) { return el.getAttribute('data-order-id'); }) })
    })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) {
        if (!data || !data.ok || !data.orders) return;
        orderStatusHydratedAt = Date.now();
        items.forEach(function (item) {
          var info = data.orders[item.getAttribute('data-order-id')];
          if (!info) return;
          applyOrderStatusTo(item, info);
          var tpl = item.querySelector('template[data-order-detail]');
          if (tpl && tpl.content) applyOrderStatusTo(tpl.content, info);
        });
      })
      .catch(function () { /* keep the Liquid state */ })
      .then(function () { orderStatusFetching = false; });
  }

  function openOrderModal(row) {
    if (!orderOverlay) return;
    var tpl = row.parentElement && row.parentElement.querySelector('template[data-order-detail]');
    var body = orderOverlay.querySelector('[data-order-modal-body]');
    var title = orderOverlay.querySelector('[data-order-modal-title]');
    if (!tpl || !body) return;
    body.innerHTML = '';
    body.appendChild(tpl.content.cloneNode(true));
    if (title) title.textContent = row.getAttribute('data-order-title') || '';
    orderOverlay.classList.add('is-open');
    orderOverlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('msv2-no-scroll');
  }

  function closeOrderModal() {
    if (!orderOverlay) return;
    orderOverlay.classList.remove('is-open');
    orderOverlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('msv2-no-scroll');
  }

  function wireOrderModal() {
    if (!orderOverlay) return;
    orderOverlay.addEventListener('click', function (e) {
      if (e.target.closest('[data-order-modal-close]')) closeOrderModal();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && orderOverlay.classList.contains('is-open')) closeOrderModal();
    });
  }

  /* ══════════ ACCOUNT TAB ══════════ */
  /* The account view (snippets/msv2-account.liquid) is rendered hidden;
     data-view="account" on the portal root swaps the grid + loyalty
     sections for it (see the CSS). */
  function setPortalView(name) {
    // Leaving the next-order tab resets the item pagination so returning always
    // lands on the first page. Re-rendering here rather than on the way back
    // means the reset happens while the card is hidden, with no visible jump.
    if (name !== 'next-order' && linesPage !== 1) {
      linesPage = 1;
      if (contract) renderNextOrder();
    }
    // Opening the tab is the moment the customer expects current data, so pull
    // the Liquid-rendered list again instead of showing page-load state. Just
    // after a charge the throttle is bypassed: the refresh that ran on success
    // was probably too early for Shopify to have created the order yet.
    if (name === 'order-history') refreshOrderHistory(chargedTooRecently());
    root.setAttribute('data-view', name);
    // The cancel flow lives under the Account tab, so keep it highlighted.
    var activeTab = name === 'cancel' ? 'account' : name;
    root.querySelectorAll('.msv2-tab').forEach(function (tab) {
      tab.classList.toggle('is-active', tab.getAttribute('data-msv2-tab') === activeTab);
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /* ══════════ EDIT DETAILS (PROFILE) MODAL ══════════ */
  var profileOverlay = document.getElementById('msv2-profile-overlay');

  function openProfileOverlay() {
    if (!profileOverlay) return;
    setProfileMsg('');
    renderSmsConsent();
    profileOverlay.classList.add('is-open');
    profileOverlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('msv2-no-scroll');
  }
  function closeProfileOverlay() {
    if (!profileOverlay) return;
    profileOverlay.classList.remove('is-open');
    profileOverlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('msv2-no-scroll');
  }
  function setProfileMsg(msg, ok) {
    var el = profileOverlay && profileOverlay.querySelector('[data-profile-msg]');
    if (!el) return;
    el.hidden = !msg;
    el.textContent = msg || '';
    el.classList.toggle('is-error', !ok && !!msg);
    el.classList.toggle('is-success', !!ok && !!msg);
  }
  /* Reflect the saved values in the "Your info" card without a reload. */
  function updateProfileView(values) {
    var map = {
      '[data-profile-name]': ((values.firstName || '') + ' ' + (values.lastName || '')).trim(),
      '[data-profile-email]': values.email || '',
      '[data-profile-phone]': values.phone || ''
    };
    Object.keys(map).forEach(function (sel) {
      var el = root.querySelector(sel);
      if (!el) return;
      el.textContent = map[sel] || SETTINGS.profileNotProvidedText;
      el.classList.toggle('is-empty', !map[sel]);
    });
  }

  /* ── SMS transactional consent ──
     Klaviyo will not send the pre-charge text unless the customer's profile carries
     transactional SMS consent, so this is what unlocks that message. It saves to its own
     endpoint rather than riding along with the profile payload: the consent has to reach
     Klaviyo before it can be recorded, and it must not be lost if the profile save fails.

     State is fetched rather than rendered, because the metafield holding it is written by the
     app via the Admin API and is not exposed to Liquid. */
  var smsConsentState = { consent: false, phone: '' };

  function renderSmsConsent() {
    var input = profileOverlay && profileOverlay.querySelector('[data-sms-consent-input]');
    if (input) input.checked = !!smsConsentState.consent;
    renderSmsConsentWarning();

    var row = root.querySelector('[data-profile-sms-consent]');
    if (row) {
      row.textContent = smsConsentState.consent
        ? SETTINGS.smsConsentStateOnText
        : SETTINGS.smsConsentStateOffText;
      row.classList.toggle('is-empty', !smsConsentState.consent);
    }
  }

  /* Klaviyo revokes SMS marketing alongside transactional, so unticking the box stops every
     text we send, not just the box reminder. Warn while the customer can still change their
     mind: the line shows the moment an opted-in customer unticks, and hides again if they
     re-tick or reopen the overlay. */
  function renderSmsConsentWarning() {
    var warn = profileOverlay && profileOverlay.querySelector('[data-sms-consent-warn]');
    if (!warn) return;
    var input = profileOverlay.querySelector('[data-sms-consent-input]');
    var revoking = smsConsentState.consent && !(input && input.checked);
    warn.hidden = !revoking;
  }

  function loadSmsConsent() {
    if (SETTINGS.designMode) return;
    fetch('/apps/customer/sms_consent.php', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.ok) return;
        smsConsentState = { consent: !!data.consent, phone: data.phone || '' };
        renderSmsConsent();
      })
      .catch(function () { /* leave the checkbox unchecked; opting in again is harmless */ });
  }

  /* Resolves to an error string, or '' when there was nothing to do or it succeeded.

     An already-consented customer is re-sent rather than skipped: if they edited their phone
     number in the same save, Klaviyo and the consent record would otherwise keep texting the
     old one. Re-subscribing an already-subscribed profile is a no-op to Klaviyo apart from
     refreshing the number, so the only case worth short-circuiting is the customer who is not
     opted in and is not asking to be. */
  function saveSmsConsent(consent, phone) {
    if (!consent && !smsConsentState.consent) return Promise.resolve('');
    if (SETTINGS.designMode) {
      smsConsentState.consent = consent;
      renderSmsConsent();
      return Promise.resolve('');
    }

    return fetch('/apps/customer/sms_consent.php', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ consent: consent, phone: phone, source: 'portal_profile' })
    })
      .then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (d) { return { ok: r.ok, data: d }; });
      })
      .then(function (res) {
        if (!res.ok || !res.data.ok) {
          return res.data.message || SETTINGS.smsConsentErrorText;
        }
        smsConsentState = { consent: !!res.data.consent, phone: res.data.phone || phone };
        renderSmsConsent();
        return '';
      })
      .catch(function () { return SETTINGS.smsConsentErrorText; });
  }

  function wireProfileOverlay() {
    if (!profileOverlay) return;

    profileOverlay.addEventListener('click', function (e) {
      if (e.target.closest('[data-profile-close]')) closeProfileOverlay();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && profileOverlay.classList.contains('is-open')) closeProfileOverlay();
    });

    var form = profileOverlay.querySelector('[data-profile-form]');
    if (!form) return;

    var consentToggle = form.querySelector('[data-sms-consent-input]');
    if (consentToggle) consentToggle.addEventListener('change', renderSmsConsentWarning);

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var btn = form.querySelector('[data-profile-save]');
      var values = {
        firstName: (form.elements.first_name.value || '').trim(),
        lastName: (form.elements.last_name.value || '').trim(),
        email: (form.elements.email.value || '').trim(),
        phone: (form.elements.phone.value || '').trim()
      };
      var consentInput = form.querySelector('[data-sms-consent-input]');
      var wantsConsent = !!(consentInput && consentInput.checked);

      if (SETTINGS.designMode) {
        updateProfileView(values);
        void saveSmsConsent(wantsConsent, values.phone);
        closeProfileOverlay();
        setSuccess(SETTINGS.profileSavedText);
        return;
      }

      setProfileMsg(SETTINGS.profileSavingText, true);
      if (btn) { btn.disabled = true; btn.classList.add('is-loading'); }
      fetch('/apps/customer/customer_update.php', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(values)
      })
        .then(function (r) { return r.json().catch(function () { return {}; }).then(function (d) { return { ok: r.ok, data: d }; }); })
        .then(function (res) {
          if (!res.ok || res.data.error || res.data.success === false) {
            setProfileMsg(res.data.message || SETTINGS.genericErrorText, false);
            return null;
          }
          updateProfileView(values);
          // The consent call runs after the profile save so it sees the number the customer
          // just entered, which is the number the consent has to be recorded against.
          return saveSmsConsent(wantsConsent, values.phone);
        })
        .then(function (consentError) {
          if (consentError === null) return; // profile save already reported its own failure
          if (consentError) {
            // The details themselves saved, so say what did and did not go through rather
            // than letting the customer think nothing was kept.
            setProfileMsg(consentError, false);
            return;
          }
          closeProfileOverlay();
          setSuccess(SETTINGS.profileSavedText);
        })
        .catch(function () {
          setProfileMsg(SETTINGS.genericErrorText, false);
        })
        .finally(function () {
          if (btn) { btn.disabled = false; btn.classList.remove('is-loading'); }
        });
    });
  }

  /* ══════════ EDIT LINE ITEM MODAL ══════════ */
  var lineEditOverlay = document.getElementById('msv2-line-edit-overlay');
  var lineEditState = {
    lineId: '',
    productUrl: '',
    productTitle: '',
    currency: 'AUD',
    initialQty: 1,
    initialVariantId: '',
    product: null
  };
  var productByHandleCache = {};

  function productHandleFromUrl(url) {
    if (!url) return '';
    var m = String(url).match(/\/products\/([^/?#]+)/i);
    return m ? decodeURIComponent(m[1]) : '';
  }

  function findCachedShopProduct(line) {
    var handle = productHandleFromUrl(line && line.product_url);
    var productId = normalizeVariantId(line && line.product_id); // numeric-ish compare
    var pools = [shopProducts, recoProducts];
    for (var p = 0; p < pools.length; p++) {
      var list = pools[p];
      if (!Array.isArray(list)) continue;
      for (var i = 0; i < list.length; i++) {
        var prod = list[i];
        if (handle && prod.handle === handle) return prod;
        if (productId && normalizeVariantId(prod.id) === productId) return prod;
      }
    }
    return null;
  }

  function normalizeAjaxProduct(p) {
    var variants = Array.isArray(p.variants) ? p.variants : [];
    var featured = (p.featured_image && p.featured_image.src) || (p.images && p.images[0] && (p.images[0].src || p.images[0])) || '';
    return {
      id: p.id,
      title: p.title,
      handle: p.handle,
      image_url: featured,
      product_url: '/products/' + p.handle,
      // /products/{handle}.js returns prices in cents
      variants: variants.map(function (v) {
        var cents = Number(v.price);
        return {
          id: v.id,
          title: v.title,
          price: Number.isFinite(cents) ? cents / 100 : null,
          available: v.available !== false
        };
      })
    };
  }

  function fetchProductByHandle(handle) {
    if (!handle) return Promise.resolve(null);
    if (productByHandleCache[handle]) return productByHandleCache[handle];
    var promise = fetch('/products/' + encodeURIComponent(handle) + '.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(normalizeAjaxProduct)
      .catch(function () { return null; });
    productByHandleCache[handle] = promise;
    return promise;
  }

  function setLineEditMsg(msg, ok) {
    var el = lineEditOverlay && lineEditOverlay.querySelector('[data-line-edit-msg]');
    if (!el) return;
    el.hidden = !msg;
    el.textContent = msg || '';
    el.classList.toggle('is-error', !ok && !!msg);
    el.classList.toggle('is-success', !!ok && !!msg);
  }

  function setLineEditQty(qty) {
    var input = lineEditOverlay && lineEditOverlay.querySelector('[data-line-edit-qty-input]');
    var minus = lineEditOverlay && lineEditOverlay.querySelector('[data-line-edit-qty-minus]');
    if (!input) return;
    var next = Math.max(1, Math.floor(Number(qty) || 1));
    input.value = String(next);
    if (minus) minus.disabled = next <= 1;
    updateLineEditPricePreview();
  }

  function selectedLineEditVariant() {
    var select = lineEditOverlay && lineEditOverlay.querySelector('[data-line-edit-variant]');
    var product = lineEditState.product;
    var variants = product && Array.isArray(product.variants) ? product.variants : [];
    if (select && select.value) {
      for (var i = 0; i < variants.length; i++) {
        if (normalizeVariantId(variants[i].id) === normalizeVariantId(select.value)) return variants[i];
      }
    }
    for (var j = 0; j < variants.length; j++) {
      if (normalizeVariantId(variants[j].id) === normalizeVariantId(lineEditState.initialVariantId)) return variants[j];
    }
    return variants[0] || null;
  }

  function updateLineEditPricePreview() {
    var priceEl = lineEditOverlay && lineEditOverlay.querySelector('[data-line-edit-price]');
    if (!priceEl) return;
    var input = lineEditOverlay.querySelector('[data-line-edit-qty-input]');
    var qty = Math.max(1, Number(input && input.value) || 1);
    var variant = selectedLineEditVariant();
    var unit = variant && variant.price != null
      ? Number(variant.price)
      : null;
    if (unit == null && contract) {
      var lines = Array.isArray(contract.lines) ? contract.lines : [];
      for (var i = 0; i < lines.length; i++) {
        if (String(lines[i].line_id) === String(lineEditState.lineId)) {
          unit = Number(lines[i].price) || 0;
          break;
        }
      }
    }
    if (unit == null || !Number.isFinite(unit)) {
      priceEl.textContent = '';
      return;
    }
    priceEl.textContent = formatMoney(unit * qty, lineEditState.currency);
  }

  function fillLineEditVariantSelect(product, selectedVariantId) {
    var field = lineEditOverlay.querySelector('[data-line-edit-variant-field]');
    var select = lineEditOverlay.querySelector('[data-line-edit-variant]');
    if (!field || !select) return;
    var variants = Array.isArray(product && product.variants) ? product.variants : [];
    var meaningful = variants.filter(function (v) {
      return v && v.title && v.title !== 'Default Title';
    });
    if (variants.length <= 1 && meaningful.length === 0) {
      field.hidden = true;
      select.innerHTML = '';
      return;
    }
    select.innerHTML = variants.map(function (v) {
      var oos = isVariantOutOfStock(v) || v.available === false;
      var isCurrent = normalizeVariantId(v.id) === normalizeVariantId(selectedVariantId);
      // Keep the currently selected variant choosable even if OOS, so save isn't blocked.
      var disabled = oos && !isCurrent;
      var label = (v.title || 'Default') + (oos ? ' — ' + (SETTINGS.shopOosLabel || 'Out of stock') : '');
      return '<option value="' + escapeAttr(String(v.id || '')) + '"' +
        (isCurrent ? ' selected' : '') +
        (disabled ? ' disabled' : '') +
        ' data-price="' + escapeAttr(String(v.price != null ? v.price : '')) + '">' +
        escapeHtml(label) +
      '</option>';
    }).join('');
    field.hidden = false;
  }

  function renderLineEditHeader(line) {
    var titleEl = lineEditOverlay.querySelector('[data-line-edit-title]');
    var imgEl = lineEditOverlay.querySelector('[data-line-edit-img]');
    if (titleEl) titleEl.textContent = line.product_title || '';
    if (imgEl) {
      var img = (line.variant_image && line.variant_image.url) || '';
      var alt = (line.variant_image && line.variant_image.altText) || line.product_title || '';
      imgEl.innerHTML = img
        ? '<img src="' + escapeAttr(img) + '" alt="' + escapeAttr(alt) + '" loading="lazy">'
        : NO_IMAGE_SVG;
    }
  }

  function closeLineEditOverlay() {
    if (!lineEditOverlay) return;
    lineEditOverlay.classList.remove('is-open');
    lineEditOverlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('msv2-no-scroll');
    setLineEditMsg('');
  }

  function openLineEditOverlay(line) {
    if (!lineEditOverlay || !line) return;
    lineEditState = {
      lineId: String(line.line_id || ''),
      productUrl: line.product_url || '',
      productTitle: line.product_title || '',
      currency: line.currency || (contract && contract.deliveryPrice && contract.deliveryPrice.currencyCode) || 'AUD',
      initialQty: Math.max(1, Number(line.quantity) || 1),
      initialVariantId: String(line.variant_id || ''),
      product: null
    };

    renderLineEditHeader(line);
    setLineEditMsg('');
    setLineEditQty(lineEditState.initialQty);

    var form = lineEditOverlay.querySelector('[data-line-edit-form]');
    var loading = lineEditOverlay.querySelector('[data-line-edit-loading]');
    var variantField = lineEditOverlay.querySelector('[data-line-edit-variant-field]');
    if (form) form.hidden = true;
    if (loading) loading.hidden = false;
    if (variantField) variantField.hidden = true;

    lineEditOverlay.classList.add('is-open');
    lineEditOverlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('msv2-no-scroll');

    var cached = findCachedShopProduct(line);
    var handle = productHandleFromUrl(line.product_url);
    var load = cached ? Promise.resolve(cached) : fetchProductByHandle(handle);

    load.then(function (product) {
      if (!lineEditOverlay.classList.contains('is-open')) return;
      if (loading) loading.hidden = true;
      if (form) form.hidden = false;
      if (!product) {
        lineEditState.product = null;
        if (variantField) variantField.hidden = true;
        setLineEditMsg(SETTINGS.lineEditLoadErrorText || '', false);
        updateLineEditPricePreview();
        return;
      }
      lineEditState.product = product;
      fillLineEditVariantSelect(product, lineEditState.initialVariantId);
      updateLineEditPricePreview();
    });
  }

  function openLineEditForLineId(lineId) {
    if (!lineId || !contract) return;
    var lines = getDisplayLines(contract);
    var line = null;
    for (var i = 0; i < lines.length; i++) {
      if (String(lines[i].line_id) === String(lineId)) {
        line = lines[i];
        break;
      }
    }
    if (!line || line.is_one_time) return;
    openLineEditOverlay(line);
  }

  function applyLineEditLocally(variantId, quantity) {
    if (!contract || !Array.isArray(contract.lines)) return;
    var product = lineEditState.product;
    var variants = product && Array.isArray(product.variants) ? product.variants : [];
    var variant = null;
    for (var i = 0; i < variants.length; i++) {
      if (normalizeVariantId(variants[i].id) === normalizeVariantId(variantId)) {
        variant = variants[i];
        break;
      }
    }
    contract.lines = contract.lines.map(function (l) {
      if (String(l.line_id) !== String(lineEditState.lineId)) return l;
      var next = Object.assign({}, l, { quantity: quantity });
      if (variant) {
        next.variant_id = 'gid://shopify/ProductVariant/' + normalizeVariantId(variant.id);
        next.variant_title = variant.title;
        if (variant.price != null) next.price = Number(variant.price);
      }
      return next;
    });
    renderNextOrder();
    renderSummary();
  }

  function wireLineEditOverlay() {
    if (!lineEditOverlay) return;

    lineEditOverlay.addEventListener('click', function (e) {
      if (e.target.closest('[data-line-edit-close]')) {
        closeLineEditOverlay();
        return;
      }
      if (e.target.closest('[data-line-edit-qty-minus]')) {
        var inputMinus = lineEditOverlay.querySelector('[data-line-edit-qty-input]');
        setLineEditQty((Number(inputMinus && inputMinus.value) || 1) - 1);
        return;
      }
      if (e.target.closest('[data-line-edit-qty-plus]')) {
        var inputPlus = lineEditOverlay.querySelector('[data-line-edit-qty-input]');
        setLineEditQty((Number(inputPlus && inputPlus.value) || 1) + 1);
      }
    });

    lineEditOverlay.addEventListener('change', function (e) {
      if (e.target.matches('[data-line-edit-variant]')) updateLineEditPricePreview();
    });
    lineEditOverlay.addEventListener('input', function (e) {
      if (e.target.matches('[data-line-edit-qty-input]')) {
        var v = Math.max(1, Math.floor(Number(e.target.value) || 1));
        e.target.value = String(v);
        var minus = lineEditOverlay.querySelector('[data-line-edit-qty-minus]');
        if (minus) minus.disabled = v <= 1;
        updateLineEditPricePreview();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && lineEditOverlay.classList.contains('is-open')) closeLineEditOverlay();
    });

    var form = lineEditOverlay.querySelector('[data-line-edit-form]');
    if (!form) return;
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var btn = form.querySelector('[data-line-edit-save]');
      var qtyInput = form.querySelector('[data-line-edit-qty-input]');
      var variantSelect = form.querySelector('[data-line-edit-variant]');
      var quantity = Math.max(1, Math.floor(Number(qtyInput && qtyInput.value) || 1));
      var variantId = (variantSelect && variantSelect.value)
        ? variantSelect.value
        : lineEditState.initialVariantId;

      if (SETTINGS.designMode) {
        applyLineEditLocally(variantId, quantity);
        closeLineEditOverlay();
        setSuccess(SETTINGS.lineEditSavedText || 'Item updated.');
        return;
      }

      if (!contract || !contract.id || !lineEditState.lineId) {
        setLineEditMsg(SETTINGS.genericErrorText, false);
        return;
      }

      var payload = {
        subscriptionContractId: contract.id,
        lineId: lineEditState.lineId,
        quantity: quantity
      };
      if (variantId && normalizeVariantId(variantId) !== normalizeVariantId(lineEditState.initialVariantId)) {
        payload.variantId = 'gid://shopify/ProductVariant/' + normalizeVariantId(variantId);
      }

      setLineEditMsg(SETTINGS.lineEditSavingText || 'Saving…', true);
      if (btn) { btn.disabled = true; btn.classList.add('is-loading'); }

      fetch('/apps/customer/subscription_update_line.php', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (r) { return r.json().catch(function () { return {}; }).then(function (d) { return { ok: r.ok, data: d }; }); })
        .then(function (res) {
          if (!res.ok || res.data.error || res.data.success === false) {
            setLineEditMsg(res.data.message || SETTINGS.genericErrorText, false);
            return;
          }
          closeLineEditOverlay();
          setSuccess(SETTINGS.lineEditSavedText || 'Item updated.');
          return applyContractMutation(res);
        })
        .catch(function () {
          setLineEditMsg(SETTINGS.genericErrorText, false);
        })
        .finally(function () {
          if (btn) { btn.disabled = false; btn.classList.remove('is-loading'); }
        });
    });
  }

  /* ══════════ CANCELLATION FLOW ══════════ */
  /* Steps live in snippets/msv2-cancel-flow.liquid; one [data-cancel-step]
     is visible at a time. Selections are collected into cancelState and,
     for now, only printed to the console — submitCancellation and
     logCancelEvent are the single places to wire the backend later. */
  var cancelHost = root.querySelector('[data-msv2-cancel]');
  var cancelState = { reason: null, reasonLabel: '', step: 'reason' };

  function buildCancelPayload(outcome, extra) {
    var messageEl = cancelHost && cancelHost.querySelector('[data-cancel-message]');
    var payload = {
      subscriptionContractId: (contract && contract.id) || null,
      reason: cancelState.reason,
      reasonLabel: cancelState.reasonLabel,
      offerShown: cancelState.reason ? 'offer-' + cancelState.reason : null,
      message: messageEl ? messageEl.value.trim() : '',
      outcome: outcome,
      timestamp: new Date().toISOString()
    };
    return Object.assign(payload, extra || {});
  }

  /* Central logging point for every choice made inside the flow.
     TODO: also POST these events to the backend for retention analytics. */
  function logCancelEvent(outcome, extra) {
    console.log('[msv2 cancel]', buildCancelPayload(outcome, extra));
  }

  /* Final cancellation: POST cancel without deferCancellation so the
     contract is cancelled immediately in Shopify (same as the v1 portal). */
  function setCancelError(msg) {
    var errEl = cancelHost && cancelHost.querySelector('[data-cancel-error]');
    if (!errEl) return;
    if (msg) {
      errEl.textContent = msg;
      errEl.hidden = false;
    } else {
      errEl.textContent = '';
      errEl.hidden = true;
    }
  }

  function showCancelSuccess() {
    if (!cancelHost) return;
    showCancelStep('success');
  }

  function submitCancellation() {
    var payload = buildCancelPayload('cancelled');
    setCancelError('');

    if (SETTINGS.designMode) {
      showCancelSuccess();
      return;
    }

    if (!payload.subscriptionContractId) {
      setCancelError(SETTINGS.cancelErrorText || SETTINGS.genericErrorText);
      return;
    }

    var reason = payload.reasonLabel || payload.reason || '';
    if (payload.reason === 'something_else' && payload.message) {
      reason = reason ? reason + ': ' + payload.message : payload.message;
    }

    var btn = cancelHost && cancelHost.querySelector('[data-cancel-action="confirm-cancel"]');
    var originalText = btn ? btn.textContent : '';
    if (btn) {
      btn.disabled = true;
      btn.classList.add('is-loading');
      btn.textContent = SETTINGS.cancelSubmittingText || originalText;
    }

    fetch('/apps/customer/subscription_status.php', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        contractId: payload.subscriptionContractId,
        action: 'cancel',
        reason: reason,
        message: payload.message || ''
      })
    })
      .then(function (r) { return r.json().catch(function () { return {}; }).then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        if (!res.ok || res.data.ok !== true) {
          setCancelError(res.data.message || SETTINGS.cancelErrorText || SETTINGS.genericErrorText);
          return;
        }
        showCancelSuccess();
        reloadContracts();
      })
      .catch(function () {
        setCancelError(SETTINGS.cancelErrorText || SETTINGS.genericErrorText);
      })
      .finally(function () {
        if (btn) {
          btn.disabled = false;
          btn.classList.remove('is-loading');
          btn.textContent = originalText;
        }
      });
  }

  /* Revoke a pending cancellation request (success-step link or the
     account-view notice). The contract never left ACTIVE, so the backend
     just marks the request as revoked. */
  function submitReactivation(btn) {
    if (SETTINGS.designMode) {
      setSuccess(SETTINGS.cancelReactivatedText);
      closeCancelFlow('account');
      return;
    }
    var id = contract && contract.id;
    if (!id) return;
    if (btn) { btn.disabled = true; btn.classList.add('is-loading'); }
    fetch('/apps/customer/subscription_status.php', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ contractId: id, action: 'reactivate' })
    })
      .then(function (r) { return r.json().catch(function () { return {}; }).then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        if (!res.ok || res.data.ok !== true) {
          setError(res.data.message || SETTINGS.genericErrorText);
          return;
        }
        // Optimistically drop the pending state so the notice disappears
        // right away; the reload below just confirms it.
        if (contract) contract.pending_cancellation = null;
        renderPendingCancellation();
        setSuccess(SETTINGS.cancelReactivatedText);
        closeCancelFlow('account');
        reloadContracts();
      })
      .catch(function () {
        setError(SETTINGS.genericErrorText);
      })
      .finally(function () {
        if (btn) { btn.disabled = false; btn.classList.remove('is-loading'); }
      });
  }

  /* Noun phrase for "delivered every …" on the confirm step
     (e.g. "week", "2 weeks", "month"). */
  function cancelFrequencyText() {
    var p = contract && contract.billingPolicy;
    if (!p || !p.interval || !p.intervalCount) return 'delivery';
    var n = Number(p.intervalCount);
    var m = { DAY: ['day', 'days'], WEEK: ['week', 'weeks'], MONTH: ['month', 'months'], YEAR: ['year', 'years'] };
    var pair = m[p.interval] || ['delivery', 'deliveries'];
    return n === 1 ? pair[0] : n + ' ' + pair[1];
  }

  /* Continue is enabled once a reason is picked; "Something else" also
     requires the mandatory note to have content. */
  function updateCancelContinue() {
    var continueBtn = cancelHost && cancelHost.querySelector('[data-cancel-continue]');
    if (!continueBtn) return;
    var ok = !!cancelState.reason;
    if (cancelState.reason === 'something_else') {
      var messageEl = cancelHost.querySelector('[data-cancel-message]');
      ok = !!(messageEl && messageEl.value.trim());
    }
    continueBtn.disabled = !ok;
  }

  function showCancelStep(name) {
    if (!cancelHost) return;
    cancelState.step = name;
    cancelHost.querySelectorAll('[data-cancel-step]').forEach(function (step) {
      step.hidden = step.getAttribute('data-cancel-step') !== name;
    });
    // The something-else offer shows a read-only preview of the note
    // typed on the reason step.
    if (name === 'offer-something_else') {
      var previewEl = cancelHost.querySelector('[data-cancel-message-preview]');
      var messageEl = cancelHost.querySelector('[data-cancel-message]');
      if (previewEl) previewEl.textContent = messageEl ? messageEl.value.trim() : '';
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function openCancelFlow() {
    if (!cancelHost) return;
    cancelState = { reason: null, reasonLabel: '', step: 'reason' };
    cancelHost.querySelectorAll('[data-cancel-reason]').forEach(function (radio) { radio.checked = false; });
    var messageEl = cancelHost.querySelector('[data-cancel-message]');
    if (messageEl) messageEl.value = '';
    var noteEl = cancelHost.querySelector('[data-cancel-note]');
    if (noteEl) noteEl.hidden = true;
    var continueBtn = cancelHost.querySelector('[data-cancel-continue]');
    if (continueBtn) continueBtn.disabled = true;
    var freqEl = cancelHost.querySelector('[data-cancel-frequency]');
    if (freqEl) freqEl.textContent = cancelFrequencyText();
    setCancelError('');
    setCancelDiscountError('');
    showCancelStep('reason');
    setPortalView('cancel');
  }

  function closeCancelFlow(view) {
    setPortalView(view || 'account');
  }

  function cancelFlowBack() {
    if (cancelState.step === 'reason') {
      closeCancelFlow('account');
    } else if (cancelState.step === 'confirm' && cancelState.reason) {
      showCancelStep('offer-' + cancelState.reason);
    } else {
      showCancelStep('reason');
    }
  }

  function flashCopied(btn) {
    // innerHTML, not textContent: the button may contain an icon.
    var original = btn.innerHTML;
    btn.textContent = SETTINGS.cancelEmailCopiedText || 'Copied!';
    btn.classList.add('is-copied');
    setTimeout(function () {
      btn.innerHTML = original;
      btn.classList.remove('is-copied');
    }, 1600);
  }

  /* ── Okendo loyalty (v2) ── */
  var loyaltyState = {
    loading: false,
    data: null,
    referralUrl: ''
  };
  var loyaltyFetchGen = 0;

  function fmtPts(n) {
    var num = Number(n);
    return Number.isFinite(num) ? num.toLocaleString() : '—';
  }

  function loyaltySkeletonCardsHtml(count) {
    var n = count || 3;
    var html = '';
    var i;
    for (i = 0; i < n; i++) {
      html += '<div class="msv2-reward-card msv2-reward-card--sk" aria-hidden="true">' +
        '<div class="msv2-reward-img msv2-sk-box"></div>' +
        '<div class="msv2-reward-body">' +
          '<span class="msv2-sk msv2-sk--w120"></span>' +
          '<span class="msv2-sk msv2-sk--w160 msv2-sk--sm"></span>' +
          '<span class="msv2-sk msv2-sk--w80 msv2-sk--sm"></span>' +
          '<span class="msv2-sk msv2-sk--btn"></span>' +
        '</div></div>';
    }
    return html;
  }

  function setLoyaltyLoading(isLoading) {
    loyaltyState.loading = !!isLoading;
    root.querySelectorAll('[data-card="refer"], [data-card="loyalty-points"], [data-card="loyalty-rewards"]').forEach(function (el) {
      if (isLoading) el.setAttribute('data-loyalty-loading', 'true');
      else el.removeAttribute('data-loyalty-loading');
    });
    if (isLoading) {
      var host = root.querySelector('[data-rewards-host]');
      if (host) host.innerHTML = loyaltySkeletonCardsHtml(3);
      var ptsEl = root.querySelector('[data-loyalty-points]');
      if (ptsEl) ptsEl.innerHTML = '<span class="msv2-sk msv2-sk--w120 msv2-sk--lg"></span>';
      var memberEl = root.querySelector('[data-loyalty-member]');
      if (memberEl) memberEl.innerHTML = '<span class="msv2-sk msv2-sk--w80 msv2-sk--sm"></span>';
      var unlockEl = root.querySelector('[data-loyalty-unlock-text]');
      if (unlockEl) unlockEl.innerHTML = '<span class="msv2-sk msv2-sk--w160 msv2-sk--sm"></span>';
      var nextEl = root.querySelector('[data-loyalty-next-text]');
      if (nextEl) nextEl.innerHTML = '<span class="msv2-sk msv2-sk--w120 msv2-sk--sm"></span>';
      var bar = root.querySelector('[data-loyalty-progress]');
      if (bar) {
        bar.style.width = '40%';
        bar.classList.add('msv2-sk-box');
        bar.textContent = '';
      }
      var badgeText = root.querySelector('[data-rewards-points-text]') ||
        root.querySelector('[data-rewards-points-badge] span:last-child');
      if (badgeText) {
        badgeText.className = 'msv2-sk msv2-sk--w80 msv2-sk--sm';
        badgeText.setAttribute('data-rewards-points-text', '');
        badgeText.textContent = '';
      }
      var linkEl = root.querySelector('[data-refer-link]');
      if (linkEl) {
        linkEl.className = 'msv2-refer-link msv2-sk msv2-sk--w160';
        linkEl.textContent = '';
        linkEl.setAttribute('aria-hidden', 'true');
      }
      var copyBtn = root.querySelector('[data-action="copy-referral"]');
      if (copyBtn) {
        copyBtn.disabled = true;
        copyBtn.setAttribute('aria-busy', 'true');
      }
    }
  }

  function loyaltyPillHtml(status, badgeLabel) {
    var label = escapeHtml(badgeLabel || '');
    if (status === 'unlocked') {
      return '<span class="msv2-pill msv2-pill--success"><svg viewBox="0 0 12 12" fill="none" aria-hidden="true"><circle cx="6" cy="6" r="5.5" stroke="currentColor"/><path d="M3.8 6l1.6 1.6L8.5 4.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg><span>' + label + '</span></span>';
    }
    if (status === 'close') {
      return '<span class="msv2-pill msv2-pill--highlight"><svg viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M6 1l1.54 3.13 3.46.5-2.5 2.44.59 3.43L6 8.88 2.91 10.5l.59-3.43L1 4.63l3.46-.5L6 1z" fill="currentColor"/></svg><span>' + label + '</span></span>';
    }
    return '<span class="msv2-pill msv2-pill--muted"><svg viewBox="0 0 12 12" fill="none" aria-hidden="true"><rect x="2.5" y="5" width="7" height="5.5" rx="1" stroke="currentColor"/><path d="M4 5V3.8a2 2 0 0 1 4 0V5" stroke="currentColor"/></svg><span>' + label + '</span></span>';
  }

  function loyaltyCtaHtml(reward) {
    var cta = reward && reward.cta;
    if (cta === 'redeem') {
      return '<button type="button" class="msv2-btn msv2-btn--primary msv2-btn--block" data-action="reward-redeem" data-reward-id="' + escapeAttr(reward.id || '') + '">' + escapeHtml(SETTINGS.rewardRedeemLabel || 'Redeem') + '</button>';
    }
    if (cta === 'add_to_box') {
      return '<button type="button" class="msv2-btn msv2-btn--primary msv2-btn--block" data-action="reward-add-to-box" data-reward-id="' + escapeAttr(reward.id || '') + '" data-discount-code="' + escapeAttr(reward.discount_code || '') + '">' + escapeHtml(SETTINGS.rewardAddToBoxLabel || 'Add to my next box') + '</button>';
    }
    if (reward && reward.already_on_box) {
      return '<button type="button" class="msv2-btn msv2-btn--disabled msv2-btn--block" disabled>' + escapeHtml(SETTINGS.rewardAddedLabel || 'Added to next box') + '</button>';
    }
    if (reward && reward.already_used) {
      return '<button type="button" class="msv2-btn msv2-btn--disabled msv2-btn--block" disabled>' + escapeHtml(SETTINGS.rewardUsedLabel || 'Reward already used') + '</button>';
    }
    return '<button type="button" class="msv2-btn msv2-btn--disabled msv2-btn--block" disabled>' + escapeHtml(SETTINGS.rewardLockedLabel || 'Not yet unlocked') + '</button>';
  }

  function renderRewardCardHtml(reward) {
    var status = reward.status || 'locked';
    var img = reward.imageUrl
      ? '<img src="' + escapeAttr(reward.imageUrl) + '" alt="' + escapeAttr(reward.title || '') + '" loading="lazy">'
      : '';
    var flag = '';
    if (status === 'unlocked') {
      flag = '<span class="msv2-reward-flag msv2-reward-flag--unlocked" aria-hidden="true"><svg viewBox="0 0 19 19" fill="none"><circle cx="9.5" cy="9.5" r="8.5" fill="currentColor"/><path d="M6 9.5l2.4 2.4L13 7.5" stroke="#fff" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg></span>';
    } else if (status === 'locked') {
      flag = '<span class="msv2-reward-lockwrap" aria-hidden="true"><svg viewBox="0 0 30 30" fill="none"><rect x="7" y="13" width="16" height="12" rx="2.5" stroke="#6b7280" stroke-width="1.8"/><path d="M10.5 13V9.5a4.5 4.5 0 0 1 9 0V13" stroke="#6b7280" stroke-width="1.8"/></svg></span>';
    }
    var progress = '';
    if (status !== 'unlocked') {
      var ptsNow = (loyaltyState.data && loyaltyState.data.customer && loyaltyState.data.customer.total_points) || 0;
      progress = '<div class="msv2-reward-progress"><div class="msv2-reward-pts"><span>' + escapeHtml(fmtPts(ptsNow)) + ' pts</span><span>' + escapeHtml(fmtPts(reward.points_cost)) + '</span></div><div class="msv2-progress"><div class="msv2-progress-bar' + (status === 'locked' ? ' msv2-progress-bar--accent' : '') + '" style="width: ' + Math.max(0, Math.min(100, Number(reward.progress_percent) || 0)) + '%;"></div></div></div>';
    }
    return '<div class="msv2-reward-card" data-reward-status="' + escapeAttr(status) + '" data-reward-id="' + escapeAttr(reward.id || '') + '">' +
      '<div class="msv2-reward-img">' + img + flag + '</div>' +
      '<div class="msv2-reward-body">' +
        '<p class="msv2-reward-title">' + escapeHtml(reward.title || '') + '</p>' +
        '<p class="msv2-reward-sub">' + escapeHtml(reward.description || '') + '</p>' +
        progress +
        loyaltyPillHtml(status, reward.badge_label) +
        loyaltyCtaHtml(reward) +
      '</div></div>';
  }

  function renderStandaloneCouponCard(coupon) {
    var reward = {
      id: 'coupon:' + (coupon.code || ''),
      title: coupon.title || coupon.code || 'Reward',
      description: coupon.already_used ? 'This reward has already been used.' : 'Add this reward to your next box.',
      status: 'unlocked',
      badge_label: coupon.already_used ? (SETTINGS.rewardUsedLabel || 'Reward already used') : 'Ready for your box',
      cta: (coupon.already_on_box || coupon.already_used) ? 'none' : 'add_to_box',
      discount_code: coupon.code || '',
      already_on_box: !!coupon.already_on_box,
      already_used: !!coupon.already_used,
      points_cost: 0,
      progress_percent: 100
    };
    return renderRewardCardHtml(reward);
  }

  function applyLoyaltySidebar(customer) {
    if (!customer) return;
    var ptsEl = root.querySelector('[data-loyalty-points]');
    if (ptsEl) ptsEl.textContent = fmtPts(customer.total_points);
    var memberEl = root.querySelector('[data-loyalty-member]');
    if (memberEl) memberEl.textContent = customer.member_since_label || '';
    var unlockEl = root.querySelector('[data-loyalty-unlock-text]');
    if (unlockEl) unlockEl.textContent = customer.unlock_text || '';
    var nextEl = root.querySelector('[data-loyalty-next-text]');
    if (nextEl) nextEl.textContent = customer.next_text || '';
    var bar = root.querySelector('[data-loyalty-progress]');
    if (bar) {
      bar.classList.remove('msv2-sk-box');
      bar.style.width = Math.max(0, Math.min(100, Number(customer.progress_percent) || 0)) + '%';
    }
    var badgeText = root.querySelector('[data-rewards-points-text]');
    if (!badgeText) {
      var badgeHost = root.querySelector('[data-rewards-points-badge]');
      if (badgeHost) {
        badgeText = document.createElement('span');
        badgeText.setAttribute('data-rewards-points-text', '');
        badgeHost.appendChild(badgeText);
      }
    }
    if (badgeText) {
      badgeText.className = '';
      badgeText.textContent = fmtPts(customer.total_points) + ' pts';
    }

    var cancelPts = root.querySelector('[data-cancel-lose-points]');
    if (cancelPts) {
      var tpl = cancelPts.getAttribute('data-template') || '';
      if (tpl.indexOf('__PTS__') !== -1) {
        cancelPts.textContent = tpl.split('__PTS__').join(fmtPts(customer.total_points));
      } else {
        cancelPts.textContent = tpl.replace(/—|\d[\d,]*/g, fmtPts(customer.total_points));
      }
    }
  }

  function renderLoyaltyRewards(data) {
    var host = root.querySelector('[data-rewards-host]');
    if (!host) return;
    var rewards = Array.isArray(data && data.rewards) ? data.rewards : [];
    var standalone = Array.isArray(data && data.standalone_coupons) ? data.standalone_coupons : [];
    var html = rewards.map(renderRewardCardHtml).join('');
    html += standalone.map(renderStandaloneCouponCard).join('');
    if (!html) {
      host.innerHTML = '<p class="msv2-card-sub">' + escapeHtml(SETTINGS.rewardEmptyText || 'Rewards will appear here soon.') + '</p>';
      return;
    }
    host.innerHTML = html;
  }

  function applyReferral(url) {
    loyaltyState.referralUrl = url || '';
    var linkEl = root.querySelector('[data-refer-link]');
    var copyBtn = root.querySelector('[data-action="copy-referral"]');
    if (linkEl) {
      linkEl.className = 'msv2-refer-link';
      linkEl.removeAttribute('aria-hidden');
      linkEl.textContent = loyaltyState.referralUrl || (SETTINGS.referLinkPlaceholder || 'Your referral link');
    }
    if (copyBtn) {
      copyBtn.disabled = !loyaltyState.referralUrl;
      copyBtn.removeAttribute('aria-busy');
    }
  }

  function showLoyaltyUnavailable() {
    setLoyaltyLoading(false);
    var host = root.querySelector('[data-rewards-host]');
    if (host) {
      host.innerHTML = '<p class="msv2-card-sub">' + escapeHtml(SETTINGS.rewardEmptyText || 'Rewards will appear here soon.') + '</p>';
    }
    var ptsEl = root.querySelector('[data-loyalty-points]');
    if (ptsEl) ptsEl.textContent = '—';
    var memberEl = root.querySelector('[data-loyalty-member]');
    if (memberEl) memberEl.textContent = '';
    var unlockEl = root.querySelector('[data-loyalty-unlock-text]');
    if (unlockEl) unlockEl.textContent = '';
    var nextEl = root.querySelector('[data-loyalty-next-text]');
    if (nextEl) nextEl.textContent = '';
    var bar = root.querySelector('[data-loyalty-progress]');
    if (bar) {
      bar.classList.remove('msv2-sk-box');
      bar.style.width = '0%';
    }
    var badgeText = root.querySelector('[data-rewards-points-text]');
    if (badgeText) {
      badgeText.className = '';
      badgeText.textContent = '— pts';
    }
    applyReferral('');
  }

  function loadLoyalty() {
    if (SETTINGS.designMode) {
      // Theme editor: keep skeletons — never inject demo points/rewards.
      setLoyaltyLoading(true);
      return Promise.resolve();
    }
    if (!contract || !contract.id) return Promise.resolve();
    var gen = ++loyaltyFetchGen;
    setLoyaltyLoading(true);
    var url = '/apps/customer/okendo_loyalty_v2.php?subscriptionContractId=' + encodeURIComponent(contract.id);
    return fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json().then(function (j) { return { ok: res.ok, json: j }; }); })
      .then(function (r) {
        if (gen !== loyaltyFetchGen) return;
        if (!r.json || r.json.success !== true || !r.json.data) {
          showLoyaltyUnavailable();
          return;
        }
        loyaltyState.data = r.json.data;
        applyLoyaltySidebar(r.json.data.customer);
        renderLoyaltyRewards(r.json.data);
        applyReferral(r.json.data.referral && r.json.data.referral.url);
        setLoyaltyLoading(false);
      })
      .catch(function () {
        if (gen !== loyaltyFetchGen) return;
        showLoyaltyUnavailable();
      });
  }

  function setRewardButtonBusy(btn, busyLabel) {
    if (!btn) return;
    btn.disabled = true;
    btn.dataset.originalLabel = btn.textContent || '';
    btn.textContent = busyLabel || '…';
  }
  function clearRewardButtonBusy(btn, label) {
    if (!btn) return;
    btn.disabled = false;
    btn.textContent = label || btn.dataset.originalLabel || '';
  }

  function flipCardToAddToBox(card, discountCode, rewardId) {
    if (!card) return;
    card.setAttribute('data-reward-status', 'unlocked');
    var body = card.querySelector('.msv2-reward-body');
    if (!body) return;
    var oldBtn = body.querySelector('[data-action="reward-redeem"], [data-action="reward-add-to-box"]');
    var html = '<button type="button" class="msv2-btn msv2-btn--primary msv2-btn--block" data-action="reward-add-to-box" data-reward-id="' +
      escapeAttr(rewardId || card.getAttribute('data-reward-id') || '') +
      '" data-discount-code="' + escapeAttr(discountCode || '') + '">' +
      escapeHtml(SETTINGS.rewardAddToBoxLabel || 'Add to my next box') + '</button>';
    if (oldBtn) {
      oldBtn.outerHTML = html;
    } else {
      body.insertAdjacentHTML('beforeend', html);
    }
    var pill = body.querySelector('.msv2-pill span:last-child');
    if (pill) pill.textContent = 'Ready for your box';
  }

  function redeemReward(btn) {
    var rewardId = btn.getAttribute('data-reward-id');
    if (!rewardId) return;
    setRewardButtonBusy(btn, SETTINGS.rewardRedeemingText || 'Redeeming…');
    fetch('/apps/customer/okendo_redeem.php', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ redemptionRuleId: rewardId })
    })
      .then(function (res) { return res.json().then(function (j) { return { ok: res.ok, json: j }; }); })
      .then(function (r) {
        var j = r.json || {};
        if (!j.success || !j.discountCode) {
          clearRewardButtonBusy(btn, SETTINGS.rewardRedeemLabel || 'Redeem');
          setError(j.message || SETTINGS.rewardRedeemErrorText || 'Could not redeem this reward.');
          return;
        }
        if (typeof j.points_balance === 'number' && loyaltyState.data && loyaltyState.data.customer) {
          loyaltyState.data.customer.total_points = j.points_balance;
          applyLoyaltySidebar(loyaltyState.data.customer);
        }
        var card = btn.closest('.msv2-reward-card');
        flipCardToAddToBox(card, j.discountCode, rewardId);
      })
      .catch(function () {
        clearRewardButtonBusy(btn, SETTINGS.rewardRedeemLabel || 'Redeem');
        setError(SETTINGS.rewardRedeemErrorText || 'Could not redeem this reward.');
      });
  }

  function addRewardToBox(btn) {
    var code = (btn.getAttribute('data-discount-code') || '').trim();
    if (!code || !contract || !contract.id) {
      showRewardAddError(SETTINGS.rewardAddErrorText || 'Could not add this reward to your next box.');
      return;
    }
    setRewardButtonBusy(btn, SETTINGS.rewardAddingText || 'Adding…');
    // lockContract:false → contract-level apply (recurring line + code, no cycle lock).
    // Default API behaviour still locks for v1 callers that omit the flag.
    fetch('/apps/customer/add_one_time_reward.php', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        subscriptionContractId: contract.id,
        discountCode: code,
        quantity: 1,
        lockContract: false
      })
    })
      .then(function (res) { return res.json().then(function (j) { return { ok: res.ok, json: j }; }); })
      .then(function (r) {
        var j = r.json || {};
        if (!j.success) {
          clearRewardButtonBusy(btn, SETTINGS.rewardAddToBoxLabel || 'Add to my next box');
          showRewardAddError(
            j.message || SETTINGS.rewardAddErrorText || 'Could not add this reward to your next box.',
            j.code
          );
          return;
        }
        clearError();
        btn.textContent = SETTINGS.rewardAddedLabel || 'Added to next box';
        btn.disabled = true;
        btn.classList.remove('msv2-btn--primary');
        btn.classList.add('msv2-btn--disabled');
        return reloadContracts().then(function () { return loadLoyalty(); });
      })
      .catch(function () {
        clearRewardButtonBusy(btn, SETTINGS.rewardAddToBoxLabel || 'Add to my next box');
        showRewardAddError(SETTINGS.rewardAddErrorText || 'Could not add this reward to your next box.');
      });
  }

  /* ── Reward add error popup (not the header banner) ── */
  var rewardErrorOverlay = document.getElementById('msv2-reward-error-overlay');

  function showRewardAddError(msg, code) {
    var text = String(msg || SETTINGS.rewardAddErrorText || 'Could not add this reward to your next box.').trim();
    if (!rewardErrorOverlay) {
      setError(text);
      return;
    }
    var textEl = rewardErrorOverlay.querySelector('[data-reward-error-text]');
    if (textEl) textEl.textContent = text;
    var titleEl = rewardErrorOverlay.querySelector('#msv2-reward-error-title');
    if (titleEl) {
      var oosTitle = code === 'REWARD_OUT_OF_STOCK'
        ? (SETTINGS.shopOosLabel || SETTINGS.outOfStockLabel || 'Out of stock')
        : '';
      titleEl.textContent = oosTitle || SETTINGS.rewardAddErrorTitle || titleEl.textContent;
    }
    rewardErrorOverlay.classList.add('is-open');
    rewardErrorOverlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('msv2-no-scroll');
  }

  function closeRewardAddError() {
    if (!rewardErrorOverlay) return;
    rewardErrorOverlay.classList.remove('is-open');
    rewardErrorOverlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('msv2-no-scroll');
  }

  function wireRewardErrorOverlay() {
    if (!rewardErrorOverlay) return;
    rewardErrorOverlay.addEventListener('click', function (e) {
      if (e.target.closest('[data-reward-error-close]')) closeRewardAddError();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && rewardErrorOverlay.classList.contains('is-open')) closeRewardAddError();
    });
  }

  function copyReferralLink(btn) {
    var url = loyaltyState.referralUrl || '';
    if (!url) {
      setError(SETTINGS.genericErrorText || 'Referral link is not available yet.');
      return;
    }
    var labelEl = btn.querySelector('span:last-child') || btn;
    var original = labelEl.textContent;
    var done = function () {
      labelEl.textContent = SETTINGS.referCopiedText || 'Copied';
      setTimeout(function () { labelEl.textContent = original; }, 1400);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(done).catch(done);
    } else {
      var ta = document.createElement('textarea');
      ta.value = url;
      ta.style.cssText = 'position:fixed;opacity:0;';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (e) {}
      document.body.removeChild(ta);
      done();
    }
  }

  function copySupportEmail(btn) {
    var email = SETTINGS.supportEmail || '';
    if (!email) return;
    var done = function () { flashCopied(btn); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(email).then(done).catch(done);
    } else {
      done();
    }
  }

  /* Retention offer for "too expensive": same endpoint + payload as the
     v1 portal's accept-offer button. On success, leave the cancel flow
     and return to the next-order tab so the updated discount is visible. */
  function setCancelDiscountError(msg) {
    var el = cancelHost && cancelHost.querySelector('[data-cancel-discount-error]');
    if (!el) return;
    if (msg) {
      el.textContent = msg;
      el.hidden = false;
    } else {
      el.textContent = '';
      el.hidden = true;
    }
  }

  function applyCancelRetentionDiscount(btn) {
    logCancelEvent('offer-accepted', { offerAction: 'apply-discount' });
    setCancelDiscountError('');

    if (SETTINGS.designMode) {
      setSuccess(SETTINGS.cancelDiscountSuccessText || 'Discount applied to your next subscription order.');
      closeCancelFlow('next-order');
      return;
    }

    var contractId = contract && contract.id;
    if (!contractId) {
      setCancelDiscountError(SETTINGS.cancelDiscountErrorText || SETTINGS.genericErrorText);
      return;
    }

    var originalText = btn ? btn.textContent : '';
    if (btn) {
      btn.disabled = true;
      btn.classList.add('is-loading');
      btn.textContent = SETTINGS.cancelDiscountApplyingText || 'Applying discount…';
    }

    fetch('/apps/customer/subscription_apply_discount.php', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        subscriptionContractId: contractId,
        reason: cancelState.reason || 'too_expensive',
        discount: {
          type: 'percentage',
          value: 20,
          title: '20% off next order (max $20)'
        }
      })
    })
      .then(function (r) { return r.json().catch(function () { return {}; }).then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        if (!res.ok || res.data.error) {
          setCancelDiscountError(res.data.message || SETTINGS.cancelDiscountErrorText || SETTINGS.genericErrorText);
          return;
        }
        setSuccess(SETTINGS.cancelDiscountSuccessText || 'Discount applied to your next subscription order.');
        closeCancelFlow('next-order');
        return reloadContracts();
      })
      .catch(function () {
        setCancelDiscountError(SETTINGS.cancelDiscountErrorText || SETTINGS.genericErrorText);
      })
      .finally(function () {
        if (btn) {
          btn.disabled = false;
          btn.classList.remove('is-loading');
          btn.textContent = originalText || SETTINGS.cancelDiscountCtaText || 'Apply 20% discount';
        }
      });
  }

  function handleCancelAction(btn, e) {
    var action = btn.getAttribute('data-cancel-action');
    switch (action) {
      case 'keep':
        logCancelEvent('kept');
        closeCancelFlow('account');
        break;
      case 'still-cancel':
        logCancelEvent('offer-declined');
        showCancelStep('confirm');
        break;
      case 'apply-discount':
        applyCancelRetentionDiscount(btn);
        break;
      case 'copy-email':
        logCancelEvent('offer-accepted', { offerAction: 'copy-email' });
        copySupportEmail(btn);
        break;
      case 'reschedule':
        logCancelEvent('offer-accepted', { offerAction: 'reschedule' });
        openReschedOverlay();
        break;
      case 'view-meals':
        // Anchor: log the choice and let the navigation happen.
        logCancelEvent('offer-accepted', { offerAction: 'view-meals', url: btn.getAttribute('href') });
        break;
      case 'confirm-cancel':
        submitCancellation();
        break;
      case 'back-to-account':
        closeCancelFlow('account');
        break;
      case 'reactivate':
        logCancelEvent('reactivated');
        submitReactivation(btn);
        break;
    }
  }

  /* ── delegated clicks ── */
  root.addEventListener('click', function (e) {
    // Require data-msv2-tab so Logout (also styled as .msv2-tab) is not treated as a view switch.
    var tabBtn = e.target.closest('.msv2-tab[data-msv2-tab]');
    if (tabBtn && !tabBtn.hasAttribute('data-placeholder')) {
      setPortalView(tabBtn.getAttribute('data-msv2-tab'));
      return;
    }
    var backBtn = e.target.closest('[data-account-back]');
    if (backBtn) { setPortalView('next-order'); return; }
    var profileBtn = e.target.closest('[data-action="edit-profile"]');
    if (profileBtn) { openProfileOverlay(); return; }
    var cancelOpenBtn = e.target.closest('[data-cancel-open]');
    if (cancelOpenBtn) { openCancelFlow(); return; }
    var cancelBackBtn = e.target.closest('[data-cancel-back]');
    if (cancelBackBtn) { cancelFlowBack(); return; }
    var cancelContinueBtn = e.target.closest('[data-cancel-continue]');
    if (cancelContinueBtn) {
      if (!cancelContinueBtn.disabled && cancelState.reason) {
        logCancelEvent('reason-selected');
        showCancelStep('offer-' + cancelState.reason);
      }
      return;
    }
    var cancelActionBtn = e.target.closest('[data-cancel-action]');
    if (cancelActionBtn) { handleCancelAction(cancelActionBtn, e); return; }
    var pageBtn = e.target.closest('[data-lines-page]');
    if (pageBtn && !pageBtn.disabled) { goToLinesPage(pageBtn.getAttribute('data-lines-page')); return; }
    var mealBtn = e.target.closest('[data-action="meal-add"]');
    if (mealBtn) { addMealToBox(mealBtn); return; }
    var editBoxBtn = e.target.closest('[data-action="edit-box"]');
    if (editBoxBtn) { openShopOverlay(); return; }
    var renewalCta = e.target.closest('[data-renewal-failure-cta]');
    if (renewalCta) { handleRenewalFailureCta(renewalCta.getAttribute('data-renewal-failure-cta')); return; }
    var lineEditBtn = e.target.closest('[data-action="line-edit"]');
    if (lineEditBtn) {
      var lineEl = lineEditBtn.closest('[data-line-id]');
      openLineEditForLineId(lineEl && lineEl.getAttribute('data-line-id'));
      return;
    }
    var lineRemoveBtn = e.target.closest('[data-action="line-remove"]');
    if (lineRemoveBtn) {
      var removeLineEl = lineRemoveBtn.closest('[data-line-id]');
      removeLineFromContract(removeLineEl && removeLineEl.getAttribute('data-line-id'));
      return;
    }
    var reschedBtn = e.target.closest('[data-action="reschedule"]');
    if (reschedBtn) { openReschedOverlay(); return; }
    var logoutBtn = e.target.closest('[data-action="logout"]');
    if (logoutBtn) { openLogoutOverlay(); return; }
    var pauseOpenBtn = e.target.closest('[data-action="pause-open"]');
    if (pauseOpenBtn) { openPauseOverlay(); return; }
    var resumeOpenBtn = e.target.closest('[data-action="resume-open"]');
    if (resumeOpenBtn) { openResumeOverlay(); return; }
    var sendNowBtn = e.target.closest('[data-action="send-now"]');
    if (sendNowBtn) { requestSendNow(); return; }
    var redeemBtn = e.target.closest('[data-action="reward-redeem"]');
    if (redeemBtn) { e.preventDefault(); redeemReward(redeemBtn); return; }
    var addBoxBtn = e.target.closest('[data-action="reward-add-to-box"]');
    if (addBoxBtn) { e.preventDefault(); addRewardToBox(addBoxBtn); return; }
    var copyRefBtn = e.target.closest('[data-action="copy-referral"]');
    if (copyRefBtn) { e.preventDefault(); copyReferralLink(copyRefBtn); return; }
    var orderRow = e.target.closest('[data-order-row]');
    if (orderRow) { openOrderModal(orderRow); return; }
    var placeholder = e.target.closest('[data-placeholder]');
    if (placeholder) { e.preventDefault(); }
  });

  /* Reason radios record the choice, reveal the mandatory note for
     "Something else" and gate the Continue button. */
  root.addEventListener('change', function (e) {
    if (!e.target.matches('[data-cancel-reason]')) return;
    cancelState.reason = e.target.value;
    cancelState.reasonLabel = e.target.getAttribute('data-label') || '';
    var noteEl = cancelHost && cancelHost.querySelector('[data-cancel-note]');
    var isElse = cancelState.reason === 'something_else';
    if (noteEl) {
      noteEl.hidden = !isElse;
      if (isElse) {
        var messageEl = noteEl.querySelector('[data-cancel-message]');
        if (messageEl) messageEl.focus();
      }
    }
    updateCancelContinue();
  });

  /* The mandatory note gates Continue while "Something else" is selected. */
  root.addEventListener('input', function (e) {
    if (e.target.matches('[data-cancel-message]')) updateCancelContinue();
  });

  /* ══════════ LOGOUT CONFIRM ══════════ */
  var logoutOverlay = document.getElementById('msv2-logout-overlay');

  function openLogoutOverlay() {
    if (!logoutOverlay) return;
    logoutOverlay.classList.add('is-open');
    logoutOverlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('msv2-no-scroll');
  }
  function closeLogoutOverlay() {
    if (!logoutOverlay) return;
    logoutOverlay.classList.remove('is-open');
    logoutOverlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('msv2-no-scroll');
  }
  function wireLogoutOverlay() {
    if (!logoutOverlay) return;
    logoutOverlay.addEventListener('click', function (e) {
      if (e.target.closest('[data-logout-close]')) closeLogoutOverlay();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && logoutOverlay.classList.contains('is-open')) closeLogoutOverlay();
    });
  }

  /* ══════════ PAUSE / RESUME SUBSCRIPTION ══════════
     Pausing (subscriptionContractPause) stops billing and boxes until the
     customer resumes; resuming reuses the same activate mutation as the
     cancellation-flow reactivate. Both hit subscription_status.php. */
  var pauseOverlay = document.getElementById('msv2-pause-overlay');
  var pauseState = { pausing: false };
  var pauseConfirmDefault = '';

  /* Show the pause card only for ACTIVE/PAUSED contracts with no pending
     cancellation, and reveal the variant that matches the current status. */
  function renderPauseControls() {
    var card = root.querySelector('[data-pause-card]');
    if (!card) return;
    var activeView = card.querySelector('[data-pause-active]');
    var pausedView = card.querySelector('[data-pause-paused]');
    var status = contract ? String(contract.status || '').toUpperCase() : '';
    var pending = !!(contract && contract.pending_cancellation);
    var isActive = status === 'ACTIVE';
    var isPaused = status === 'PAUSED';
    var show = !pending && (isActive || isPaused);
    card.hidden = !show;
    if (activeView) activeView.hidden = !(show && isActive);
    if (pausedView) pausedView.hidden = !(show && isPaused);
  }

  function setPauseMsg(msg) {
    var el = pauseOverlay && pauseOverlay.querySelector('[data-pause-msg]');
    if (!el) return;
    if (msg) { el.textContent = msg; el.hidden = false; }
    else { el.textContent = ''; el.hidden = true; }
  }

  function setPauseLoading(on) {
    pauseState.pausing = !!on;
    var btn = pauseOverlay && pauseOverlay.querySelector('[data-pause-confirm]');
    if (!btn) return;
    btn.disabled = !!on;
    btn.classList.toggle('is-loading', !!on);
    btn.textContent = on ? (SETTINGS.pausingText || 'Pausing…') : (pauseConfirmDefault || btn.textContent);
  }

  function openPauseOverlay() {
    if (!pauseOverlay || !contract) return;
    setPauseMsg('');
    setPauseLoading(false);
    pauseOverlay.classList.add('is-open');
    pauseOverlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('msv2-no-scroll');
  }

  function closePauseOverlay() {
    if (!pauseOverlay || pauseState.pausing) return;
    pauseOverlay.classList.remove('is-open');
    pauseOverlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('msv2-no-scroll');
    setPauseMsg('');
  }

  function confirmPause() {
    if (!contract || !contract.id) return;
    if (SETTINGS.designMode) {
      closePauseOverlay();
      setSuccess(SETTINGS.pauseSuccessText || 'Your subscription has been paused.');
      return;
    }
    setPauseMsg('');
    setPauseLoading(true);
    fetch('/apps/customer/subscription_status.php', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ contractId: contract.id, action: 'pause' })
    })
      .then(function (r) { return r.json().catch(function () { return {}; }).then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        if (!res.ok || res.data.ok !== true) {
          setPauseLoading(false);
          setPauseMsg(res.data.message || SETTINGS.pauseErrorText || SETTINGS.genericErrorText);
          return;
        }
        pauseState.pausing = false;
        pauseOverlay.classList.remove('is-open');
        pauseOverlay.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('msv2-no-scroll');
        setSuccess(SETTINGS.pauseSuccessText || 'Your subscription has been paused.');
        reloadContracts();
      })
      .catch(function () {
        setPauseLoading(false);
        setPauseMsg(SETTINGS.pauseErrorText || SETTINGS.genericErrorText);
      });
  }

  /* Resume (reactivate) is gated behind a confirmation modal so the customer
     explicitly approves before we activate the contract. On success the
     server re-syncs the next billing date forward; we surface that date
     (next_billing_sync.target_date) in the success banner. */
  var resumeOverlay = document.getElementById('msv2-resume-overlay');
  var resumeState = { resuming: false };
  var resumeConfirmDefault = '';

  function setResumeMsg(msg) {
    var el = resumeOverlay && resumeOverlay.querySelector('[data-resume-msg]');
    if (!el) return;
    if (msg) { el.textContent = msg; el.hidden = false; }
    else { el.textContent = ''; el.hidden = true; }
  }

  function setResumeLoading(on) {
    resumeState.resuming = !!on;
    var btn = resumeOverlay && resumeOverlay.querySelector('[data-resume-confirm]');
    if (!btn) return;
    btn.disabled = !!on;
    btn.classList.toggle('is-loading', !!on);
    btn.textContent = on ? (SETTINGS.resumingText || 'Resuming…') : (resumeConfirmDefault || btn.textContent);
  }

  function openResumeOverlay() {
    if (!resumeOverlay || !contract) return;
    setResumeMsg('');
    setResumeLoading(false);
    resumeOverlay.classList.add('is-open');
    resumeOverlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('msv2-no-scroll');
  }

  function closeResumeOverlay() {
    if (!resumeOverlay || resumeState.resuming) return;
    resumeOverlay.classList.remove('is-open');
    resumeOverlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('msv2-no-scroll');
    setResumeMsg('');
  }

  function confirmResume() {
    if (!contract || !contract.id) return;
    if (SETTINGS.designMode) {
      resumeState.resuming = false;
      resumeOverlay.classList.remove('is-open');
      resumeOverlay.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('msv2-no-scroll');
      setSuccess(SETTINGS.resumeSuccessText || 'Your subscription is active again.');
      return;
    }
    setResumeMsg('');
    setResumeLoading(true);
    fetch('/apps/customer/subscription_status.php', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ contractId: contract.id, action: 'activate' })
    })
      .then(function (r) { return r.json().catch(function () { return {}; }).then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        if (!res.ok || res.data.ok !== true) {
          setResumeLoading(false);
          setResumeMsg(res.data.message || SETTINGS.resumeErrorText || SETTINGS.genericErrorText);
          return;
        }
        resumeState.resuming = false;
        resumeOverlay.classList.remove('is-open');
        resumeOverlay.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('msv2-no-scroll');
        // Surface the re-synced next billing date when the server returns one.
        var sync = res.data.next_billing_sync;
        var dateIso = sync && sync.target_date;
        var msg = SETTINGS.resumeSuccessText || 'Your subscription is active again.';
        if (dateIso && SETTINGS.resumeSuccessDated) {
          msg = SETTINGS.resumeSuccessDated.replace(/\{\{\s*date\s*\}\}/g, formatDate(dateIso));
        }
        setSuccess(msg);
        reloadContracts();
      })
      .catch(function () {
        setResumeLoading(false);
        setResumeMsg(SETTINGS.resumeErrorText || SETTINGS.genericErrorText);
      });
  }

  function wireResumeOverlay() {
    if (!resumeOverlay) return;
    var confirmBtn = resumeOverlay.querySelector('[data-resume-confirm]');
    resumeConfirmDefault = confirmBtn ? confirmBtn.textContent : '';
    resumeOverlay.addEventListener('click', function (e) {
      if (e.target.closest('[data-resume-close]')) { closeResumeOverlay(); return; }
      var c = e.target.closest('[data-resume-confirm]');
      if (c && !c.disabled) confirmResume();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && resumeOverlay.classList.contains('is-open')) closeResumeOverlay();
    });
  }

  function wirePauseOverlay() {
    if (!pauseOverlay) return;
    var confirmBtn = pauseOverlay.querySelector('[data-pause-confirm]');
    pauseConfirmDefault = confirmBtn ? confirmBtn.textContent : '';
    pauseOverlay.addEventListener('click', function (e) {
      if (e.target.closest('[data-pause-close]')) { closePauseOverlay(); return; }
      var c = e.target.closest('[data-pause-confirm]');
      if (c && !c.disabled) confirmPause();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && pauseOverlay.classList.contains('is-open')) closePauseOverlay();
    });
  }

  /* ══════════ CHANGE DELIVERY FREQUENCY ══════════
     Anchor-based cadence change (subscription_change_frequency.php): the
     customer picks weekly / fortnightly / monthly and the server anchors the
     schedule on tomorrow in shop time, then lets Shopify recalculate every
     billing cycle from that anchor.

     Selecting an option asks the endpoint for a preview (no Shopify writes) so
     the confirmation sentence quotes a real date. The commit response carries
     the date Shopify actually committed, which can differ from the previewed
     one for fortnightly when Shopify snaps to its own fortnight grid — hence
     billing_moved and the separate success wording. */
  var frequencyOverlay = document.getElementById('msv2-frequency-overlay');
  var frequencyState = { selected: null, previewGen: 0, checking: false, saving: false };
  var frequencyConfirmDefault = '';

  var FREQUENCY_CADENCES = {
    weekly: { interval: 'WEEK', count: 1 },
    fortnightly: { interval: 'WEEK', count: 2 },
    monthly: { interval: 'MONTH', count: 1 }
  };

  /* Mirrors the server's cadence comparison so the "Current" badge still lands
     on Monthly when a contract expresses it as every 30 days. */
  function frequencyCadenceDays(interval, count) {
    var n = Math.max(1, Number(count) || 1);
    if (interval === 'DAY') return n;
    if (interval === 'WEEK') return n * 7;
    if (interval === 'MONTH') return n * 30;
    if (interval === 'YEAR') return n * 365;
    return null;
  }

  function currentFrequencyKey() {
    var policy = contract && contract.billingPolicy;
    if (!policy || !policy.interval) return null;
    var interval = String(policy.interval).toUpperCase();
    var count = Math.max(1, Number(policy.intervalCount) || 1);
    var keys = Object.keys(FREQUENCY_CADENCES);
    for (var i = 0; i < keys.length; i++) {
      var target = FREQUENCY_CADENCES[keys[i]];
      if (interval === target.interval && count === target.count) return keys[i];
      var a = frequencyCadenceDays(interval, count);
      var b = frequencyCadenceDays(target.interval, target.count);
      if (a !== null && b !== null && a === b) return keys[i];
    }
    return null;
  }

  /* Locale strings use [token] placeholders, matching the promo/remove copy. */
  function fillTokens(template, values) {
    var out = String(template || '');
    Object.keys(values).forEach(function (key) {
      out = out.split('[' + key + ']').join(values[key]);
    });
    return out;
  }

  function ordinalDay(day) {
    var n = Number(day) || 0;
    var teens = n % 100;
    if (teens >= 11 && teens <= 13) return n + 'th';
    if (n % 10 === 1) return n + 'st';
    if (n % 10 === 2) return n + 'nd';
    if (n % 10 === 3) return n + 'rd';
    return n + 'th';
  }

  /* Only ACTIVE / PAUSED contracts can have their policies updated. */
  function updateFrequencyButton() {
    var buttons = root.querySelectorAll('[data-action="change-frequency"]');
    if (!buttons.length) return;
    var status = contract ? String(contract.status || '').toUpperCase() : '';
    var allowed = !!contract && (status === 'ACTIVE' || status === 'PAUSED');
    buttons.forEach(function (btn) {
      btn.disabled = !allowed;
      btn.setAttribute('aria-disabled', allowed ? 'false' : 'true');
    });
  }

  function setFrequencyMsg(msg) {
    var el = frequencyOverlay && frequencyOverlay.querySelector('[data-frequency-msg]');
    if (!el) return;
    if (msg) { el.textContent = msg; el.hidden = false; }
    else { el.textContent = ''; el.hidden = true; }
  }

  function setFrequencyRestoreNote(count) {
    var el = frequencyOverlay && frequencyOverlay.querySelector('[data-frequency-restore-note]');
    if (!el) return;
    var n = Number(count) || 0;
    if (n < 1) { el.textContent = ''; el.hidden = true; return; }
    var template = n === 1
      ? (SETTINGS.frequencyRestoreNoteOne || 'You\'ve skipped [count] upcoming delivery. Changing your frequency will restore it.')
      : (SETTINGS.frequencyRestoreNoteOther || 'You\'ve skipped [count] upcoming deliveries. Changing your frequency will restore them.');
    el.textContent = fillTokens(template, { count: n });
    el.hidden = false;
  }

  function setFrequencySummary(text, deliveryText) {
    var wrap = frequencyOverlay && frequencyOverlay.querySelector('[data-frequency-summary]');
    var textEl = frequencyOverlay && frequencyOverlay.querySelector('[data-frequency-summary-text]');
    var deliveryEl = frequencyOverlay && frequencyOverlay.querySelector('[data-frequency-summary-delivery]');
    if (!wrap || !textEl) return;
    if (!text) {
      textEl.textContent = '';
      if (deliveryEl) { deliveryEl.textContent = ''; deliveryEl.hidden = true; }
      wrap.hidden = true;
      return;
    }
    textEl.textContent = text;
    if (deliveryEl) {
      deliveryEl.textContent = deliveryText || '';
      deliveryEl.hidden = !deliveryText;
    }
    wrap.hidden = false;
  }

  function setFrequencyConfirmEnabled(on) {
    var btn = frequencyOverlay && frequencyOverlay.querySelector('[data-frequency-confirm]');
    if (btn) btn.disabled = !on;
  }

  function setFrequencyBusy(state) {
    var btn = frequencyOverlay && frequencyOverlay.querySelector('[data-frequency-confirm]');
    frequencyState.checking = state === 'checking';
    frequencyState.saving = state === 'saving';
    if (!btn) return;
    btn.classList.toggle('is-loading', !!state);
    if (state === 'checking') {
      btn.disabled = true;
      btn.textContent = SETTINGS.frequencyCheckingText || 'Checking your new schedule…';
    } else if (state === 'saving') {
      btn.disabled = true;
      btn.textContent = SETTINGS.frequencySavingText || 'Updating your schedule…';
    } else {
      btn.textContent = frequencyConfirmDefault || btn.textContent;
    }
  }

  function renderFrequencyPreview(data) {
    var templates = {
      weekly: SETTINGS.frequencySummaryWeekly,
      fortnightly: SETTINGS.frequencySummaryFortnightly,
      monthly: SETTINGS.frequencySummaryMonthly
    };
    var anchor = data.anchor || {};
    var summary = fillTokens(templates[data.frequency] || '', {
      date: formatYmd(data.expected_next_billing_date),
      weekday: anchor.weekday_name || '',
      monthday: ordinalDay(anchor.monthday)
    });
    var delivery = data.expected_next_delivery_date && SETTINGS.frequencySummaryDelivery
      ? fillTokens(SETTINGS.frequencySummaryDelivery, { delivery: formatYmd(data.expected_next_delivery_date) })
      : '';

    setFrequencySummary(summary, delivery);
    setFrequencyRestoreNote(data.restores_skipped_count);

    // Already on this cadence AND anchor: nothing to commit.
    if (data.no_change) {
      setFrequencyMsg(SETTINGS.frequencyNoChangeText || 'Your subscription already runs on this frequency.');
      setFrequencyConfirmEnabled(false);
      return;
    }
    setFrequencyConfirmEnabled(true);
  }

  function requestFrequencyPreview(key) {
    if (!contract || !contract.id) return;
    var gen = ++frequencyState.previewGen;
    setFrequencyMsg('');
    setFrequencySummary('');
    setFrequencyRestoreNote(0);
    setFrequencyBusy('checking');

    if (SETTINGS.designMode) {
      var tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      var ymd = tomorrow.getFullYear() + '-' +
        String(tomorrow.getMonth() + 1).padStart(2, '0') + '-' +
        String(tomorrow.getDate()).padStart(2, '0');
      setFrequencyBusy(null);
      renderFrequencyPreview({
        frequency: key,
        anchor: {
          weekday_name: tomorrow.toLocaleDateString(undefined, { weekday: 'long' }),
          monthday: tomorrow.getDate()
        },
        expected_next_billing_date: ymd,
        no_change: false,
        restores_skipped_count: 0
      });
      return;
    }

    fetch('/apps/customer/subscription_change_frequency.php', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ subscriptionContractId: contract.id, frequency: key, preview: true })
    })
      .then(function (r) { return r.json().catch(function () { return {}; }).then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        if (gen !== frequencyState.previewGen) return; // a newer selection won
        setFrequencyBusy(null);
        if (!res.ok || res.data.ok !== true) {
          setFrequencyMsg(res.data.message || SETTINGS.frequencyPreviewErrorText || SETTINGS.genericErrorText);
          setFrequencyConfirmEnabled(false);
          return;
        }
        renderFrequencyPreview(res.data);
      })
      .catch(function () {
        if (gen !== frequencyState.previewGen) return;
        setFrequencyBusy(null);
        setFrequencyMsg(SETTINGS.frequencyPreviewErrorText || SETTINGS.genericErrorText);
        setFrequencyConfirmEnabled(false);
      });
  }

  function openFrequencyOverlay() {
    if (!frequencyOverlay || !contract) return;
    var current = currentFrequencyKey();

    frequencyState.selected = null;
    frequencyState.previewGen++;
    setFrequencyMsg('');
    setFrequencySummary('');
    setFrequencyRestoreNote(0);
    setFrequencyBusy(null);
    setFrequencyConfirmEnabled(false);

    // Nothing is pre-selected: pre-checking the current cadence would make
    // clicking it a no-op (no change event) and leave the dialog looking dead.
    // The badge alone shows where the customer is today.
    frequencyOverlay.querySelectorAll('[data-frequency-row]').forEach(function (row) {
      var radio = row.querySelector('[data-frequency-option]');
      var badge = row.querySelector('[data-frequency-current]');
      if (radio) radio.checked = false;
      if (badge) badge.hidden = !current || row.getAttribute('data-frequency-row') !== current;
      row.classList.remove('is-selected');
    });

    frequencyOverlay.classList.add('is-open');
    frequencyOverlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('msv2-no-scroll');
  }

  function closeFrequencyOverlay(force) {
    if (!frequencyOverlay) return;
    if (!force && (frequencyState.saving || frequencyState.checking)) return;
    frequencyState.previewGen++;
    frequencyOverlay.classList.remove('is-open');
    frequencyOverlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('msv2-no-scroll');
    setFrequencyMsg('');
    setFrequencySummary('');
    setFrequencyRestoreNote(0);
    setFrequencyBusy(null);
  }

  function confirmFrequencyChange() {
    var key = frequencyState.selected;
    if (!key || !contract || !contract.id) return;

    if (SETTINGS.designMode) {
      closeFrequencyOverlay(true);
      setSuccess(SETTINGS.frequencySuccessText || 'Frequency updated.');
      return;
    }

    setFrequencyMsg('');
    setFrequencyBusy('saving');
    fetch('/apps/customer/subscription_change_frequency.php', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ subscriptionContractId: contract.id, frequency: key })
    })
      .then(function (r) { return r.json().catch(function () { return {}; }).then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        if (!res.ok || res.data.ok !== true) {
          setFrequencyBusy(null);
          setFrequencyMsg(res.data.message || SETTINGS.frequencyErrorText || SETTINGS.genericErrorText);
          return;
        }
        frequencyState.saving = false;
        closeFrequencyOverlay(true);
        // The committed date wins over the previewed one; different wording when
        // Shopify moved it so the customer isn't held to the promised date.
        var resolved = res.data.resolved_next_billing_date;
        var dateText = resolved ? formatYmd(resolved) : '';
        var template = res.data.billing_moved
          ? (SETTINGS.frequencyMovedNote || 'Your next order was scheduled for [date].')
          : (SETTINGS.frequencySuccessText || 'Frequency updated. Your next order is [date].');
        setSuccess(fillTokens(template, { date: dateText }));
        reloadContracts();
      })
      .catch(function () {
        setFrequencyBusy(null);
        setFrequencyMsg(SETTINGS.frequencyErrorText || SETTINGS.genericErrorText);
      });
  }

  function wireFrequencyOverlay() {
    root.querySelectorAll('[data-action="change-frequency"]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!btn.disabled) openFrequencyOverlay();
      });
    });
    if (!frequencyOverlay) return;

    var confirmBtn = frequencyOverlay.querySelector('[data-frequency-confirm]');
    frequencyConfirmDefault = confirmBtn ? confirmBtn.textContent.trim() : '';

    frequencyOverlay.addEventListener('change', function (e) {
      var radio = e.target.closest('[data-frequency-option]');
      if (!radio) return;
      frequencyState.selected = radio.value;
      frequencyOverlay.querySelectorAll('[data-frequency-row]').forEach(function (row) {
        row.classList.toggle('is-selected', row.getAttribute('data-frequency-row') === radio.value);
      });
      requestFrequencyPreview(radio.value);
    });

    frequencyOverlay.addEventListener('click', function (e) {
      if (e.target.closest('[data-frequency-close]')) { closeFrequencyOverlay(); return; }
      var c = e.target.closest('[data-frequency-confirm]');
      if (c && !c.disabled) confirmFrequencyChange();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && frequencyOverlay.classList.contains('is-open')) closeFrequencyOverlay();
    });
  }

  /* ── init ── */
  wirePromoForm();
  wireCouponRemoval();
  wireCouponRemoveOverlay();
  wireRewardErrorOverlay();
  wirePaymentEdit();
  wireCarousels();
  wireAddressOverlay();
  wireShopOverlay();
  wireReschedOverlay();
  wireSendNowOverlay();
  wireSendNowWarnOverlay();
  wireLineRemoveOverlay();
  wireOrderModal();
  wireProfileOverlay();
  wireLineEditOverlay();
  wireLogoutOverlay();
  wirePauseOverlay();
  wireResumeOverlay();
  wireFrequencyOverlay();
  loadContracts();
  loadMeals();
  loadSmsConsent();

  /* Restore the view from the URL so order-history pagination reloads
     (?view=order-history&page=N) land back on the right tab. */
  (function () {
    var view = new URLSearchParams(window.location.search).get('view');
    if (view === 'order-history' || view === 'account') setPortalView(view);
  })();
})();
